import { UsInventoryDetail } from './UsInventoryDetail';
import { withOrderBookDisplay } from './orderBookDisplay';
import { queueBookStamp } from './queueBookStamp';
import { unsupportedVenue } from '@/lib/venueGuard';
import { requestPropsFeed } from '@/hooks/useHydra';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DataPressureCard } from '@/components/organisms/DataPressureCard';
import { useNavigate } from 'react-router-dom';
import { UnavailableCardBookNotice } from './UnavailableCardBookNotice';
import {
  Crosshair,
  X,
  Loader2,
  RefreshCw,
  ChevronDown,
  Eye,
  EyeOff,
  Pencil,
  Pin,
  PinOff,
  Receipt,
  Activity,
} from 'lucide-react';
import { parseTicker } from '../../lib/kalshiTicker';
import type { ParsedTicker } from '../../lib/kalshiTicker/types';
import type { SourceProof } from '../../lib/gameState/sourceEvidence';
import {
  focusedDashboardHref,
  type FocusedSport,
} from '../../lib/routing/focusedDashboard';
import { isAtpChallengerTicker } from '../../lib/routing/dashboardSport';
import {
  coerceSport,
  sportFromTicker,
  sportMatchesFilter,
  type SportFilter,
} from '../../lib/kalshiTicker/sportFilter';
import { isHoveredRowPresent, isSameGameHighlighted, rowGameKey } from './gameHighlight';
import { resolvedGameDate } from '../../lib/kalshiTicker/resolvedGameDate';
import {
  gameDetailHref as canonicalGameDetailHref,
  gameDetailHrefForRow,
  parseGameKeyV2,
  promoteToGameKeyV2,
} from '../../services/analyticsGameKey';
import { useAuthStore } from '../../stores/authStore';
import { useAccountStore } from '../../stores/accountStore';
import type { KalshiPosition } from '../../types/kalshi';
import { isKalshiCancelPending } from '../../lib/trading/kalshiCancelLifecycle';
import { workingCloses } from '../../lib/trading/workingCloses';
import { useMarketStore } from '../../stores/marketStore';
import type { HydraMarket } from '../../clients/HydraClient';
import { usePositionPnl, MTM_PNL_TOOLTIP } from '../../hooks/ui/usePositionPnl';
import { useThrottledMarketVersion } from '../../hooks/ui/useThrottledMarketVersion';
import { createKeyedIdentityStore, createVmIdentityStore } from './stableVm';
import {
  gameStateKeyForTickerOrToken,
  resolveOrderEventStateCertainty,
  useEventStateFeedSnapshotState,
  useGameStateLifecycleSignature,
} from '../../hooks/ui/useEventStateCertainty';
import { useGameStatesPoll } from '../../hooks/useGameStatesPoll';
import { useOrdersForCloseCount } from '../../hooks/useOrdersAreDeskWide';
import { useUserKalshiPOS, type UserOrderSlice } from '../../hooks/useUserKalshiPOS';
import { OrgTradersTable, type OrgTraderRow } from '../../components/organisms/OrgTradersTable';
import { OrgSwitcher } from '../../components/OrgSwitcher';
import { useAdminOrgs, useOrgScope } from '../../stores/orgScope';
import { Tooltip } from '../../components/atoms/Tooltip';
import { ExpandedGamePanel } from '../ValueDashboard/index';
import { exactLiveCardGame } from './exactLiveCardGame';
import { PositionSidebar, PositionsRefreshWarning } from '../ValueDashboard/PositionSidebar';
import { KalshiCancelSyncNotice } from '../../components/KalshiCancelSyncNotice';
import { UsBalanceSlot } from '../ValueDashboard/UsBalanceSlot';
import { useUsRail, type UsRail } from '../ValueDashboard/useUsRail';
import { usRailCancellableCount, type UsRailAccount, type UsRailRead } from '../ValueDashboard/usRailModel';
import type { BetLineContext, TradeIntent } from '../ValueDashboard/GameDetailPanel';
import { spreadLineKey, synthesizeFromSpread, synthesizeFromTotal, totalLineKey } from '../SpreadsTotals/synthesize';
import { buildBetLineContext } from '../SpreadsTotals/buildBetLineContext';
import { buildMultiOutcomeBetContext, synthesizeFromMultiOutcome } from '../ValueDashboard/multiOutcome';
import {
  classifyMlbExtendedTicker,
} from '../../lib/kalshiTicker/sports/mlb';
import {
  hasPropSynthKey,
  parsePropSynthKey,
  propGroupKey,
  propRungKey,
} from '../../lib/sport/propSynthKey';
import { propStatOfKalshiSeries, propStatSport, propRungLabels, propRungTag, PROP_STAT_LABELS, type PropStat } from '../../lib/sport/propStat';
import { classifyPolyExtendedTicker } from '../../lib/kalshiTicker/polyPropsEvents';
import { buildLadderTableRows, chipKeyFor, type LadderSourceRow } from '../GameDetail/ladderRows';
import { buildLadderBetLineContext } from '../GameDetail/ladderBetLineContext';
import { VenueChip } from '../../components/atoms/VenueChip';
import { RfqOriginChip } from '../../components/cards/PositionCard';
import { GameDetailLink } from '../../components/atoms/GameDetailLink';
import { AccountRecordDialog } from '../../components/accountRecord/AccountRecordDialog';
import { recordFromPosition } from '../../components/accountRecord/accountRecordModel';
import { useAccountRecord, useOpenRecordRow } from '../../components/accountRecord/useAccountRecord';
import { OrderbooksPausedBadge } from '../../components/atoms/OrderbooksPausedBadge';
import {
  QueueVintageBadge,
  queueSnapshotIsHardFailure,
  queueSourceGapBreaksContinuity,
  queueSourceKind,
  queueSourceSampleToken,
  type QueueSourceKind,
} from '../../components/atoms/QueueVintageBadge';
import { ConfirmModal, Modal } from '../../components/atoms/Modal';
import { SortableTh } from '../../components/atoms/SortableTh';
import { EditOrderPriceModal } from '../../components/molecules';
import {
  describePolyMarketCached,
  describePolyOrderCached,
  polyOrderLiveData,
  polyPositionLiveData,
  resolvePolyCloseTarget,
} from '../../lib/polymarket/describeOrder';
import { usePolyBalance } from '../../hooks/usePolyBalance';
import { polyOrderQueueFromBook } from '../../lib/polymarket/orderQueue';
import {
  invalidatePolyOrdersShare,
  usePolymarketRelayOrders,
} from '../../hooks/usePolymarketRelayOrders';
import { usePolymarketRelayPositions } from '../../hooks/usePolymarketRelayPositions';
import { usePolyFeeSchedules } from '../../hooks/usePolyFeeSchedules';
import type { PolyRelayPosition } from '../../lib/polymarket/relay';
import { estimatePolyOrderFeeCents } from '../../lib/pricing/polyFees';
import { polyTradeEconomics } from '../../lib/pricing/polyEconomics';
import { mirrorCents } from '../../lib/pricing/subCent';
import { describeUnpricedReasons, describeWithheldTotal, summarizeUnrealizedCoverage, withheldHeadlineText, type UnpricedReason } from '../../lib/accounting/unrealizedCoverage';
import { usPositionEconomics } from '../../lib/polymarketUs/positionEconomics';
import { useUsPositionMarks } from '../../hooks/useUsPositionMarks';
import { kalshiReportedBalanceTitle } from '../../lib/kalshiBalance';
import { ORG_BALANCE_HIDDEN_LABEL, ORG_BALANCE_HIDDEN_TITLE } from '../../lib/orgBalanceHidden';
import type { PositionWithPnl } from '../../hooks/ui/usePositionPnl';
import { describeMarket } from '../../lib/kalshiTicker';
import { useAccountIdentityVersion } from '../../stores/accountMarketIdentity';
import { MULTI_OUTCOME_SPORTS, SPORT_TABS, type Sport, type GameEventRow, type GameRow, type PlayerPropLadder, type PropRung, type SpreadLine, type TotalLine, type VenueBook } from '../ValueDashboard/types';
import { americanOdds } from '../ValueDashboard/tradeModalBook';
import { AlertControlsMenu } from './AlertControlsMenu';
import { EditPositionModal, type EditPositionData } from './EditPositionModal';
import {
  compareDefaultSort,
  compareExactSizes,
  compareOrderRowsDefault,
  makeOrderRowComparator,
  orderRowId,
  polySortStartMs,
  type OrderSortLiveKeys,
  type SortedOrderRow,
} from './orderSort';
import { applyLockedOrder, orderingForView } from './lockedOrder';
import { admitRows, useProgressiveAdmission } from './progressiveRows';
import { useWorkingView, useWorkingViewField } from '@/hooks/useWorkingView';
import { measureTopOcclusion, useWorkingScroll } from '@/hooks/useWorkingScroll';
import {
  TRACKER_READING_DEFAULTS,
  trackerReading,
  type PositionSortKey,
} from './readingView';
import type { PinnedEntry } from './pinIdentity';
import { usePinnedKalshiOrderIds } from '../../hooks/usePinnedKalshiOrderIds';
import {
  editOrderKeepingPin,
  selectOwnedPinKeys,
  selectOwnedPins,
  useTrackerPins,
} from '../../stores/trackerPins';
import {
  readPinsViewMode,
  writePinsViewMode,
  type PinsViewMode,
} from './pinsViewMode';
import {
  PinnedHeaderControls,
  pinnedOrdersToolbarStyle,
  pinnedPositionClass,
  readPinsSticky,
  writePinsSticky,
} from './pinsSticky';
import {
  DATA_EXPANDED_KEY,
  DATA_SHOWN_KEY,
  FILLS_EXPANDED_KEY,
  FILLS_HIDDEN_KEY,
  TRACKER_EXPANDED_KEY,
  TRACKER_HIDDEN_KEY,
  readRailPref,
  writeRailPref,
} from './railPrefs';
import { readPinsCollapsed, writePinsCollapsed } from './pinsPersistence';
import {
  PINNED_TABLE_GRID_CLASS,
  PinnedCardStrip,
  PinnedOrderRow,
  PinnedPositionRow,
  PinnedTombstoneRow,
  stripTickerTail,
} from './pinnedRows';
import { marketKindFromTicker, type MarketKind } from './marketKind';
import {
  TRACKER_FAMILY_CHIPS,
  familyMatchesFilter,
  kalshiTrackerFamily,
  polyOrderTrackerFamily,
  polyPositionTrackerFamily,
  type TrackerFamilyFilter,
  type TrackerMarketFamily,
} from './trackerMarketFamily';

// Re-exported so `./index` stays the import path every existing caller and test
// already uses; the implementation now lives in ./marketKind (see #539).
export { marketKindFromTicker };
import { isOrdersSyncFresh, OrdersMismatchBanner, OrdersSyncBadge } from './ordersSyncStatus';
import { kalshiReadiness, polyReadiness, trackerAdmission } from './venueReadiness';
import { useKalshiAccountPhase } from '../../hooks/useKalshiAccountPhase';
import { noticeWorthy, usNoticeWorthy, VenueCoverageNotice, VenuePlaceholder } from './VenueCoverageNotice';
import { UsUnavailableRetry } from './UsUnavailableRetry';
import { usListsUnavailable, usReadinessUnavailable } from './usListsState';
import { usRealizedOf, UsRealizedValue } from './usRealized';
import { usPositionsComplete } from './usPositionCoverage';
import { UsTrackerFeed, useUsTrackerReadiness, type UsAccountView, type UsOpenPanel, type UsTrackerRead, type UsUnavailablePanel } from './usTrackerFeed';
import { UsBulkCancelLines, UsBulkCancelReport, useUsBulkCancel } from './UsBulkCancel';
import { usVisibleInventoryAll } from './usBulkCancelModel';
import {
  UsCloseControl,
  UsPositionTableRow,
  useUsMarketRequests,
  useUsTrackerMarkets,
  usHoldingRowsOf,
  usOrderActionProps,
  usOrderRowsOf,
  usPinOriginOf,
  usRowLink,
  useUsRowDispatch,
  usShownOrdersOf,
  type UsSortedPosRow,
} from './usTrackerRows';
import { usOrderKey, usPositionKey } from '../../lib/polymarketUs/portfolioModel';
import { decimalUnits, signedDecimalUnits } from '../../lib/polymarketUs/ticketModel';
import type { UsOrderRow, UsPositionRow } from '../../lib/polymarketUs/types';
import { usHoldingPinKey, usOrderPinKey } from './pinIdentity';
import type { TradingVenue } from '../../lib/venues';
import {
  LockToggleButton,
  RefreshProgress,
  SortSegmentButton,
  SportMarketFilterRows,
  type ActiveSortDir,
} from './trackerToolbar';
// Re-exported so marketKindFilter.test.ts (which imports MarketKind from './index')
// keeps resolving after the type moved to ./marketKind.
export type { MarketKind };
import { readTrackerFilters, writeTrackerFilters } from './filterPersistence';
import {
  type ClosedPolyPositionFences,
  isClosedPolyPositionSuppressed,
  polyBuyBaseline,
  reconcileClosedPolyPositionFences,
} from './polyPositionSuppression';
import { playToneFor, playDivergenceTone } from '../../lib/audio/tradeAudio';
import { WAY_BEHIND_QUEUE_ABOVE_THRESHOLD } from '../../lib/queueAlerts/fellBehind';
import { selectQueueSoundChannel } from '../../lib/queueAlerts/queueDelivery';
import {
  stepConfirmedQueueDelivery,
  NO_QUEUE_CONFIRMATIONS,
  type QueueCrossingConfirmations,
} from '../../lib/queueAlerts/confirmCrossing';
import { queueAlertEvidence } from '../../lib/queueAlerts/alertEvidence';
import { useAlertPreferences } from '../../hooks/ui/useAlertPreferences';
import {
  DEFAULT_VELOCITY_WARN,
  DEFAULT_VELOCITY_ALERT,
} from '../../lib/trading/queueVelocity';
import {
  bookMid,
  computeDivergence,
  divergenceRingTickers,
  stepDivergenceLatch,
  DIVERGENCE_THRESHOLD_CENTS,
  POLY_STALE_MS,
  type DivergenceInfo,
} from '../../lib/divergence/polyKalshiDivergence';
import {
  POLY_MID_COLUMN_LABEL,
  sideNativeProbCents,
  UPOddsValue,
  type UPOdds,
} from '../../components/cards/upOdds';
import { useRestingCloseCount } from '@/hooks/useRestingCloseCount';
import { useDisplaySettings } from '../../stores/displaySettings';
import { readSessionOrderSort, writeSessionOrderSort } from '../../lib/trading/positionsOrderSortSession';
import { cancelPolyOrderViaRelay } from '../../lib/polymarket/relay';
import { describePolyCancelRow } from '../../lib/trading/polyCancelPresentation';
import { usePolyCancelStates } from '../../hooks/usePolyCancelStates';
import {
  describeCancelVenueCounts,
  describeAllPinned,
  describeCancelVenues,
  describeSkippedPinned,
  freezeCancelSnapshot,
  type FrozenCancelSnapshot,
} from '../../lib/trading/gameOrderSelection';
import { recordBulkCancelAttempt } from '../../lib/trading/bulkCancelAttemptAudit';
import {
  buildAllCancelTargets,
  buildVisibleCancelTargets,
  shouldOfferCancelVisible,
} from './bulkCancelScopes';
import {
  executeBulkOrderCancel,
  type BulkCancelReport,
} from '../../lib/trading/bulkOrderCancel';
import { closePolyPosition } from '../../lib/polymarket/closePosition';
import {
  CardGrid,
  CompactListGrid,
  BetTeamTitle,
  FamilyPendingMark,
  EdgeFadeScroller,
  InfoTooltip,
  RestingCloseNote,
  PinToggle,
  SAME_GAME_HIGHLIGHT,
  MarketTypeChip,
  SummaryStrip,
  SummaryTile,
  ViewModeToggle,
} from '../../components/cards/cardKit';
import {
  fmtCents,
  fmtLastFillAt,
  fmtUsd,
  fmtUsdSigned,
  ticketLabel,
  vmBet,
} from '../../components/cards/cardFormat';
import { useHeadroom, bumpHeadroom } from '../../hooks/useHeadroom';
import { OrdersHelp, PositionsHelp } from '../../components/cards/helpContent';
import { CompactOrderItem, OrderCard } from '../../components/cards/OrderCard';
import { OrderCardRow } from './OrderCardRow';

import { CompactPositionItem, PositionCard } from '../../components/cards/PositionCard';
import { PositionCardRow } from './PositionCardRow';
import { PolyCloseControl, polyCloseState } from './PolyCloseControl';
import { cardAnnotationsFor, useCardAnnotationsActive } from '../../components/cards/CardAnnotations';
import {
  EMPTY_ORDER_TABLE_MARKET,
  OrderTableRow,
  resolveOrderTableRowMarket,
  type OrderTableRowMarket,
} from './OrderTableRow';

import { DivergenceBadge } from '../../components/cards/DivergenceBadge';
import {
  buildKalshiOrderVM,
  buildPolyOrderVM,
  buildUsOrderVM,
  ownSiblingBehindQty,
  type SiblingOrder,
  sideNativePriceCents,
  siblingGroupKey,
  queueBehindAtPriceLevel,
  type OrderCardVM,
  type PriceQuote,
} from '../../components/cards/orderCardModel';
import { PREGAME_MAKER_REBATE_PCT } from '../../lib/pricing/makerRebate';
import { makerRateForTicker } from '../../lib/pricing/fees';
import { useMakerRebateActive } from '../../hooks/useMakerRebate';
import { useFeeStore } from '../../services/hydraFees';
import {
  buildKalshiPositionVM,
  buildUsPositionVM,
  buildPolyPositionVM,
  type PositionCardVM,
} from '../../components/cards/positionCardModel';
import { orderVelocity } from '../../components/cards/orderVelocity';
import { useVelocitySpikeFlash } from '../../hooks/ui/useVelocitySpikeFlash';
import {
  recordPriceSample,
  priceVelocityWithCurrentPrice,
  prunePriceVelocity,
} from '../../components/cards/priceVelocity';
import { useTickerFeedNeeds } from '@/hooks/useTickerFeedNeeds';
import { useRfqPositionPrefs } from '../../stores/rfqPositionPrefs';

// Queue-position alert thresholds (lower = closer to the front of the
// resting-bid queue). Crossing each threshold from above fires a one-shot
// audio cue; the row stays tinted while the order remains at or below the
// threshold so the visual matches the audible state.
//
// Since #233 these are DELIVERY fallbacks only: the live values come from the
// user's queueWarning / queueCritical channel thresholds, which default to
// exactly these numbers. They remain here as the typed floor for the
// `number | null` settings field and as the documented shipped default.
// The persistent queue TINT is unaffected by any of this — see queueCardTone.
const QUEUE_WARNING_THRESHOLD = 50_000;
const QUEUE_CRITICAL_THRESHOLD = 10_000;
/** Shared stand-in for a cue whose flash is switched off — one stable identity,
 *  so hiding in-flight flashes never churns a consumer's props. */
const EMPTY_FLASH_IDS: ReadonlySet<string> = new Set();
/** #1331 — the list of the view that is not shown: one stable empty identity. */
const NO_ROWS: readonly never[] = [];
// 24s (was 9.6s) — Josh asked for the queue-critical flash 2.5× longer so it's
// easier to catch (G9-and-Beyond, Jun 17). Keep in sync with the CSS animation
// duration on .animate-queue-critical-card-flash in globals.css.
const QUEUE_CRITICAL_FLASH_MS = 24_000;
// One-shot fell-behind flash (#178). Keep in sync with the CSS animation
// duration on .animate-fell-behind-card-flash in globals.css.
const FELL_BEHIND_FLASH_MS = 6_000;
// One-shot queue-warning flash (#233) — OPT-IN, off by default. Keep in sync
// with the CSS animation duration on .animate-queue-warning-card-flash.
const QUEUE_WARNING_FLASH_MS = 6_000;
// One-shot cross-venue divergence flash. Keep in sync with the CSS animation
// duration on .animate-divergence-flash in globals.css.
const DIVERGENCE_FLASH_MS = 6_000;
// Divergence recompute cadence. A 2¢ cross-venue gap doesn't need the ~30Hz
// market tick — recompute on a coarse clock so the per-sport getGames sweep
// (~10ms each) isn't run every frame in this already-heavy view. Holdings
// changes still recompute immediately (the memo also keys on the held set).
// Tradeoff: this is the alert's sampling resolution too — a gap that opens past
// 2¢ AND falls back under the re-arm floor entirely within one interval is
// never observed. For a coarse "venues disagree" heads-up that sub-second blip
// is noise, so 1s is the deliberate default (flash + sound fire from the same
// throttled effect, so they stay in lockstep — this only adds ~1s latency).
const DIVERGENCE_REFRESH_MS = 1_000;
const PNL_CARD_REFRESH_INTERVAL_MS = 10_000;
const POSITION_VIEW_MODES = ['compact', 'table', 'card', 'mini'] as const;
const SURFACE_VIEW_MODES = ['card', 'table'] as const;

// Rail + pinned-strip persistence lives in ./railPrefs and ./pinsPersistence
// (#339). The pin entry type + key algebra live in ./pinIdentity (#188); the
// strip's view-mode key + its legacy-'card' migration live in ./pinsViewMode
// (#76).

// Tri-state per sport (Prof, Jun 6): absent → not filtered on; 'include' →
// row's sport must be in the include set; 'exclude' → hide this sport.
// Empty map = all sports. Types + derivation live in lib/kalshiTicker/
// sportFilter — this module used to carry a verbatim inline copy of both,
// which is how a sport-classification fix could be applied to one copy and
// silently miss the loops using the other. One definition only.

// Data the EditOrderPriceModal needs to render and the venue-aware replace
// callback needs to dispatch. Built fresh each time a row's "Edit price"
// icon is clicked from a row or card.
export interface EditOrderData {
  venue: 'kalshi' | 'polymarket';
  orderId: string;
  marketLabel: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  remainingCount: number;
  currentPriceCents: number;
  ticker?: string;
  /** Polymarket only — passed through to the relay so the replacement
   *  carries the right CLOB tick size + NEG_RISK flag. Compatibility
   *  pass-through: the replace path does NOT read this for its pre-cancel
   *  tick gate. It re-resolves fresh metadata itself and rejects when that
   *  resolution fails, because caller-seeded metadata can predate a tick
   *  regime change. */
  tickSize?: string;
  negRisk?: boolean;
}

interface PendingCancelOrder {
  venue: TradingVenue;
  orderId: string;
  label: string;
}

// ── Helpers ─────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return `${cents}¢`;
}

/** Table-cell twin of the order card's PriceWithOdds. Takes a {@link PriceQuote}
 *  so the cents printed here are the exact value its odds came from (#452);
 *  `fmtCents` (centicent grain) rather than the local whole-value formatCents,
 *  because a fee-adjusted basis is sub-cent by nature. */
export function PriceWithOddsInline({
  quote,
  muted = false,
}: {
  quote: PriceQuote;
  muted?: boolean;
}) {
  const { cents, odds } = quote;
  if (cents == null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={muted ? 'text-muted-foreground' : 'text-foreground'}>
      {fmtCents(cents)}
      {odds && <span className="ml-1.5 text-[10px] opacity-80">{odds}</span>}
    </span>
  );
}

// Delegate to the comma-grouping card formatter — traders read five-figure
// P&L here and "+$20282.62" without separators caused real misreads
// (Prof, Jun 5: "all 3 of these boxes could take a comma").
function formatDollars(cents: number): string {
  return fmtUsdSigned(cents / 100);
}


// The market-family chips (#980) classify through ./trackerMarketFamily, not
// `marketKindFromTicker`: that one folds props/futures/unknowns into
// moneyline, which the Value Dashboard's routing depends on.

// `marketKindFromTicker` moved to ./marketKind (#539) — the Value Dashboard
// needs the same classification to pick a focused link's board, and importing
// this file for it would form a cycle. Re-exported below so every existing
// caller and test keeps its current import path.

/** #980 — flag a card VM whose market family is still loading. Only a TRUE
 *  flag is written, so every classified row's VM keeps its exact prior shape
 *  (and its #731 identity). */
function withFamilyPending<T extends { familyPending?: boolean }>(vm: T, pending: boolean | undefined): T {
  return pending ? { ...vm, familyPending: true } : vm;
}

/**
 * A Polymarket position's label, as its table row and its Close (confirm
 * dialog and result line) name it, in every layout. `desc` is the held
 * token's live description, when the live frame has it. Token desc needs the
 * live frame; once the market ages out (game ended) the market-level lookup
 * still knows the matchup from its persisted labels — hex is the true last
 * resort. Orientation stays on the row's own ledger side; the market desc
 * never carries it.
 */
function polyPositionLabel(
  conditionId: string,
  desc: ReturnType<typeof describePolyOrderCached>,
): string {
  const marketDesc = desc ? null : describePolyMarketCached(conditionId);
  return desc
    ? `${desc.matchup} — ${desc.sport.toUpperCase()} — ${desc.date.slice(5)}`
    : marketDesc
      ? `${marketDesc.matchup} — ${marketDesc.sport.toUpperCase()}`
      : conditionId.length > 16
        ? `${conditionId.slice(0, 6)}…${conditionId.slice(-4)}`
        : conditionId;
}

function cycleKindFilter(prev: TrackerFamilyFilter, family: TrackerMarketFamily): TrackerFamilyFilter {
  const next = new Map(prev);
  const cur = next.get(family);
  if (cur === undefined) next.set(family, 'include');
  else if (cur === 'include') next.set(family, 'exclude');
  else next.delete(family);
  return next;
}

/** Exported for tests. A row whose sport is still unresolved must pass even
 * an include-mode filter — hiding a real money row behind a classification
 * miss is worse than showing it unfiltered.
 *
 * This applies to EVERY venue, and — since #428 — to BOTH the sport and kind
 * filters; a pending market family passes `familyMatchesFilter` the same way. It was written
 * for poly rows (a describePolyOrder cache miss yields null), under the
 * assumption that "Kalshi rows never hit this: their sport parses straight
 * from the ticker string." That assumption was false: tournament-winner
 * futures like `KXMENWORLDCUP-26-ES` carry no matchup grammar, so
 * `sportFromTicker` returned null and the World Cup Final card vanished under
 * every include-mode chip — including Soccer, which should have matched it. */
export function rowSportMatchesFilter(sport: Sport | null, filter: SportFilter): boolean {
  return sport == null || sportMatchesFilter(sport, filter);
}

/** A Kalshi row's sport, FRAME-FIRST.
 *
 * Hydra stamps `sport` on the live frame and that is authoritative — it
 * knows things no ticker grammar encodes (a winner-futures leg belongs to
 * soccer; a remapped series changes hands). `sportFromTicker` stays as the
 * fallback for rows with no frame: settled markets, and anything not yet
 * ingested. Exported for tests. */
export function kalshiRowSport(ticker: string): Sport | null {
  const frameSport = useMarketStore.getState().getMarket(ticker)?.sport;
  return coerceSport(frameSport) ?? sportFromTicker(ticker);
}

/** The complete filter decision for one Kalshi row — the single source of
 *  truth shared by the positions and open-orders loops, which previously
 *  each inlined their own copy and so had to be fixed twice. Returns the
 *  resolved sport too, because the row object needs it for grouping, and
 *  whether the row is listed ONLY because its family is still pending (#980):
 *  true only while a family chip is active, so it drives the "?" marker, the
 *  unclassified count and the Cancel-visible warning — never an All view.
 *  Exported for tests. */
export function kalshiRowFilterState(
  ticker: string,
  sportFilter: SportFilter,
  kindFilter: TrackerFamilyFilter,
): { sport: Sport | null; visible: boolean; familyPending: boolean } {
  const sport = kalshiRowSport(ticker);
  const family = kalshiTrackerFamily(ticker);
  const visible =
    rowSportMatchesFilter(sport, sportFilter) && familyMatchesFilter(family, kindFilter);
  return { sport, visible, familyPending: family == null && kindFilter.size > 0 };
}

// User-local short time stamp for event start — e.g. "4:05p" for a 7:05p ET
// game viewed from PST. Mirrors marketStore.formatStartTimeLocal (which
// drives the dashboard time pill) so the two surfaces read identically.
// Returns '' for unknown / +Infinity so callers can no-op concatenate.
const _LOCAL_TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
// Build a GameDetail deep-link from a position/order ticker. Pass through
// `?ticker=…&side=…` so GameDetail can pre-expand the right line and
// pre-fill the trade ticket on landing. Returns null when the ticker
// doesn't parse cleanly (rare — non-game tickers).
/** Same alpha-sort collapse GameDetail's private normalizeKey applies —
 *  hydra's canonicalGameKey and marketStore row keys can disagree on team
 *  order, so compare both through the sorted form. All three key shapes
 *  collapse to `date|LO|HI`:
 *    - 5-seg V2 row key `sport|date|gameNo|LO|HI` (uniform since the
 *      canonicalGameKeyV2 promotion — its absence here silently broke the
 *      buy-panel keyHit fallback on main: 5-seg keys fell through raw and
 *      never matched the market's legacy 4-seg canonicalGameKey, so
 *      spread/total order-title clicks bounced to the GameDetail page
 *      instead of opening the panel, Dan 2026-07-08)
 *    - legacy 4-seg `sport|date|A|B`
 *    - legacy 3-seg `date|A|B`
 *  gameNo is DROPPED — this is deliberately the cross-shape blend, so a
 *  doubleheader's two games normalize identically. Callers that need
 *  game-level precision must exact-match the raw V2 key FIRST and use this
 *  only as the fallback (resolveBuyPanelTarget, buyPanelGame). */
export function normalizeGameKey(key: string): string {
  const parts = key.split('|');
  if (parts.length === 5) {
    const [, date, , a, b] = parts;
    const [teamA, teamB] = [a, b].sort();
    return `${date}|${teamA}|${teamB}`;
  }
  if (parts.length === 4) {
    const [, date, a, b] = parts;
    const [teamA, teamB] = [a, b].sort();
    return `${date}|${teamA}|${teamB}`;
  }
  if (parts.length === 3) {
    const [date, a, b] = parts;
    const [teamA, teamB] = [a, b].sort();
    return `${date}|${teamA}|${teamB}`;
  }
  return key;
}

/**
 * MM Program rebate fraction folded into this surface's open-order QUOTES —
 * gated on the reachable controls (org enrolled × the MM Rebate toggle in the
 * global StatusBar) and otherwise fixed at the PREGAME rate.
 *
 * **The pregame rate is a deliberate floor, not a live/pregame decision.** The
 * program pays 50% pregame and 100% live, but no producer-owned live state
 * reaches this surface, and a rebate must not be sized from an inferred one.
 * `GameRow.gameState` — what the pre-trade surfaces key their rebate to — is
 * marketStore's heuristic ("a venue book is tradable AND the scheduled start
 * has passed"), which `lib/sport/liveDisplayState` (#121) documents as a
 * reasonable guess and a poor claim: a postponed game keeps a tradable book
 * past a stale start and reads live indefinitely. PR #467 review: do not
 * fabricate per-market live state here.
 *
 * The floor errs in the safe direction. A genuinely live market earns the
 * LARGER 100% rebate, so quoting the pregame 50% understates the trader's
 * benefit — the displayed price is never better than the one they get.
 *
 * Replace this with the real per-market rate once producer-owned live state
 * exists (hydra canonical `statusId`, the #121 direction) — that is the single
 * line to change, and the reason it is a named hook rather than an inline
 * expression.
 */
export function useOpenOrderQuoteRebatePct(): number {
  return useMakerRebateActive() ? PREGAME_MAKER_REBATE_PCT : 0;
}

/** Resolve a position/order ticker to the live moneyline GameRow + the
 *  trade-intent prefill for the in-page buy panel (Joshua's ask: click a
 *  game on the tracker → the dashboard's buying window opens on the right).
 *
 *  Sport scope comes from the market's canonicalGameKey prefix, so each
 *  click scans ONE sport's rows, not all of them. Settled rows
 *  (gameState 'final') are skipped on purpose: their books are dead and
 *  orders would be rejected, so the caller's GameDetail-page fallback
 *  (which renders archived games) is the right surface.
 *
 *  Kalshi slots match by exact ticker → team side + held order side.
 *  Polymarket needs token matching instead: buildPolyMoneylinePair puts
 *  the SAME slug ticker on both team slots, and the canonical-yes team can
 *  land on either, so picking a slot by ticker alone arms the OPPOSITE
 *  token ~half the time (review catch). We match the HELD token against
 *  each slot's tokenId/noTokenId so the prefilled ticket always adds to
 *  the trader's actual exposure.
 *
 *  Returns null when the market/game isn't resolvable from the live store
 *  — caller falls back to the GameDetail page. */
/** Sport tabs to scan for a canonicalGameKey. The key's sport prefix is
 *  LOWERCASE ('nba|2026-06-13|NYK|SAS'), same casing as g1 Sport ids, so a
 *  direct compare works. Tennis is unified: hydra prefixes both tours as
 *  'tennis|', which maps to the ATP + WTA tabs. Unknown prefixes fall back
 *  to scanning everything — correctness over scan cost. Exported for
 *  tests. */
export function sportsForCanonicalKey(ck: string | null | undefined): Sport[] {
  const all = SPORT_TABS.map((t) => t.id as Sport);
  const prefix =
    ck && ck.split('|').length === 4 ? ck.split('|')[0].toLowerCase() : null;
  if (prefix == null) return all;
  const scoped = all.filter((sp) =>
    prefix === 'tennis' ? sp === 'atp' || sp === 'wta' : sp === prefix,
  );
  return scoped.length > 0 ? scoped : all;
}

/** Full-game spreads have their own exact contract, never the parent moneyline.
 * One sport's existing event projection is enough; no additional feed demand. */
function findSpreadEventLine(sport: Sport, match: (line: SpreadLine) => boolean): { event: GameEventRow; idx: number } | null {
  const events = useMarketStore.getState().getGameEvents(sport, { dateFilter: 'all', statusFilter: 'all' });
  for (const event of events) {
    if (event.gameState === 'final') continue;
    const idx = event.spreads.findIndex(match);
    if (idx !== -1) return { event, idx };
  }
  return null;
}

function resolveSpreadPanelTarget(ticker: string, side: 'yes' | 'no'): { game: GameRow; intent: TradeIntent } | null {
  if (marketKindFromTicker(ticker) !== 'spread') return null;
  const market = useMarketStore.getState().getMarket(ticker);
  const sport = coerceSport(market?.sport);
  if (!market || !sport) return null;
  const hit = findSpreadEventLine(sport, line => line.kalshi?.marketTicker === ticker);
  return hit ? {
    game: synthesizeFromSpread(hit.event, hit.event.spreads[hit.idx]),
    intent: { side: 'away', venue: 'kalshi', initialOrderSide: side, postOnly: false },
  } : null;
}

/** Share the one live event projection between book and caption/chip consumers. */
function liveSpreadPanel(gameKey: string, sport: Sport): { game: GameRow; context: BetLineContext } | null {
  const lineKey = gameKey.slice(gameKey.lastIndexOf('|') + 1);
  const hit = findSpreadEventLine(sport, line => spreadLineKey(line) === lineKey);
  if (!hit) return null;
  return {
    game: synthesizeFromSpread(hit.event, hit.event.spreads[hit.idx]),
    // This order opens its exact contract. A single-line context also prevents
    // the panel's index-based chip override from changing it when other lines
    // arrive or reorder. Alternate-line navigation stays in event details.
    context: buildBetLineContext({ kind: 'spread', event: hit.event, lines: [hit.event.spreads[hit.idx]],
      selectedIdx: 0, onSelectIdx: () => {} }),
  };
}

/** #1189: Soccer winner legs live in the multi-outcome inventory, not getGames.
 * Match the exact Kalshi ticker, never a team name, sibling or parent market.
 * Keep one outcome chip so sibling reordering cannot change the selected book. */
export function resolveMultiOutcomePanelTarget(ticker: string, side: 'yes' | 'no'):
  { game: GameRow; intent: TradeIntent; context: BetLineContext } | null {
  const store = useMarketStore.getState();
  const market = store.getMarket(ticker);
  const sport = market?.sport?.toLowerCase() as Sport | undefined;
  if (!market || market.source !== 'kalshi' || !sport || !MULTI_OUTCOME_SPORTS.has(sport)) return null;
  for (const event of store.getMultiOutcomeEvents(sport, { dateFilter: 'all', statusFilter: 'all' })) {
    if (event.gameState === 'final' || event.kind === 'advance') continue;
    const outcome = event.outcomes.find(row => row.kalshi?.marketTicker === ticker);
    if (!outcome) continue;
    return {
      game: synthesizeFromMultiOutcome(event, outcome),
      intent: { side: 'away', venue: 'kalshi', initialOrderSide: side, postOnly: false },
      context: buildMultiOutcomeBetContext({ ...event, outcomes: [outcome] }, outcome),
    };
  }
  return null;
}

function resolveBuyPanelTarget(
  ticker: string,
  posSide: 'yes' | 'no',
): { game: GameRow; intent: TradeIntent } | null {
  const store = useMarketStore.getState();
  const mkt = store.getMarket(ticker);
  if (!mkt) return null;
  const ck = mkt.canonicalGameKey;
  const sports = sportsForCanonicalKey(ck);
  const heldToken = posSide === 'yes' ? mkt.yesTokenId : mkt.noTokenId;
  for (const sp of sports) {
    const games = store.getGames(sp, 'moneyline', { dateFilter: 'all', statusFilter: 'all' });
    for (const g of games) {
      if (g.gameState === 'final') continue;
      if (g.kalshi?.away.marketTicker === ticker) {
        return { game: g, intent: { side: 'away', venue: 'kalshi', initialOrderSide: posSide, postOnly: false } };
      }
      if (g.kalshi?.home.marketTicker === ticker) {
        return { game: g, intent: { side: 'home', venue: 'kalshi', initialOrderSide: posSide, postOnly: false } };
      }
      if (heldToken && g.polymarket && (g.polymarket.away.marketTicker === ticker || g.polymarket.home.marketTicker === ticker)) {
        for (const side of ['away', 'home'] as const) {
          const book = g.polymarket[side];
          if (book.tokenId === heldToken) {
            return { game: g, intent: { side, venue: 'polymarket', initialOrderSide: 'yes', postOnly: false } };
          }
          if (book.noTokenId === heldToken) {
            return { game: g, intent: { side, venue: 'polymarket', initialOrderSide: 'no', postOnly: false } };
          }
        }
      }
    }
  }
  // Advance-stamped legs (knockout "To Advance", and — issue #98 — cup-winner
  // futures once hydra stamps them with the final's canonical key): the store
  // already collapses these onto their OWN binary row via getAdvanceGame, with
  // each leg's book on the side its `yesOutcome` names. Resolving through that
  // row is what makes the panel trade the EXACT held ticker; the moneyline
  // fallback below would open the final's KXWCGAME moneyline instead — a
  // different contract (Danielle DNL-001). Slot is chosen by matching the held
  // ticker against the row's books, so the ticker the panel trades is the
  // ticker the trader holds by construction, not by inference.
  //
  // NB this cannot go through the moneyline loop above: soccer is a
  // multi-outcome sport, so getGames('soccer', …) is always empty.
  // dhAmbiguous gate (HYDRA_API "dhAmbiguous"): when the producer sets the
  // flag, V2 is absent BY INVARIANT and the legacy key must NOT be promoted
  // to gameNo=1 — that attaches the leg to GAME 1 of a doubleheader it may
  // not belong to, which is the phantom the flag exists to prevent. Core's
  // REST/WS now stamps poly rows with these fields (2026-08-22), so this
  // promote is no longer only ever fed by edge frames.
  const advKey = mkt.canonicalGameKeyV2 ?? (ck && !mkt.dhAmbiguous ? promoteToGameKeyV2(ck) : null);
  if (mkt.marketType === 'advance' && advKey) {
    const adv = store.getAdvanceGame(advKey);
    if (adv?.kalshi) {
      for (const side of ['away', 'home'] as const) {
        if (adv.kalshi[side].marketTicker === ticker) {
          return {
            game: adv,
            intent: { side, venue: 'kalshi', initialOrderSide: posSide, postOnly: false },
          };
        }
      }
    }
  }

  // Event identity does not prove contract identity. NFL props, period lines
  // and spreads share the moneyline's canonical key; substituting that row
  // would arm a different order. An unsupported exact target opens no book.
  return null;
}

/** Locate the live totals event + the index of the first line matching `match`.
 *  A line-identifying predicate (its own kalshi/poly `marketTicker`, or a held
 *  CLOB token) names exactly one line on one event, so doubleheaders never
 *  mis-resolve — no key normalization needed. Full-game totals only: a period
 *  total (F5 / 1H / quarter) isn't in the default getGameEvents set, so it
 *  returns null and the caller deep-links truthfully rather than opening a
 *  full-game line of the same number. */
function findTotalEventLine(
  match: (line: TotalLine) => boolean,
): { event: GameEventRow; idx: number } | null {
  const store = useMarketStore.getState();
  for (const tab of SPORT_TABS) {
    const events = store.getGameEvents(tab.id as Sport, { dateFilter: 'all', statusFilter: 'all' });
    for (const event of events) {
      const idx = event.totals.findIndex(match);
      if (idx !== -1) return { event, idx };
    }
  }
  return null;
}

/** Panel target (synth GameRow + intent) for a resolved totals line. away/home
 *  slots point at one book, so `intent.side` is a no-op; the panel's YES/NO
 *  toggle (initialOrderSide + the page's panelMarketSide) is the real control.
 *  The betLineContext is NOT built here — it's derived live from the synth key
 *  while the panel is open (liveTotalBetLineContext) so alternate-line chips and
 *  their books track the feed instead of freezing at click time. */
function totalPanelTarget(
  event: GameEventRow,
  idx: number,
  venue: 'kalshi' | 'polymarket',
  initialOrderSide: 'yes' | 'no',
): { game: GameRow; intent: TradeIntent } {
  return {
    game: synthesizeFromTotal(event, event.totals[idx]),
    intent: { side: 'away', venue, initialOrderSide, postOnly: false },
  };
}

/** Live totals betLineContext for an open panel keyed `<eventKey>|total|<lineKey>`
 *  (see synthesizeFromTotal). Re-derived from the CURRENT event on every feed
 *  tick so alternate-line chips and their `getGameForIdx` books track the feed,
 *  not frozen at click. `selectedIdx` stays the held line's index — a constant
 *  value — so the panel's own chip selection survives the rebuild: panelChipIdx
 *  keys on the selectedIdx VALUE, not on betLineContext identity
 *  (GameDetailPanel.tsx). Matches by totalLineKey so it covers Kalshi- and
 *  Poly-sourced lines alike. Exported for the nav regression test. */
export function liveTotalBetLineContext(gameKey: string): BetLineContext | null {
  // A native US total (`|total|pmus-<slug>`, #1030) has no K/P line or chips.
  if (!gameKey.includes('|total|') || gameKey.includes('|total|pmus-')) return null;
  const lineKey = gameKey.slice(gameKey.lastIndexOf('|') + 1);
  const hit = findTotalEventLine((line) => totalLineKey(line) === lineKey);
  if (!hit) return null;
  return buildBetLineContext({
    kind: 'total',
    event: hit.event,
    lines: hit.event.totals,
    selectedIdx: hit.idx,
    // No-op: the panel owns its chip selection after open (same as the
    // ValueDashboard totals tab). There's no dashboard selection to sync back.
    onSelectIdx: () => {},
  });
}

/** Issue #96: a totals position/order opens the EXACT totals market in the
 *  panel's totals mode (chip strip preselected to the held line, YES/NO =
 *  Over/Under), the same way a moneyline click opens its order book — not the
 *  game's moneyline shell (the resolveBuyPanelTarget fallback). Totals only;
 *  spreads use their own exact resolver (see positionClickTarget).
 *
 *  Kalshi side passes straight through with no inversion: a YES position is long
 *  Over, a NO position is long Under, both on the SAME market/line. Returns null
 *  on any miss (ticker not a total, event/line not in the feed) so the caller
 *  deep-links truthfully, never a different market family. */
function resolveTotalPanelTarget(
  ticker: string,
  posSide: 'yes' | 'no',
): { game: GameRow; intent: TradeIntent } | null {
  if (marketKindFromTicker(ticker) !== 'total') return null;
  const hit = findTotalEventLine((line) => line.kalshi?.marketTicker === ticker);
  return hit ? totalPanelTarget(hit.event, hit.idx, 'kalshi', posSide) : null;
}

/** Poly totals #96 (venue parity with Kalshi): a Poly total position's ticker is
 *  an opaque conditionId — not classifiable via marketKindFromTicker — so match
 *  by the HELD CLOB token (the same key resolveBuyPanelTarget's poly branch
 *  trusts) instead of the ticker string. Non-poly tickers carry no token and
 *  return null, leaving the Kalshi/moneyline path untouched. */
function resolvePolyTotalPanelTarget(
  ticker: string,
  posSide: 'yes' | 'no',
): { game: GameRow; intent: TradeIntent } | null {
  const store = useMarketStore.getState();
  // A poly position card carries the market's conditionId, but the live store is
  // keyed by the PM-… slug — so resolve conditionId → slug before getMarket (fall
  // back to the input for tickers that are already live slugs). This is the same
  // step nearby poly code takes before getMarket; without it getMarket misses on
  // a real position card and the totals nav silently never fires (review: Brendan).
  const slug = store.getTickerByConditionId(ticker) ?? ticker;
  const mkt = store.getMarket(slug);
  const heldToken = posSide === 'yes' ? mkt?.yesTokenId : mkt?.noTokenId;
  return heldToken ? resolvePolyTotalByToken(heldToken) : null;
}

/** Poly total OPEN ORDERS identify by CLOB token id (asset_id), like every other
 *  poly order — same token-matched totals resolution as the position path.
 *  Exported for the nav regression test. */
export function resolvePolyTotalOrderTarget(
  assetId: string,
): { game: GameRow; intent: TradeIntent } | null {
  return assetId ? resolvePolyTotalByToken(assetId) : null;
}

/** Find the totals line one of whose Poly tokens equals `token` and open it on
 *  the matching side: the YES token is Over, the NO token is Under. Side is read
 *  from which token the frame/order says is held, so it can't invert. */
function resolvePolyTotalByToken(
  token: string,
): { game: GameRow; intent: TradeIntent } | null {
  const hit = findTotalEventLine(
    (line) => line.polymarket?.tokenId === token || line.polymarket?.noTokenId === token,
  );
  if (!hit) return null;
  const line = hit.event.totals[hit.idx];
  const initialOrderSide = line.polymarket?.tokenId === token ? 'yes' : 'no';
  return totalPanelTarget(hit.event, hit.idx, 'polymarket', initialOrderSide);
}

// ── MLB player props (#184) ───────────────────────────────────────────────
//
// A player-prop position resolved to the PARENT GAME'S MONEYLINE: a prop ticker
// classifies as 'moneyline' (marketKindFromTicker knows only spread/total), so
// it fell through to resolveBuyPanelTarget, matched no moneyline slot, and hit
// the canonical-key fallback — and hydra stamps every prop frame with its parent
// game's key, so that fallback always hits. Same defect class #96 fixed for
// totals and #149 fixed for GameDetail prop rows; this is the Position Tracker
// half, and it follows #96's shape exactly.
//
// Identity is taken from the AUTHORITATIVE parsers, never from display text:
// `classifyMlbExtendedTicker` for Kalshi, `classifyPolyExtendedTicker` for the
// Poly slug. The rung itself is then matched by its OWN market ticker (Kalshi)
// or its OWN CLOB token (Poly) — so the market the panel opens is the market the
// trader holds by construction, not by inference. Anything unresolvable returns
// null and the caller deep-links; it never degrades to a nearby market.

/** A located prop rung — everything the panel target and its threshold strip
 *  need, with the parent row supplying the synth's non-book fields. */
interface PropRungHit {
  game: GameRow;
  stat: PropStat;
  ladder: PlayerPropLadder;
  rung: PropRung;
}

/** Threshold-free ladder title ("Elly De La Cruz (CIN) Hits"). Poly-only
 *  ladders carry no team code — omit the "()" rather than print an empty one. */
function propLadderLabel(ladder: PlayerPropLadder, stat: PropStat): string {
  const team = ladder.teamCode ? ` (${ladder.teamCode})` : '';
  return `${ladder.playerName}${team} ${PROP_STAT_LABELS[stat]}`;
}

/** Exact parent identity only. A cold Tracker may receive its held prop before
 *  any moneyline. In that case use canonical prop metadata, without borrowing
 *  a neighbouring game or manufacturing a tradeable moneyline book. The caller
 *  must still resolve the exact held rung before opening a ticket. */
function propGameRow(gameKey: string, stat: PropStat): GameRow | null {
  const store = useMarketStore.getState();
  const sport = propStatSport(stat);
  const parent = store.getGames(sport, 'moneyline', { dateFilter: 'all', statusFilter: 'all' })
    .find((g) => g.key === gameKey);
  if (parent) return parent;
  const key = parseGameKeyV2(gameKey);
  if (!key || key.sport !== sport) return null;
  const prop = store.getLivePlayerPropMarkets(sport).find((m) =>
    m.gameKey === gameKey && m.awayCode && m.homeCode &&
    [m.awayCode, m.homeCode].sort().join('|') === `${key.teamLo}|${key.teamHi}`);
  if (!prop?.awayCode || !prop.homeCode) return null;
  return {
    key: gameKey, sport, date: key.date,
    timePt: formatStartTimeLocal(prop.scheduledStartMs), startTimeMs: prop.scheduledStartMs,
    gameState: 'pre', finalWinnerSide: null,
    awayCode: prop.awayCode, homeCode: prop.homeCode,
    awayName: prop.awayCode, homeName: prop.homeCode,
    kalshi: null, polymarket: null,
  };
}

/** One player's ladder within a stat group, with its parent row. */
function propLadderAt(
  gameKey: string,
  stat: PropStat,
  playerKey: string,
  nativeTicker?: string,
): { game: GameRow; ladder: PlayerPropLadder } | null {
  const game = propGameRow(gameKey, stat);
  if (!game) return null;
  const ladder = useMarketStore
    .getState()
    .getPlayerProps(propStatSport(stat), gameKey, nativeTicker ? [nativeTicker] : undefined)
    .find((g) => g.stat === stat)
    ?.players.find((p) => p.playerKey === playerKey);
  return ladder ? { game, ladder } : null;
}

/** First rung in the game's `stat` group satisfying `match`. The predicate is
 *  always an exact ticker/token equality, so it names exactly one rung. */
function findPropRung(
  gameKey: string,
  stat: PropStat,
  match: (rung: PropRung) => boolean,
  nativeTicker?: string,
): PropRungHit | null {
  const game = propGameRow(gameKey, stat);
  if (!game) return null;
  const group = useMarketStore
    .getState()
    .getPlayerProps(propStatSport(stat), gameKey, nativeTicker ? [nativeTicker] : undefined)
    .find((g) => g.stat === stat);
  for (const ladder of group?.players ?? []) {
    const rung = ladder.rungs.find(match);
    if (rung) return { game, stat, ladder, rung };
  }
  return null;
}

/** The rung's synth GameRow — byte-identical in shape to the one
 *  GameLinesSection builds for the Player Props tab, so the same market
 *  presents the same way whichever surface opened it. Both away/home slots
 *  carry the one book (a rung is a single binary outcome), which is why
 *  `intent.side` is a no-op and the panel's YES/NO toggle is the real control.
 *
 *  The two NAME slots are the exception: they carry the outcome and its
 *  complement (#251), because `outcomeSideLabel` reads them as the ticket's
 *  YES/NO wording. Writing one label into both would make BUY YES and BUY NO
 *  read identically. */
function propRungSynth(hit: PropRungHit): GameRow {
  const { game, stat, ladder, rung } = hit;
  const { label, notLabel } = propRungLabels(
    ladder.playerName,
    ladder.teamCode,
    stat,
    rung.threshold,
    ladder.playerKey,
  );
  return {
    ...game,
    key: propRungKey(game.key, stat, ladder.playerKey, rung.threshold),
    gameState: 'pre',
    finalWinnerSide: null,
    awayName: label,
    homeName: notLabel,
    kalshi: rung.kalshi ? { away: rung.kalshi, home: rung.kalshi } : null,
    polymarket: rung.polymarket ? { away: rung.polymarket, home: rung.polymarket } : null,
  };
}

function propPanelTarget(
  hit: PropRungHit,
  venue: 'kalshi' | 'polymarket',
  initialOrderSide: 'yes' | 'no',
): { game: GameRow; intent: TradeIntent } {
  return {
    game: propRungSynth(hit),
    intent: { side: 'away', venue, initialOrderSide, postOnly: false },
  };
}

/** Kalshi player-prop position/order → its exact rung. The held side passes
 *  straight through with no inversion: a rung is one binary market, and YES/NO
 *  on it are the two sides of that same market (the Kalshi totals rule). */
function resolveKalshiPropPanelTarget(
  ticker: string,
  posSide: 'yes' | 'no',
): { game: GameRow; intent: TradeIntent } | null {
  // MLB by its ticker classifier; every wire prop sport by the exact series
  // (lib/sport/propStat). Either way the rung below is the EXACT ticker.
  const mlb = classifyMlbExtendedTicker(ticker);
  const stat = mlb?.kind === 'prop' ? mlb.stat : propStatOfKalshiSeries(ticker);
  if (!stat) return null;
  const gameKey = useMarketStore.getState().getGameKeyV2ForTicker(ticker);
  if (!gameKey) return null;
  const hit = findPropRung(gameKey, stat, (rung) => rung.kalshi?.marketTicker === ticker, ticker);
  return hit ? propPanelTarget(hit, 'kalshi', posSide) : null;
}

/** Find the prop rung one of whose Poly tokens equals `token`, and open it on
 *  the side that token names — read from WHICH token matched, so it can't
 *  invert. Poly prop books are a static Gamma snapshot with no orderbook on
 *  either engine (hydra 404s their orderbook), so the panel opens on the right
 *  market with no depth; that is the market's steady state, not a failure. */
function resolvePolyPropByToken(token: string): { game: GameRow; intent: TradeIntent } | null {
  const store = useMarketStore.getState();
  const slug = store.getTickerByTokenId(token);
  if (!slug) return null;
  const cls = classifyPolyExtendedTicker(slug);
  if (cls?.kind !== 'prop') return null;
  const gameKey = store.getGameKeyV2ForTicker(slug);
  if (!gameKey) return null;
  const hit = findPropRung(
    gameKey,
    cls.stat,
    (rung) => rung.polymarket?.tokenId === token || rung.polymarket?.noTokenId === token,
  );
  if (!hit) return null;
  return propPanelTarget(hit, 'polymarket', hit.rung.polymarket?.tokenId === token ? 'yes' : 'no');
}

/** Poly prop POSITION cards carry the market's conditionId, and the live store
 *  keys frames by the PM-… slug — so normalize before getMarket, or the lookup
 *  misses on every real card and the nav silently never fires (the round-2
 *  review catch on #96's totals twin). Falls back to the input for tickers that
 *  are already live slugs. */
function resolvePolyPropPanelTarget(
  ticker: string,
  posSide: 'yes' | 'no',
): { game: GameRow; intent: TradeIntent } | null {
  const store = useMarketStore.getState();
  const mkt = store.getMarket(store.getTickerByConditionId(ticker) ?? ticker);
  const heldToken = posSide === 'yes' ? mkt?.yesTokenId : mkt?.noTokenId;
  return heldToken ? resolvePolyPropByToken(heldToken) : null;
}

/** Poly prop OPEN ORDERS identify by CLOB token id (asset_id) like every other
 *  poly order — same token-matched resolution as the position path. Exported
 *  for the nav regression test. */
export function resolvePolyPropOrderTarget(
  assetId: string,
): { game: GameRow; intent: TradeIntent } | null {
  return assetId ? resolvePolyPropByToken(assetId) : null;
}

/** Is this ticker a player prop at all? The gate for "never fall back to the
 *  parent moneyline": a prop that can't be resolved must deep-link, not open a
 *  plausible neighbour. Classification is by TICKER GRAMMAR, so it still fires
 *  for a prop whose frame hasn't streamed in — exactly the case where the
 *  moneyline fallback would otherwise have been most confident. Accepts a Poly
 *  conditionId (what a poly position card carries) as well as a slug. */
function isPropTicker(ticker: string): boolean {
  if (propStatOfKalshiSeries(ticker)) return true;
  if (classifyMlbExtendedTicker(ticker)?.kind === 'prop') return true;
  const store = useMarketStore.getState();
  return classifyPolyExtendedTicker(store.getTickerByConditionId(ticker) ?? ticker)?.kind === 'prop';
}

/** Poly open orders identify by CLOB token id (asset_id), not a marketStore
 *  ticker — resolve the token to its live game + team slot the same way
 *  resolveBuyPanelTarget's token branch does, so a poly order row/card click
 *  opens the buy panel like every other surface (they used to hard-navigate
 *  to GameDetail, the one click-contract holdout). Runs on click only, so
 *  the all-sports sweep cost is fine (same shape as describePolyOrder's).
 *  Multi-outcome (soccer) tokens don't map to a plain GameRow here — they
 *  return null and the caller keeps its GameDetail-nav fallback. */
function resolvePolyOrderPanelTarget(
  assetId: string,
): { game: GameRow; intent: TradeIntent } | null {
  if (!assetId) return null;
  const store = useMarketStore.getState();
  for (const tab of SPORT_TABS) {
    const games = store.getGames(tab.id, 'moneyline', { dateFilter: 'all', statusFilter: 'all' });
    for (const g of games) {
      if (g.gameState === 'final') continue;
      const poly = g.polymarket;
      if (!poly) continue;
      for (const teamSide of ['away', 'home'] as const) {
        const book = poly[teamSide];
        if (book.tokenId === assetId) {
          return {
            game: g,
            intent: { side: teamSide, venue: 'polymarket', initialOrderSide: 'yes', postOnly: false },
          };
        }
        if (book.noTokenId === assetId) {
          return {
            game: g,
            intent: { side: teamSide, venue: 'polymarket', initialOrderSide: 'no', postOnly: false },
          };
        }
      }
    }
  }
  return null;
}

/** Everything an open prop panel needs, re-derived from the CURRENT ladder:
 *  the held rung's synth (so the order ticket's book keeps ticking instead of
 *  freezing on the click-time snapshot) and its threshold strip (so a SIBLING
 *  rung's chip price tracks the feed too — the round-1 review catch on #96).
 *  Never captured at click; there is deliberately nowhere to store it.
 *
 *  ONE function rather than two because both outputs need the same ladder, and
 *  building it is the expensive part: `getMlbPlayerProps` walks every MLB frame
 *  in the store (~5k on a full slate) and builds every player's ladder. Two
 *  independent callers cost 7.3ms per feed tick — 29ms/s at the panel's 4Hz
 *  clock, measured on a 5,040-market slate; this halves it, and it is the same
 *  once-per-tick cost GameDetail already pays for the same data.
 *
 *  The strip reuses GameDetail's ladder machinery wholesale rather than
 *  restating it, so the strip a prop position opens is the strip its game page
 *  shows. Selection is pinned by rung IDENTITY (the held rung's own market
 *  ticker) — never an array index — so a rung that stops being listed falls
 *  back to the strip's own default instead of sliding the selection onto a
 *  different threshold. `betLineContext` is null for a lone-rung ladder: one
 *  rung is not a ladder, and the panel renders it as an ordinary market.
 *
 *  Null overall when the rung has left the feed — the caller keeps its
 *  snapshot rather than blanking the panel. Exported for the regression test. */
export function livePropPanel(
  synthKey: string,
  nativeTicker?: string,
): { game: GameRow; betLineContext: BetLineContext | null } | null {
  const parsed = parsePropSynthKey(synthKey);
  if (!parsed) return null;
  const stat = parsed.stat;
  const found = propLadderAt(parsed.gameKey, stat, parsed.playerKey, nativeTicker);
  if (!found) return null;
  const { game, ladder } = found;
  const held = ladder.rungs.find((r) => r.threshold === parsed.threshold
    && (!nativeTicker || r.kalshi?.marketTicker === nativeTicker));
  if (!held) return null;
  const groupKey = propGroupKey(game.key, stat, ladder.playerKey);
  const groupLabel = propLadderLabel(ladder, stat);
  // Synthesized once and reused: the held rung's synth IS the panel's game AND
  // the object the strip carries for that rung, so a chip and the ticket can
  // never disagree about a rung's book.
  const synths = ladder.rungs.map((rung) =>
    propRungSynth({ game, stat, ladder, rung }),
  );
  const heldSynth = synths[ladder.rungs.indexOf(held)];
  const rows: LadderSourceRow[] = ladder.rungs.map((rung, i) => ({
    // The panel has no density filters (those are GameDetail's Hide-0-Vol /
    // Hide-settled toolbar) — every listed rung belongs on the strip.
    visible: true,
    // Read by buildLadderTableRows only to stamp row.settled, which
    // buildLadderBetLineContext never consumes. Not a settlement claim.
    settled: false,
    synth: synths[i],
    singleSide: 'away' as const,
    ladder: { groupKey, groupLabel, rungLabel: propRungTag(stat, rung.threshold) },
  }));
  const heldChipKey = chipKeyFor(held.kalshi, held.polymarket, `${heldSynth.key}|away`);
  const built = buildLadderTableRows(rows, new Map([[groupKey, heldChipKey]]));
  const row = built.rows.find((r) => r.key === groupKey);
  // Only hand over a strip that AGREES with the ticket: the selected chip must
  // be the rung the panel is trading, or there is no strip.
  //
  // Today the mismatch branch CANNOT fire, and saying so is more useful than
  // implying it guards a live bug: buildLadderTableRows only drops a rung with
  // no book on EITHER venue, and every rung getMlbPlayerProps emits carries at
  // least one (a Kalshi rung always has its own book; a Poly-only rung is
  // created with the Poly one). Probed directly — stripping a rung of every
  // price signal leaves the book object in place, so it stays in the chips.
  // It is kept as an invariant, not a fix: if a future venue join ever yields a
  // bookless rung, the held chip would go missing, the stored selection would
  // miss, and the strip would fall back to its closest-to-50¢ default —
  // highlighting a DIFFERENT threshold than the ticket trades. That is the
  // stranded-highlight class from #141, and withholding beats disagreeing.
  // Also null for a lone-rung ladder, which has no chips at all.
  const stripAgrees =
    row?.chips != null &&
    row.selectedIdx != null &&
    row.chips[row.selectedIdx]?.key === heldChipKey;
  return {
    game: heldSynth,
    // No-op onSelect: the panel owns its chip selection after open, same as the
    // totals strip above — there is no dashboard selection here to sync back.
    betLineContext: stripAgrees
      ? buildLadderBetLineContext(row, built.rungTargets.get(groupKey), () => {})
      : null,
  };
}

/** Thin readers over {@link livePropPanel} — they call the SAME function the
 *  panel does, so a test through either exercises the real derivation rather
 *  than a re-implementation of it. */
export function livePropRungSynth(synthKey: string): GameRow | null {
  return livePropPanel(synthKey)?.game ?? null;
}

export function livePropBetLineContext(gameKey: string): BetLineContext | null {
  return livePropPanel(gameKey)?.betLineContext ?? null;
}

/** The canonical key a frame uses to point at its related game, normalized to
 *  the uniform 5-seg V2 shape. Null when hydra hasn't stamped the
 *  relationship — the contractually honest "no linkage" state. */
function linkedGameKey(mkt: HydraMarket | null | undefined): string | null {
  const v2 = mkt?.canonicalGameKeyV2 ?? null;
  if (v2 && parseGameKeyV2(v2)) return v2;
  const legacy = mkt?.canonicalGameKey ?? null;
  if (!legacy) return null;
  const promoted = promoteToGameKeyV2(legacy);
  return parseGameKeyV2(promoted) ? promoted : null;
}

/** Locate the live row a canonical key names. Multi-outcome sports (soccer)
 *  never appear in getGames — their rows come from getMultiOutcomeEvents,
 *  whose event key is the same V2 key — so the getter is chosen by sport.
 *  Exact V2 match first, legacy normalized blend as the shape-flip fallback:
 *  the same matching discipline resolveBuyPanelTarget uses. */
function findRowByCanonicalKey(
  sport: string,
  keyV2: string,
  legacyKey: string | null,
): { key: string; sport: string; awayCode: string; homeCode: string } | null {
  const sp = sport.toLowerCase() as Sport;
  const store = useMarketStore.getState();
  const rows: { key: string; sport: string; awayCode: string; homeCode: string }[] =
    MULTI_OUTCOME_SPORTS.has(sp)
      ? store.getMultiOutcomeEvents(sp, { dateFilter: 'all', statusFilter: 'all' })
      : store.getGames(sp, 'moneyline', { dateFilter: 'all', statusFilter: 'all' });
  const want = legacyKey ? normalizeGameKey(legacyKey) : null;
  return (
    rows.find((r) => r.key === keyV2) ??
    (want ? (rows.find((r) => normalizeGameKey(r.key) === want) ?? null) : null)
  );
}

function gameDetailHref(ticker: string, side: 'yes' | 'no'): string | null {
  const query = `?ticker=${encodeURIComponent(ticker)}&side=${side}`;
  // Prefer the live market frame: hydra's canonical codes + v2 key produce
  // the sport-aware, doubleheader-correct URL GameDetail actually resolves.
  // Raw ticker codes can differ from canonical — the A's are ATH on Kalshi
  // tickers but OAK canonically, so the legacy ticker-code URL landed on an
  // empty detail page ("no consensus, no bets" — Dan, Jul 8).
  const mkt = useMarketStore.getState().getMarket(ticker);
  if (mkt?.canonicalAwayCode && mkt.canonicalHomeCode && mkt.sport) {
    const v2 = parseGameKeyV2(mkt.canonicalGameKeyV2);
    const date = v2?.date ?? mkt.canonicalGameKey?.split('|')[1];
    if (date) {
      return (
        canonicalGameDetailHref({
          sport: mkt.sport,
          date,
          awayCode: mkt.canonicalAwayCode,
          homeCode: mkt.canonicalHomeCode,
          gameNo: v2?.gameNo,
        }) + query
      );
    }
  }
  // Single-team futures (winner markets like KXMENWORLDCUP-26-ES): the frame
  // carries no matchup codes, so the canonical branch above can't fire — but
  // once hydra has stamped the leg with the related game's canonical key
  // (issue #98: a cup-winner leg becomes the final's advance market once the
  // pairing is deterministic), that key IS the link. Look the row up by key
  // and deep-link to it. No key, or no row yet, ⇒ no affordance: the icon's
  // presence signals a page exists, and an unstamped relationship is exactly
  // the state where guessing is forbidden.
  //
  // This replaced a display-name / ticker-prefix heuristic (d1dc57bb), which
  // was both fragile (renamed or prefix-ambiguous teams) and — for soccer,
  // the only series that has these — inert, since getGames is empty for
  // multi-outcome sports. Never reintroduce name matching here.
  const linkKey = linkedGameKey(mkt);
  if (mkt?.sport && linkKey) {
    const row = findRowByCanonicalKey(mkt.sport, linkKey, mkt.canonicalGameKey ?? null);
    if (row) {
      const rowHref = gameDetailHrefForRow(row);
      if (rowHref) return rowHref + query;
    }
  }
  // Fallback — market not in the feed (e.g. edge-gated NFL): legacy
  // sport-less URL from the raw ticker codes. GameDetail treats a missing
  // :sport as a wildcard and resolves whenever ticker codes ARE canonical.
  const parsed = parseTicker(ticker);
  if (!parsed.game || !parsed.date) return null;
  const a = parsed.game.awayCode;
  const b = parsed.game.homeCode;
  return `/game/${parsed.date}/${a}/${b}${query}`;
}

// ── #248: routed graph chips → focused Value Dashboard ──────────────────────
//
// The graph chip beside a market label has always meant "a chart surface exists
// for this market". For most sports that surface is the `/game/...` detail
// page, which opens the exact market from `?ticker=&side=` on a feed narrowed
// to that one game. Tennis alone is routed to the Value Dashboard's focused
// deep link instead:
//
//   - Tennis (#248) had no usable destination at all. A tennis ticker parses to
//     `game: null` and tennis frames routinely carry no canonical key, so ATP/WTA
//     rows showed either no chip or a URL GameDetail could not resolve. Routing
//     them could only improve matters, so it is unconditional.
//   - CFB was routed here too (#539, a destination preference). #1174 returned
//     it to Game Detail: the focused dashboard has to load the whole CFB board
//     before its ticket opens, which traders reported as a slow, indirect
//     journey compared with NFL. CFB has a working `/game/...` page, so its chip
//     now keeps `legacyHref` exactly like NFL. The dashboard still parses and
//     honours an existing CFB focus link.
//
// The destination re-derives the exact market from live rows and opens the
// EXISTING trade panel through it — never a second order-entry path.
//
// `legacyHref` is what makes the blast radius zero: it is the destination the
// calling surface produced before #248, returned byte for byte whenever the
// source is not PROVED to be a routed sport. That preserves both the
// destination and the chip's VISIBILITY — a surface with no chip does not gain
// one, and a Polymarket table row that had a Game Detail URL keeps it. The
// field is needed because those two facts differ per surface: Polymarket
// card/pinned paths can have no chip while the two Polymarket TABLE paths do
// have a legacy URL.
//
// Identity is taken from the store and the ticker grammar only. Never from a
// display label, a player name, or a `matchup` string — that is the #92 rule,
// and the reason a routed chip fails closed (`focus=unavailable`) instead of
// guessing a nearby market.

export interface GraphChipHrefInput {
  /** What the calling surface owns: a Kalshi market ticker, a Polymarket
   *  conditionId (position rows/cards), or a Polymarket CLOB token id (order
   *  rows/cards). Resolved to a market-store ticker before anything is built. */
  identifier: string;
  /** The orientation the SURFACE declares. Null when it has none to declare —
   *  a Polymarket order VM carries a CLOB identifier, not a side. Null is never
   *  substituted for: an orientation nothing proves fails closed. */
  side: 'yes' | 'no' | null;
  venue: 'kalshi' | 'polymarket';
  /** The href this surface produced before #248; null when it showed no chip. */
  legacyHref: string | null;
}

/** A graph-chip source resolved to the one market it names. */
interface ChipMarket {
  /** The market-store ticker/slug — the decisive market identity in the URL.
   *  Never a conditionId, never a CLOB token. */
  ticker: string;
  /** Orientation proved by WHICH of the market's two tokens the source names.
   *  Only the Polymarket token path can prove it; null everywhere else, where
   *  the surface's own declared side is already authoritative. */
  nativeSide: 'yes' | 'no' | null;
  /** The source named a token the resolved market does not own (a stale token
   *  index entry). #92's rule: an unprovable native-YES identity opens nothing. */
  ambiguousOrientation: boolean;
}

/** Resolve a surface identifier to the exact market-store ticker it names.
 *  Kalshi surfaces already carry the market ticker. Polymarket surfaces carry
 *  a conditionId or a CLOB token id; both resolve through the store's OWN
 *  indexes — the same seams the totals/props resolvers use — never through
 *  display text. Null when nothing in the store names the identifier, which is
 *  also the state where nothing about the source (tennis included) is proved. */
function resolveChipMarket(
  identifier: string,
  venue: 'kalshi' | 'polymarket',
): ChipMarket | null {
  if (!identifier) return null;
  if (venue === 'kalshi') {
    return { ticker: identifier, nativeSide: null, ambiguousOrientation: false };
  }
  const store = useMarketStore.getState();
  const byToken = store.getTickerByTokenId(identifier);
  if (byToken) {
    // WHICH token matched is the orientation proof — read from the market's own
    // frame so it cannot invert, and refused outright when the frame no longer
    // owns the token the index remembers.
    const mkt = store.getMarket(byToken);
    const nativeSide =
      mkt?.yesTokenId === identifier ? 'yes' : mkt?.noTokenId === identifier ? 'no' : null;
    return { ticker: byToken, nativeSide, ambiguousOrientation: nativeSide === null };
  }
  const ticker =
    store.getTickerByConditionId(identifier) ??
    // Surfaces that already hold the PM-… slug (a resolved description) pass it
    // straight through.
    (store.getMarket(identifier) ? identifier : null);
  return ticker ? { ticker, nativeSide: null, ambiguousOrientation: false } : null;
}

/** Focused-dashboard proof for a graph-chip source. Ticker GRAMMAR first
 *  (`KXATP…` / `KXWTA…` / a `PM-atp-…` slug classify with no live frame at
 *  all — exactly the absent-frame case), then the frame's own `sport`. Null for
 *  every source not proved to be a routed sport, which is the signal to hand
 *  back `legacyHref`.
 *
 *  CFB is deliberately absent (#1174): its chip keeps the Game Detail route,
 *  which opens the exact moneyline, spread or total line from `?ticker=`. */
function focusedSportForChip(
  ticker: string,
  mkt: HydraMarket | null,
): FocusedSport | null {
  // #604 — exact-series check BEFORE the ATP fallback: a Challenger ticker
  // must open the Challenger board, where its row actually lives. Display
  // classification and stored position data stay plain ATP.
  if (isAtpChallengerTicker(ticker)) return 'atp-challenger';
  const sport = sportFromTicker(ticker) ?? coerceSport(mkt?.sport);
  return sport === 'atp' || sport === 'wta' ? sport : null;
}

/** The exact away/home code pair the EXISTING route already proved for this
 *  market: hydra's canonical codes when the frame carries them (the pair the
 *  `/game/:sport/:date/:away/:home` route is built from), else the ticker's own
 *  competitor codes — a tennis match ticker encodes both positionally. Order is
 *  preserved and nothing is normalized; the pair is an identity, not a label. */
function chipRouteCodePair(mkt: HydraMarket | null, parsed: ParsedTicker): string | null {
  if (mkt?.canonicalAwayCode && mkt.canonicalHomeCode) {
    return `${mkt.canonicalAwayCode}|${mkt.canonicalHomeCode}`;
  }
  if (parsed.game?.awayCode && parsed.game.homeCode) {
    return `${parsed.game.awayCode}|${parsed.game.homeCode}`;
  }
  // Positional order, deliberately unsorted: the recorded contract pins
  // `GEA|SHA` for `…-26AUG01GEASHA-GEA`, and alpha-sorting would mint a
  // different identity. If a row happens to order its codes the other way the
  // cost is a fail-closed hydration miss, never a wrong market.
  const p1 = parsed.tennis?.player1.playerCode;
  const p2 = parsed.tennis?.player2.playerCode;
  return p1 && p2 ? `${p1}|${p2}` : null;
}

/**
 * The graph-chip destination for one surface. Routed sources (ATP/WTA — see
 * `focusedSportForChip`) get the focused Value Dashboard URL; every other
 * source, CFB included (#1174), gets `legacyHref` back untouched.
 *
 * Event identity precedence is the repo contract's: the exact five-part row key
 * from `marketStore.getGameKeyV2ForTicker` — the store's central fail-closed
 * identity seam, which returns null on a doubleheader-ambiguous frame — and,
 * only when that is absent, the exact route/ticker code pair the source already
 * proved. `promoteToGameKeyV2` is deliberately NOT called here: promoting a
 * legacy key in the navigation layer is the gameNo=1 trap the store getter
 * exists to close.
 *
 * A routed source whose identity is incomplete keeps its chip and emits
 * `focus=unavailable` rather than silently disappearing. (Tennis chips route
 * to the focused dashboard by preference — #248; since #1284 a tennis Game
 * Detail page exists, but routing them there is a separate product call.)
 *
 * Exported so the #248 tests exercise the SAME seam every render path calls.
 */
export function graphChipHref(input: GraphChipHrefInput): string | null {
  const resolved = resolveChipMarket(input.identifier, input.venue);
  if (!resolved) return input.legacyHref;

  const { ticker, nativeSide, ambiguousOrientation } = resolved;
  const store = useMarketStore.getState();
  const mkt = store.getMarket(ticker);
  const sport = focusedSportForChip(ticker, mkt);
  if (!sport) return input.legacyHref;

  const venue = input.venue;
  const parsed = parseTicker(ticker);
  const gameKeyV2 = store.getGameKeyV2ForTicker(ticker);
  // Only a true five-part key is a row key. `getGameKeyV2ForTicker` passes a
  // sport-less analytics key through unchanged, and that is not a row identity.
  const rowKey = gameKeyV2 && gameKeyV2.split('|').length === 5 ? gameKeyV2 : null;
  // The date must be the one the ROW will carry, because both the board's date
  // filter and the matcher's `row.date === date` guard compare against
  // `GameRow.date` — and that is derived from the event START TIME in local
  // terms, never from the canonical key. Hydra keys tennis off the UTC calendar
  // day, so a late-UTC European match keyed `…|2026-08-02|…` renders on the
  // 2026-08-01 board: measured live, 10 of 16 non-final tennis rows drifted a
  // day, and every one of those chips landed on the unavailable notice.
  const date = resolvedGameDate(ticker) ?? parsed.date ?? null;

  // Orientation is proved or it is nothing. `nativeSide` is the token's own
  // proof; failing that, the surface's declared side. A Polymarket order whose
  // identifier is a conditionId (no `asset_id` on the CLOB payload) resolves to
  // a market but proves NO side — exactly the "missing or ambiguous native-YES
  // identity opens nothing" rule, and the same exit a token the market no longer
  // owns takes. Substituting a default here would pre-fill a trade ticket on a
  // possibly inverted side.
  const side = nativeSide ?? input.side;
  if (ambiguousOrientation || !side) {
    return focusedDashboardHref({
      kind: 'unavailable',
      sport,
      ...(date ? { date } : {}),
      ticker,
      venue,
      reason: 'ambiguous-source-identity',
    });
  }

  const eventIdentity = rowKey ?? chipRouteCodePair(mkt, parsed);
  // Roster-less identity space. ATP/WTA are in marketStore's ROSTERLESS_SPORTS:
  // when a frame carries `canonicalTeams`, the row builder REWRITES that row's
  // away/home codes out of hydra's lossy 3-char ticker pack (`GEA`/`SHA`) into
  // its full-surname canonical space (`Gea`/`Shapovalov`), and the hydration
  // matcher compares a two-code identity with strict, case-sensitive `===`.
  //
  // So the one shape that reaches this fallback — `canonicalTeams` present,
  // canonical KEY absent, hence no five-part row key — would emit a pair that
  // provably cannot match any row: a chip that looks live, burns the full
  // FOCUSED_DASHBOARD_WAIT_MS, and freezes on `unavailable` anyway. Say it up
  // front instead. (Wrong-row risk was nil either way — the book match still
  // requires an exact `marketTicker` — so this is availability, not safety.)
  //
  // Re-expressing the pair here is deliberately NOT done: `rosterlessDisplayCodes`
  // is private to marketStore, and the only exported route to a row's resolved
  // codes is a full `getGames` rebuild — the per-row-per-render sweep this file
  // refuses on purpose (#214's `inLiveFeed` note). Duplicating that resolution
  // in the navigation layer would recreate the drift this guard exists to catch.
  const unmatchableCodeSpace = !rowKey && !!mkt?.canonicalTeams;
  if (eventIdentity && date && !unmatchableCodeSpace) {
    return focusedDashboardHref({
      kind: 'market',
      target: { sport, date, eventIdentity, ticker, venue, side },
    });
  }
  return focusedDashboardHref({
    kind: 'unavailable',
    sport,
    ...(date ? { date } : {}),
    ticker,
    venue,
    side,
    reason: 'missing-event-identity',
  });
}

/** Graph-chip href for a position surface — card, mini, compact, pinned card,
 *  pinned compact, pinned table row, and the Kalshi/Polymarket table rows. One
 *  call so every position surface routes identically. Exported for the tests. */
export function positionChipHref(
  vm: Pick<PositionCardVM, 'venue' | 'ticker' | 'side'>,
  legacyHref: string | null,
): string | null {
  // #1226 — a Polymarket US row's link comes from the US catalogue's game on
  // the board (`usRowLink`), handed in as the legacy href; the store knows
  // nothing of its slug.
  if (vm.venue === 'polymarket_us') return legacyHref;
  return graphChipHref({
    identifier: vm.ticker,
    side: vm.side,
    venue: vm.venue,
    legacyHref,
  });
}

/** Graph-chip href for an Open Orders surface — card, Kalshi/Polymarket table
 *  rows, pinned card, pinned compact, pinned table row.
 *
 *  A Polymarket order VM carries NO side of its own (`OrderCardVM.side` is null
 *  for a CLOB token): which of the market's two tokens it names is the only
 *  honest proof of orientation, and `graphChipHref` reads that from the live
 *  frame. The null is passed straight through — never defaulted — so an order
 *  whose identifier proves no orientation (a conditionId rather than a token)
 *  fails closed instead of guessing YES. A Kalshi order always carries its
 *  side; without one there is nothing to route, so the surface keeps its
 *  pre-#248 href (which that same gate already made null). Exported for the
 *  tests. */
export function orderChipHref(
  vm: Pick<OrderCardVM, 'venue' | 'ticker' | 'side'>,
  legacyHref: string | null,
): string | null {
  if (vm.venue === 'polymarket_us') return legacyHref;
  if (!vm.side && vm.venue !== 'polymarket') return legacyHref;
  return graphChipHref({
    identifier: vm.ticker,
    side: vm.side,
    venue: vm.venue,
    legacyHref,
  });
}

/** The Game Detail URL the two Polymarket TABLE paths (open positions, open
 *  orders) each assembled inline before #248 — extracted verbatim so both feed
 *  the graph-chip seam ONE recorded legacy destination instead of two copies of
 *  the same template literal. Null whenever the description cannot prove an
 *  `AWAY @ HOME` matchup: moneyline descriptions carry one, frame-fallback
 *  (spread/total) descriptions do not, and those rows showed no chip before and
 *  show none now. Exported for the tests. */
export function polyTableLegacyGameHref(
  desc: { matchup: string; date: string; betTeam: string; side: 'yes' | 'no' } | null,
): string | null {
  if (!desc) return null;
  const [away, home] = desc.matchup.split(' @ ');
  if (!away || !home) return null;
  const params = new URLSearchParams({ team: desc.betTeam, side: desc.side });
  return `/game/${desc.date}/${away}/${home}?${params.toString()}`;
}

/** Is this ticker in the live feed at all? The cheap (Map-lookup) precondition
 *  for an order panel — used only to keep the row tooltip honest, never to
 *  gate the click. Exported for the #214 tests, which pin the one thing the
 *  tooltip asserts: that a market it calls absent really is absent. */
export function inLiveFeed(ticker: string): boolean {
  return useMarketStore.getState().getMarket(ticker) != null;
}

/** Everything a position card/row click needs, resolved together.
 *
 *  The two results are INDEPENDENT (issue #98): `panel` drives the click and
 *  `href` drives the game-detail icon. Gating the click on `href` — the old
 *  behavior — left the WC winner card completely inert, because a futures leg
 *  has no game page until hydra stamps its relationship, while its order panel
 *  was resolvable the whole time. Exported for the component tests, which
 *  exercise this exact seam the card grid and table rows use. */
export function positionClickTarget(
  ticker: string,
  side: 'yes' | 'no',
): { panel: { game: GameRow; intent: TradeIntent } | null; href: string | null } {
  const href = gameDetailHref(ticker, side);
  // Totals (issue #96) resolve ONLY to the exact totals market. Try the Kalshi
  // matcher (gated on the parsed ticker), then the Poly matcher (a Poly total's
  // conditionId ticker doesn't parse as a total, so it's matched by held token).
  const totalPanel =
    resolveTotalPanelTarget(ticker, side) ?? resolvePolyTotalPanelTarget(ticker, side);
  if (totalPanel) return { panel: totalPanel, href };
  // A ticker that PARSES as a total but didn't resolve must NOT fall back to the
  // moneyline shell (criteria 1 & 5) — deep-link via href instead.
  if (marketKindFromTicker(ticker) === 'total') return { panel: null, href };
  // Player props (#184) resolve ONLY to the exact rung — the same rule #96 set
  // for totals. Kalshi matches on its own ticker; a Poly prop position carries
  // an opaque conditionId, so it matches on the HELD CLOB token instead.
  const propPanel =
    resolveKalshiPropPanelTarget(ticker, side) ?? resolvePolyPropPanelTarget(ticker, side);
  if (propPanel) return { panel: propPanel, href };
  // A ticker that IS a prop but didn't resolve must NOT reach the moneyline
  // fallback below — that fallback matches on the parent game's canonical key,
  // which hydra stamps on every prop frame, so it would ALWAYS hit and always
  // open the wrong contract. This line is the "no silent moneyline" guarantee.
  if (isPropTicker(ticker)) return { panel: null, href };
  const spreadPanel = resolveSpreadPanelTarget(ticker, side);
  if (spreadPanel) return { panel: spreadPanel, href };
  if (marketKindFromTicker(ticker) === 'spread') return { panel: null, href };
  const multiPanel = resolveMultiOutcomePanelTarget(ticker, side);
  if (multiPanel) return { panel: multiPanel, href };
  // Remaining families require an exact ticker/token match too.
  return { panel: resolveBuyPanelTarget(ticker, side), href };
}

export { gameDetailHref };

/** #1184 — the account's held Kalshi position for this ticker (and side, when
 *  known), from the current positions read; null when not held or ambiguous.
 *  Polymarket tokens and US slugs never match, so their rows keep their paths. */
export function heldKalshiPosition(ticker: string, side?: 'yes' | 'no'): KalshiPosition | null {
  const held = useAccountStore.getState().positions
    .filter((p) => p.ticker === ticker && (!side || p.side === side) && p.position !== 0);
  return held.length === 1 ? held[0] : null;
}

/** The click handler for an Open Orders card/row — shared by the Open Orders
 *  render paths so the decision lives in exactly one place.
 *
 *  NEVER gated on `href` (#214). The order panel and the game-detail page are
 *  INDEPENDENT surfaces: `openOrderPanel` resolves the panel from the live
 *  market itself, so a market can be perfectly tradable with no game page at
 *  all. Boxing and tennis frames can carry no canonical key, and their tickers
 *  parse to no game, so `gameDetailHref` has nothing to build a URL from — the
 *  old `href ? … : undefined` gate rendered those cards inert while their books
 *  were live. Same decoupling #98 applied to position cards; the Open Orders
 *  paths were left behind.
 *
 *  A click that resolves neither a panel nor a destination is a safe no-op:
 *  `openOrderPanel` opens nothing rather than guessing a nearby market — with no
 *  canonical key its key-based fallback cannot fire at all, so resolution is
 *  exact-ticker-or-nothing. Position cards have behaved this way since #98.
 *
 *  Polymarket orders keep their own path: a poly VM's `ticker` IS a CLOB token
 *  id, never a parseable Kalshi ticker, so `href` is only its navigate fallback.
 *
 *  Exported so the regression tests exercise the SAME decision every render path
 *  uses instead of re-implementing the call site — the seam #98 established with
 *  `positionClickTarget`. */
export function orderCardOpenHandler(
  vm: Pick<OrderCardVM, 'venue' | 'ticker' | 'side'>,
  href: string | null,
  openPanel: (ticker: string, side: 'yes' | 'no' | null | undefined) => void,
  openPolyPanel: (assetId: string, fallbackHref: string | null) => void,
): () => void {
  if (vm.venue === 'polymarket') return () => openPolyPanel(vm.ticker, href);
  return () => openPanel(vm.ticker, vm.side);
}

export function formatStartTimeLocal(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '';
  const parts = _LOCAL_TIME_FMT.formatToParts(new Date(ms));
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
  const dayPeriod = (parts.find((p) => p.type === 'dayPeriod')?.value ?? '').toLowerCase();
  const ampm = dayPeriod.startsWith('p') ? 'p' : 'a';
  return hour && minute ? `${hour}:${minute}${ampm}` : '';
}

// Tints the "Now" (mark) cell so the trader can sweep the column for
// outliers. Buckets cover 0-99 with no gaps; null/undefined falls through
// to the muted default so a missing mark doesn't read as "in the money".
function nowColorClass(cents: number | null | undefined): string {
  if (cents == null) return 'text-muted-foreground';
  if (cents <= 15) return 'text-red-500';
  if (cents <= 25) return 'text-red-400';
  if (cents <= 35) return 'text-red-300';
  if (cents <= 45) return 'text-orange-400';
  if (cents <= 55) return 'text-yellow-400';
  if (cents <= 65) return 'text-[#9aa00d]';
  if (cents <= 75) return 'text-green-300';
  if (cents <= 85) return 'text-green-400';
  return 'text-green-500';
}

// `PositionSortKey` now lives in ./readingView, which owns the working-view
// slice that carries it (#828). Imported above rather than redeclared so the
// stored record's allowlist and the view's own type cannot drift apart.
type OrderSortKey = 'default' | 'size' | 'queue' | 'velocity';

/** Re-settle the orders table's row order at this cadence when sorting by a LIVE
 *  metric (queue/velocity). Sorting on a value that ticks every market update made
 *  rows leapfrog constantly ("positions spazzing"); snapshotting the sort keys on
 *  this interval keeps the order calm while the cells still render live values. */
const ORDER_SORT_STABILIZE_MS = 2000;

interface SortState<K extends string> {
  key: K;
  dir: ActiveSortDir;
}

// ── Cancel all: what it says about Polymarket US ────────────────

/** Each US account's own cancellable-order count from the last read that saw
 * the account whole, for one sign-in and Cancel all generation (`key`). The
 * read on screen when the key changed is `ignore`d: it predates the change
 * (a sweep), so nothing from it may stand in for an account later. */
export interface UsCancelCountMemory {
  key: string;
  counts: ReadonlyMap<string, number>;
  ignore: UsRailRead | null;
}

/** What Cancel all says about US: nothing, an unknown count ("+ US"), or a
 * count. A count may be the last one read: `latestReadFailed` is the code of a
 * read that failed outright (its last answer is still shown), `carried` the
 * accounts the latest read could not see whose last count stands in. Either
 * marks the count stale in the hover; the label keeps the number. */
export type UsCancelAllHint =
  | { kind: 'none' }
  | { kind: 'unknown' }
  | { kind: 'count'; count: number; latestReadFailed: string | null; carried: readonly string[] };

const NO_US_HINT: UsCancelAllHint = { kind: 'none' };
const UNKNOWN_US_HINT: UsCancelAllHint = { kind: 'unknown' };

const usAccountReadWhole = (account: UsRailAccount) =>
  account.error === null && account.readiness !== null && !account.ordersCapped;
const usAccountCancellable = (account: UsRailAccount) => usRailCancellableCount({ accounts: [account] }) ?? 0;

/** `memory` with what `read` saw whole; the same object when nothing changed. */
export function rememberUsCancelCounts(memory: UsCancelCountMemory, key: string, read: UsRailRead | null): UsCancelCountMemory {
  if (memory.key !== key) return { key, counts: new Map(), ignore: read };
  if (!read || read === memory.ignore) return memory;
  let counts: Map<string, number> | null = null;
  for (const account of read.accounts) {
    if (!usAccountReadWhole(account)) continue;
    const n = usAccountCancellable(account);
    if ((counts ?? memory.counts).get(account.accountId) === n) continue;
    counts ??= new Map(memory.counts);
    counts.set(account.accountId, n);
  }
  return counts ? { ...memory, counts } : memory;
}

/**
 * #1034 — the US part of the Cancel all label. Before any read has answered
 * for this sign-in nothing is claimed. A read that failed before ever
 * answering leaves the count unknown ("+ US") whenever the Tracker's own
 * account read found a US account, even if the shared presence check failed
 * too: US is never dropped from the label for a trader who has it. Once a
 * count is known, a later failure (the whole read, or one account) keeps the
 * last count rather than flipping to "+ US" on every failed poll; the hover
 * says it is stale. An account never read whole since the last sweep, or one
 * with more orders than a read covers, is unknown, never a number. The read
 * still on screen from before a sweep (`ignore`, kept by the rail while its
 * re-read is on the way or after that re-read failed) counts nothing: its
 * orders may be the ones just cancelled.
 */
export function usCancelAllHint({ venueReadable, hasUs, status, error, read, memory, ignore }: {
  venueReadable: boolean;
  hasUs: boolean | null;
  status: UsRail['status'];
  error: string | null;
  read: UsRailRead | null;
  memory: ReadonlyMap<string, number>;
  /** The read on screen when the counts were last forgotten (a sweep). */
  ignore: UsRailRead | null;
}): UsCancelAllHint {
  if (!venueReadable) return NO_US_HINT;
  if (!read) return status === 'unavailable' && hasUs !== false ? UNKNOWN_US_HINT : NO_US_HINT;
  const predatesSweep = read === ignore;
  let count = 0;
  const carried: string[] = [];
  for (const account of read.accounts) {
    if (account.ordersCapped) return UNKNOWN_US_HINT;
    if (!predatesSweep && usAccountReadWhole(account)) {
      count += usAccountCancellable(account);
      continue;
    }
    const last = memory.get(account.accountId);
    if (last === undefined) return UNKNOWN_US_HINT;
    count += last;
    carried.push(account.accountId);
  }
  if (count === 0) return NO_US_HINT;
  return { kind: 'count', count, latestReadFailed: status === 'unavailable' ? error ?? 'US_READ_UNAVAILABLE' : null, carried };
}

/** Where the Cancel all hover says a US count comes from: "as last read", or
 * why it is a stale count. */
function usCancelCountSource(hint: Extract<UsCancelAllHint, { kind: 'count' }>): string {
  if (hint.latestReadFailed === null && hint.carried.length === 0) return 'as last read';
  const why = hint.latestReadFailed !== null
    ? `the latest US read failed (${hint.latestReadFailed})`
    : hint.carried.length === 1
      ? `account …${hint.carried[0].slice(-4)} could not be read on the latest read, so its last count is used`
      : `${hint.carried.length} accounts could not be read on the latest read, so their last counts are used`;
  return `stale count: ${why}; the confirmation reads again`;
}

// ── Public component (role dispatcher) ──────────────────────────

interface PositionTrackerProps {
  onSelectTrader?: (row: OrgTraderRow) => void;
  onSelectOrg?: (row: OrgSummaryRow) => void;
}

export function PositionTracker({ onSelectTrader }: PositionTrackerProps = {}) {
  useAccountIdentityVersion((s) => s.version);
  // Consumer feed filters: every role renders TraderView, which declares the
  // held rows BY TICKER (feed-filters-per-view, 2026-09-12) — see
  // `trackerFeedIds` there. Managers/admins add the org table, which
  // declares its own rows the same way. Nothing is declared at this level:
  // an axis-less declaration here would broaden the whole connection. The
  // by-ticker fold carries the held rows' SPORTS, which is what the old
  // "declare the sports it holds" declaration here asked for.
  const role = useAuthStore((s) => s.user?.role ?? null);
  const userId = useAuthStore((s) => s.user?.id ?? null);

  if (role === 'manager') {
    return (
      <ManagerPositionsView
        currentUserId={userId}
        onSelectTrader={onSelectTrader}
      />
    );
  }
  if (role === 'admin') {
    return (
      <AdminPositionsView
        currentUserId={userId}
        onSelectTrader={onSelectTrader}
      />
    );
  }
  return <TraderView />;
}

// ── Trader view (own data only) ─────────────────────────────────

// `footer` slots extra content (the org-roster table for manager/admin) into
// the END of the MAIN content column — beside the viewport-tall sticky rail,
// not as a detached block below it — so it flows with positions/orders instead
// of stranding a gap under the rail. Bare <TraderView/> (traders, popouts)
// passes nothing → renders nothing extra.
/** A positions-page row: `venue`+`ticker` identify its GAME (what the highlight
 *  groups on); `key` identifies the ROW (a position and an order on one ticker
 *  share a venue+ticker but are distinct rows). The cleanup effect needs `key` so
 *  a hovered row that unmounts isn't kept "alive" by a same-ticker sibling — see
 *  isHoveredRowPresent. No `side`: rowGameKey never reads it (a game key is
 *  side-agnostic) and it's folded into `key` already. */
export type SameGameRowRef = {
  venue: TradingVenue;
  ticker: string;
  key: string;
};

function TraderView({
  footer,
  topBar,
  ownAccountChip,
}: {
  footer?: ReactNode;
  topBar?: ReactNode;
  /** Beside the Open Positions, Open Orders and Polymarket US titles: whose
   *  account they show, for a page whose top bar lists another organization. */
  ownAccountChip?: ReactNode;
} = {}) {
  const navigate = useNavigate();
  // #979: fill card annotation slots only on a page that shows annotations.
  const annotated = useCardAnnotationsActive();

  // ONE alert-preference snapshot for the whole view (#233), consumed by every
  // delivery gate: queue sound/visual, velocity visuals, divergence sound and
  // visual. `prefs` is reactive and drives render-time gates; `prefsRef`
  // mirrors it so the market-feed-driven effects can read current values
  // WITHOUT listing prefs in their dep arrays (which would re-run them, and
  // reseed transition maps, on every unrelated preference edit).
  //
  // Same-tab freshness comes from #231's subscribeAlertPreferences, which
  // useAlertPreferences subscribes to — so the ref is emit-time truth and no
  // emit site needs its own readAlertPreferences().
  //
  // The same snapshot feeds #234's alert pull-up, which is props-only for this
  // reason: it edits through these setters instead of taking a second
  // subscription of its own.
  const {
    prefs: alertPrefs,
    setChannelSoundEnabled,
    setChannelVisualEnabled,
    setChannelDeliveryThreshold,
  } = useAlertPreferences();
  const prefsRef = useRef(alertPrefs);
  useEffect(() => {
    prefsRef.current = alertPrefs;
  }, [alertPrefs]);
  // Signed-in user id — keys the per-order velocity-mute persistence so it
  // survives reload/remount and stays isolated per trader (issue #140).
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const status = useAccountStore((s) => s.status);
  const openOrders = useAccountStore((s) => s.openOrders);
  // Orders that may count as closing a position — none on a desk-wide list (#961).
  const closeCountOrders = useOrdersForCloseCount();
  const recentFills = useAccountStore((s) => s.recentFills);
  const keyMode = useAccountStore((s) => s.keyMode);
  const realizedPnlCents = useAccountStore((s) => s.realizedPnlCents);
  const realizedFeesCents = useAccountStore((s) => s.realizedFeesCents);
  const realizedPnlNetCents = useAccountStore((s) => s.realizedPnlNetCents);
  const displayRealizedPnlCents = realizedPnlNetCents ?? realizedPnlCents;
  // Rail-header balance readout (Bal K $x · P $y). Lives in the pinned
  // header band next to the Tracker/Fills toggles so the prong headers
  // stay one line (Brendan, Jul 9 — the docked prong header wrapped).
  // Poly NOT gated on the Kalshi connection — a Kalshi outage (Thu 3–5am
  // ET maintenance) must not blank the poly number.
  const kalshiBalance = useAccountStore((s) => s.balance);
  const kalshiBalanceHidden = useAccountStore((s) => s.balanceHidden);
  const polyBalance = usePolyBalance();
  const cancelOrder = useAccountStore((s) => s.cancelOrder);
  const cancelOrders = useAccountStore((s) => s.cancelOrders);
  // #241 — shared per-order cancel fence. Replaces the local cancelingOrder
  // flag for Kalshi rows so the control stays disabled until the venue's own
  // Open Orders list drops the ID. Polymarket keeps its hook-local flag.
  const kalshiCancelStates = useAccountStore((s) => s.kalshiCancelStates);
  const replaceOrder = useAccountStore((s) => s.replaceOrder);
  const refreshPositions = useAccountStore((s) => s.refreshPositions);
  const refreshOrders = useAccountStore((s) => s.refreshOrders);
  const ordersSync = useAccountStore((s) => s.ordersSync);
  const accountStatus = useAccountStore((s) => s.status);
  const headroom = useHeadroom();
  const refreshPnl = useAccountStore((s) => s.refreshPnl);

  const positionViewMode = useDisplaySettings((s) => s.positionViewMode);
  const orderViewMode = useDisplaySettings((s) => s.orderViewMode);
  const setPositionViewMode = useDisplaySettings((s) => s.setPositionViewMode);
  const setOrderViewMode = useDisplaySettings((s) => s.setOrderViewMode);
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  const feeAdjustedOdds = useDisplaySettings((s) => s.feeAdjustedOdds);
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const showRestingCloseCount = useRestingCloseCount();
  const toggleShowPnl = useDisplaySettings((s) => s.toggleShowPnl);
  // MM Program rebate for open-order QUOTES (bid/ask + last): the org's
  // enrollment × the trader's MM Rebate toggle, at the documented PREGAME
  // floor (see useOpenOrderQuoteRebatePct — this surface does not infer live
  // state). The toggle lives in the global StatusBar, so flipping it there
  // moves these cards and rows. Order TOTALS (Cost / To Win) stay on the gross
  // maker fee — a separate contract from a per-contract quote (#452 / #467).
  const quoteRebatePct = useOpenOrderQuoteRebatePct();
  // Subscribe to both fee-map authorities. `ready` stays true after the first
  // load, so it cannot invalidate rows when the 60s refresh changes an event
  // from its pregame maker rate to its live rate.
  const feeSeriesRevision = useFeeStore((s) => s.fees);
  const feeEventRevision = useFeeStore((s) => s.eventFees);
  // The position tracker — positions + open orders + recent fills — docks as a
  // content-pushing sticky sidebar on the right (Dan's value-dashboard format,
  // rendered in-page rather than as a floating overlay). Default visible;
  // toggled from the header; shares the right slot with the buy panel.
  // Two-prong rail: the tracker (P&L + positions + orders) and recent fills
  // each dock in the rail or hide, and each COLLAPSES to its header via the
  // header-icon toggle. The tracker merged the old page title/balance readout,
  // so collapsed still shows positions count + balance. Both prongs' pop-outs
  // were dropped on purpose: the full tables render right beside the rail, so
  // a popped-out mini card added nothing (/fills survives as the dashboard
  // overlay's pop-out only). The rail shrinks to just the toggle buttons when
  // neither prong is docked.
  const [trackerHidden, setTrackerHidden] = useState(() => readRailPref(TRACKER_HIDDEN_KEY));
  const [trackerExpanded, setTrackerExpanded] = useState(() =>
    readRailPref(TRACKER_EXPANDED_KEY),
  );
  const [fillsHidden, setFillsHidden] = useState(() => readRailPref(FILLS_HIDDEN_KEY));
  const [fillsExpanded, setFillsExpanded] = useState(() => readRailPref(FILLS_EXPANDED_KEY));

  useEffect(() => {
    writeRailPref(TRACKER_HIDDEN_KEY, trackerHidden);
  }, [trackerHidden]);
  useEffect(() => {
    writeRailPref(TRACKER_EXPANDED_KEY, trackerExpanded);
  }, [trackerExpanded]);
  useEffect(() => {
    writeRailPref(FILLS_HIDDEN_KEY, fillsHidden);
  }, [fillsHidden]);
  useEffect(() => {
    writeRailPref(FILLS_EXPANDED_KEY, fillsExpanded);
  }, [fillsExpanded]);
  // #1124 Data pressure prong: hidden until the trader asks for it. Stable
  // toggles, so the card holds no reference to this page's render scope (#836).
  const [dataShown, setDataShown] = useState(() => readRailPref(DATA_SHOWN_KEY));
  const [dataExpanded, setDataExpanded] = useState(() => readRailPref(DATA_EXPANDED_KEY));
  useEffect(() => {
    writeRailPref(DATA_SHOWN_KEY, dataShown);
  }, [dataShown]);
  useEffect(() => {
    writeRailPref(DATA_EXPANDED_KEY, dataExpanded);
  }, [dataExpanded]);
  const toggleDataShown = useCallback(() => setDataShown((v) => !v), []);
  const toggleDataExpanded = useCallback(() => setDataExpanded((v) => !v), []);

  // Pinned strip state — ordered by pin time (append), held per signed-in
  // user in stores/trackerPins (#943) so Pull All on the dashboard reads the
  // same pins. Collapse folds the strip to its header line so it costs almost
  // nothing on small screens without losing the pins.
  const pinViewerId = useAuthStore((s) => s.user?.id ?? null);
  const pinnedEntries = useTrackerPins((s) => selectOwnedPins(s, pinViewerId));
  const pinnedKeySet = useTrackerPins((s) => selectOwnedPinKeys(s, pinViewerId));
  const [pinsCollapsed, setPinsCollapsed] = useState(readPinsCollapsed);
  useEffect(() => {
    writePinsCollapsed(pinsCollapsed);
  }, [pinsCollapsed]);
  const togglePin = useCallback(
    (entry: PinnedEntry) => useTrackerPins.getState().toggle(entry, pinViewerId),
    [pinViewerId],
  );
  // #943 — the pinned Kalshi orders Cancel All leaves resting.
  const pinnedKalshiIds = usePinnedKalshiOrderIds();
  // Pinned strip item shape (#76): 'card' = the real PositionCard/OrderCard
  // used by Open Positions and Open Orders, 'compact' = the horizontal scroll
  // of compact items this strip used to call 'card', 'table' = vertical stack
  // of single-line rows (orders-table economy). readPinsViewMode owns the
  // legacy-'card' → 'compact' migration; see ./pinsViewMode for why that has
  // to be one-time rather than a standing reinterpretation.
  const [pinsViewMode, setPinsViewMode] = useState<PinsViewMode>(readPinsViewMode);
  useEffect(() => {
    writePinsViewMode(pinsViewMode);
  }, [pinsViewMode]);
  // Static by default (Prof, Jul 24): a tall Card-mode strip should not lock
  // over the orders below unless the trader explicitly opts into the original
  // two-edge sticky behavior.
  const [pinsSticky, setPinsSticky] = useState(readPinsSticky);
  useEffect(() => {
    writePinsSticky(pinsSticky);
  }, [pinsSticky]);
  // Live height of the pinned block, mirrored into the orders toolbar's
  // stick offset, but ONLY while the trader has opted into sticky pins. The
  // block then parks at both scrollport edges, so the orders toolbar must sit
  // below it rather than underneath it. ResizeObserver keeps that offset
  // honest across collapse, view-mode, and row-count changes. CALLBACK ref,
  // not an effect: the block mounts conditionally (pins exist) INSIDE the
  // page's status!=='connected' early-return gate, so the callback is what
  // reliably catches attach/detach.
  const [pinnedBlockH, setPinnedBlockH] = useState(0);
  const pinnedBlockRo = useRef<ResizeObserver | null>(null);
  const pinnedBlockRef = useCallback((el: HTMLElement | null) => {
    pinnedBlockRo.current?.disconnect();
    pinnedBlockRo.current = null;
    if (!el) {
      setPinnedBlockH(0);
      return;
    }
    const measure = () => setPinnedBlockH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    pinnedBlockRo.current = ro;
  }, []);

  // #828 — the sticky chrome the reading-position primitive must scroll a
  // restored row BELOW rather than behind.
  //
  // POSITIONAL, not a stored height. What covers the pane's top edge depends on
  // where the reader is: the positions toolbar owns it in the positions region,
  // the orders toolbar takes over lower down, and the pinned strip — when the
  // trader has opted into sticky pins — is `sticky -bottom-6 -top-6`, so it
  // docks at the BOTTOM until the reader has scrolled past it and only then
  // reaches the top. `measureTopOcclusion` resolves that at the moment it is
  // asked: it counts only contiguous chrome covering the top edge, ignores a
  // bottom-docked strip, and counts overlapping bands once.
  //
  // The hook calls this on capture and restore only, never on a feed render, so
  // handing it live geometry costs nothing per market tick.
  const stickyBands = useRef<{ positions: HTMLElement | null; orders: HTMLElement | null;
    pins: HTMLElement | null }>({ positions: null, orders: null, pins: null });
  const positionsToolbarRef = useCallback((el: HTMLDivElement | null) => {
    stickyBands.current.positions = el;
  }, []);
  const ordersToolbarRef = useCallback((el: HTMLDivElement | null) => {
    stickyBands.current.orders = el;
  }, []);
  // The strip registers as a band ONLY while sticky pins are opted in. Without
  // that option it is ordinary in-flow content, and content sitting at the top
  // edge is precisely what an anchor should be, not chrome to scroll past.
  const pinnedStripRef = useCallback((el: HTMLElement | null) => {
    stickyBands.current.pins = el;
    pinnedBlockRef(el);
  }, [pinnedBlockRef]);
  // Stable identity: the hook takes `occlusion` as an effect dependency, and a
  // new function each render would tear the effect down and re-arm it mid-visit.
  const readingOcclusion = useCallback((root: HTMLElement) => {
    const { positions, orders, pins } = stickyBands.current;
    return measureTopOcclusion(root, [positions, orders, pins]
      .filter((el): el is HTMLElement => el != null));
  }, []);

  const trackerDocked = !trackerHidden;
  const fillsDocked = !fillsHidden;

  const toggleTrackerHidden = () => {
    setTrackerHidden((v) => !v);
  };
  const toggleFillsHidden = () => {
    setFillsHidden((v) => !v);
  };
  // Open Positions sort — the one reading control on this page that had no
  // owner at all (#828). It rides #824's working-view store, NOT a tenth
  // storage key: the nine durable keys and the Open Orders session sort keep
  // their existing contracts untouched. A constant instance because /positions
  // takes no query parameters, so there is nothing in the URL to key on and
  // nothing for a `fromUrl` overlay to outrank.
  const reading = useWorkingView(trackerReading, 'tracker', TRACKER_READING_DEFAULTS);
  const [rawPositionSort, setPositionSort] = useWorkingViewField(reading, 'positionSort');
  // NOT a redundant re-wrap — deleting this costs ~6x the sort work on hover.
  //
  // `useWorkingView` rebuilds its value by PARSING on every render, so this
  // field arrives as a brand-new object each time even when the key and
  // direction have not moved. The `useState` it replaced was referentially
  // stable, and `sortedPositions` below depends on this by identity: without
  // the latch every render that touches anything at all — a row hover setting
  // `hoveredRow`, a velocity tick — re-sorts the whole list and rebuilds every
  // position view-model, which is exactly the derivation cost #731 measured
  // out. `stableVm` still holds the row references steady, so nothing renders
  // wrong; the waste is invisible, which is why it needs a test rather than a
  // comment alone. Measured on the hover path: 12 sorts per 10 renders without
  // this, 2 with it. The Dashboard never hit this because all six of its
  // reading fields are primitives.
  const positionSort = useMemo(
    () => ({ key: rawPositionSort.key, dir: rawPositionSort.dir }),
    [rawPositionSort.key, rawPositionSort.dir],
  );
  const [orderSort, setOrderSort] = useState<SortState<OrderSortKey>>(() => {
    // Session pick first so an involuntary remount (reconnect/auth blip) keeps
    // the trader's live sort; fall back to the persisted landing default. The
    // session store allows queue/velocity (the default store coerces them away),
    // so live keys must seed from session, never from the default.
    const session = readSessionOrderSort();
    if (session) return session;
    const saved = useDisplaySettings.getState().defaultPositionsOrderSort;
    return { key: saved.key, dir: saved.dir };
  });
  // Persist every Open Orders sort change to the per-session store so the next
  // mount (including an involuntary reconnect remount) restores this pick
  // instead of reverting to the factory default.
  useEffect(() => {
    writeSessionOrderSort(orderSort);
  }, [orderSort]);
  // Restore persisted filters once on mount (issue #83). One read seeds all
  // four maps; the write-through effect below re-persists on every change.
  const persistedFilters = useMemo(() => readTrackerFilters(), []);
  // Card-order Lock (#97): a snapshot of the sorted row identities to hold, or
  // null when unlocked. Ephemeral by design — a fresh load starts unlocked, and
  // changing the sort (below) clears it. Per surface (positions / orders).
  const [positionsLockKeys, setPositionsLockKeys] = useState<string[] | null>(null);
  const [ordersLockKeys, setOrdersLockKeys] = useState<string[] | null>(null);
  const [sportFilter, setSportFilter] = useState<SportFilter>(() => persistedFilters.posSport);
  const [posKindFilter, setPosKindFilter] = useState<TrackerFamilyFilter>(() => persistedFilters.posKind);
  // Stable identity for "the visible-positions filter changed" — drives the
  // P&L card re-anchor below. Covers both chip lines.
  const sportFilterKey = useMemo(
    () =>
      [
        ...SPORT_TABS.filter((sport) => sportFilter.has(sport.id)).map(
          (sport) => `${sport.id}:${sportFilter.get(sport.id)}`,
        ),
        ...TRACKER_FAMILY_CHIPS.filter((k) => posKindFilter.has(k.id)).map(
          (k) => `${k.id}:${posKindFilter.get(k.id)}`,
        ),
      ].join('|'),
    [sportFilter, posKindFilter],
  );
  const handleAllSportsFilter = useCallback(() => {
    setSportFilter(new Map());
  }, []);
  // #980: one-time notice per scope after the v1 Moneyline split migrated a
  // saved filter. Any change to that scope's family chips retires it.
  const [kindMigrationNotice, setKindMigrationNotice] = useState(() => persistedFilters.migrationNotice);
  const dismissPosKindNotice = useCallback(() => {
    setKindMigrationNotice((prev) => (prev.positions ? { ...prev, positions: false } : prev));
  }, []);
  const dismissOrderKindNotice = useCallback(() => {
    setKindMigrationNotice((prev) => (prev.orders ? { ...prev, orders: false } : prev));
  }, []);
  const handleAllPosKindsFilter = useCallback(() => {
    setPosKindFilter(new Map());
    dismissPosKindNotice();
  }, [dismissPosKindNotice]);
  const handlePosKindFilterToggle = useCallback((kind: TrackerMarketFamily) => {
    setPosKindFilter((prev) => cycleKindFilter(prev, kind));
    dismissPosKindNotice();
  }, [dismissPosKindNotice]);
  // 3-click cycle per chip: off → include ("only X") → exclude (red X) → off.
  const handleSportFilterToggle = useCallback((sport: Sport) => {
    setSportFilter((prev) => {
      const next = new Map(prev);
      const cur = next.get(sport);
      if (cur === undefined) next.set(sport, 'include');
      else if (cur === 'include') next.set(sport, 'exclude');
      else next.delete(sport);
      return next;
    });
  }, []);
  // Open Orders gets its OWN filter (Dan, Jul 7): the toolbar chips scope to
  // Open Positions so a trader can focus one sport's positions while still
  // watching every resting order — the section-level chips below narrow the
  // orders list independently.
  const [orderSportFilter, setOrderSportFilter] = useState<SportFilter>(() => persistedFilters.ordSport);
  const [orderKindFilter, setOrderKindFilter] = useState<TrackerFamilyFilter>(() => persistedFilters.ordKind);
  // Write-through: persist all four maps whenever any changes. Fires on mount
  // too, which re-normalizes storage (drops entries validation removed on read).
  // Clearing a map to empty (the "All sports"/"All markets" buttons) persists as
  // empty, so a reload doesn't resurrect a cleared filter — Clear stays authoritative.
  useEffect(() => {
    writeTrackerFilters({
      posSport: sportFilter,
      posKind: posKindFilter,
      ordSport: orderSportFilter,
      ordKind: orderKindFilter,
      migrationNotice: kindMigrationNotice,
    });
  }, [sportFilter, posKindFilter, orderSportFilter, orderKindFilter, kindMigrationNotice]);
  const handleAllOrderSportsFilter = useCallback(() => {
    setOrderSportFilter(new Map());
  }, []);
  const handleAllOrderKindsFilter = useCallback(() => {
    setOrderKindFilter(new Map());
    dismissOrderKindNotice();
  }, [dismissOrderKindNotice]);
  const handleOrderKindFilterToggle = useCallback((kind: TrackerMarketFamily) => {
    setOrderKindFilter((prev) => cycleKindFilter(prev, kind));
    dismissOrderKindNotice();
  }, [dismissOrderKindNotice]);
  const handleOrderSportFilterToggle = useCallback((sport: Sport) => {
    setOrderSportFilter((prev) => {
      const next = new Map(prev);
      const cur = next.get(sport);
      if (cur === undefined) next.set(sport, 'include');
      else if (cur === 'include') next.set(sport, 'exclude');
      else next.delete(sport);
      return next;
    });
  }, []);
  const orderFilterKey = useMemo(
    () =>
      [
        ...SPORT_TABS.filter((sport) => orderSportFilter.has(sport.id)).map(
          (sport) => `${sport.id}:${orderSportFilter.get(sport.id)}`,
        ),
        ...TRACKER_FAMILY_CHIPS.filter((kind) => orderKindFilter.has(kind.id)).map(
          (kind) => `${kind.id}:${orderKindFilter.get(kind.id)}`,
        ),
      ].join('|'),
    [orderSportFilter, orderKindFilter],
  );

  const { positionsWithPnl, positionsSync } = usePositionPnl();
  const kalshiSnapshotConfirmed = positionsSync.synced && positionsSync.consecutiveFailures === 0;
  const currentUserId = useAuthStore((st) => st.user?.id ?? null);
  const showRfq = useRfqPositionPrefs((st) => st.showRfq);
  const setShowRfq = useRfqPositionPrefs((st) => st.setShowRfq);
  useEffect(() => {
    useRfqPositionPrefs.getState().loadFor(currentUserId);
  }, [currentUserId]);
  const rfqPositionCount = useMemo(
    () => positionsWithPnl.filter((p) => p.origin === 'rfq').length,
    [positionsWithPnl],
  );

  // Last-trade price (hydra-sourced) for the Open Orders "Last" column.
  const getLastTradeYesCents = useMarketStore((s) => s.getLastTradeYesCents);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);
  // Cross-venue divergence: getGames does the Kalshi<->Polymarket canonical-key
  // pairing; getOrderbookAgeMs gates poly freshness so a stale feed never alerts.
  const getGames = useMarketStore((s) => s.getGames);
  const getOrderbookAgeMs = useMarketStore((s) => s.getOrderbookAgeMs);
  // Hydra orderbook for cumulative queue depth (same-side levels at strictly
  // better prices fill before this order does, so they belong in "queue").
  const getOrderbookDisplay = useMarketStore((s) => s.getOrderbookDisplay);
  const getOrderbookRaw = useMarketStore((s) => s.getOrderbookRaw);
  // Event-start lookup that resolves via canonical game key — sibling
  // markets (Kalshi NO legs, spreads, totals) share the Polymarket-
  // sourced `eventStartTime` so all bets on one game sort together.
  const getEventStartTimeMsByTicker = useMarketStore((s) => s.getEventStartTimeMsByTicker);
  // The page's market clock, throttled to POSITIONS_TICK_MS. Every
  // downstream memo/effect keyed on marketVersion inherits the cadence — the
  // raw ~30 Hz store counter re-ran this entire component per tick and was
  // the diagnosed main-thread saturator behind the hydra 8 MiB kill loop.
  const marketVersion = useThrottledMarketVersion();

  // Queue position lives on /api/kalshi/me/snapshot (the relay's per-user
  // shape) — it isn't carried on accountStore.openOrders. Pull it via the
  // existing snapshot hook and key by orderId.
  const orderTickers = useMemo(
    () => Array.from(new Set(openOrders.map((o) => o.ticker))),
    [openOrders],
  );
  const {
    ordersByTicker,
    displayOrdersByTicker,
    queueStatus,
    snapshotSeq,
  } = useUserKalshiPOS(orderTickers);
  const humanOrdersByTicker = displayOrdersByTicker ?? ordersByTicker;
  const strictOrderSliceById = useMemo(() => {
    const byId = new Map<string, UserOrderSlice>();
    for (const slices of ordersByTicker.values()) {
      for (const slice of slices) byId.set(slice.orderId, slice);
    }
    return byId;
  }, [ordersByTicker]);
  // Route-entry and 4 Hz queue work both need per-order lookup. Scanning a
  // ticker's full slice for every open order makes a large same-market ladder
  // quadratic; index the already-filtered snapshot once instead.
  const humanOrderSliceById = useMemo(() => {
    const byId = new Map<string, UserOrderSlice>();
    for (const slices of humanOrdersByTicker.values()) {
      for (const slice of slices) byId.set(slice.orderId, slice);
    }
    return byId;
  }, [humanOrdersByTicker]);
  // Queue position has two deliberately separate contracts:
  //   - human display/sort: last confirmed value, with an explicit vintage;
  //   - alerts/velocity: fresh relay evidence only.
  // This keeps a healthy five-second refresh readable without letting an aged
  // value ring a bell or masquerade as a new queue movement.
  const queueSafetyFresh = queueStatus == null || queueStatus.fresh || queueStatus.perOrder === true;
  const queueHardFailure = !queueStatus?.perOrder && queueSnapshotIsHardFailure(queueStatus);
  const queueSampleToken = queueSourceSampleToken(queueStatus, snapshotSeq);
  const currentQueueSourceKind = queueSourceKind(queueStatus);

  // Polymarket org-key rows for the unified Open Orders / Open Positions
  // tables below. Both hooks auto-poll and silently return empty arrays
  // for traders whose org doesn't have a poly credential, so they're
  // safe to mount unconditionally — no extra availability gate needed.
  const polyOrdersState = usePolymarketRelayOrders();
  const polyPositionsState = usePolymarketRelayPositions();
  // A readable Kalshi snapshot cannot certify retained or unreadable Poly
  // holdings. Even credential absence does not prove its historical ledger flat.
  const polySnapshotConfirmed = polyPositionsState.status === 'ready' && polyPositionsState.snapshotConfirmed;
  // #904 — per-venue readiness. The page admits each venue's read surface on
  // its own; a venue that is not configured for this account is excluded
  // from combined figures and named on the tile, while a configured venue
  // whose read is failing, reconnecting or pending still withholds them.
  // Not configured and unreadable are different states on purpose.
  const accountError = useAccountStore((s) => s.error);
  const credentialCount = useAccountStore((s) => s.credentialCount);
  const credentialsStatus = useAccountStore((s) => s.credentialsStatus);
  const connectWanted = useAccountStore((s) => s.connectWanted);
  const { failure: kalshiConnectionFailure } = useKalshiAccountPhase();
  const kalshiVenue = useMemo(
    () => kalshiReadiness({
      status, error: accountError, credentialCount, credentialsStatus, positionsSync,
      connectionFailure: kalshiConnectionFailure, connectWanted,
      positionCount: positionsWithPnl.length, openOrderCount: openOrders.length,
    }),
    [status, accountError, credentialCount, credentialsStatus, positionsSync, kalshiConnectionFailure, connectWanted,
      positionsWithPnl.length, openOrders.length],
  );
  // #1060: a failed key check is retried by the trader, once per press (the
  // order ticket's Retry, #1041); never on a timer.
  const retryKalshiKeys = status === 'disconnected' && credentialsStatus === 'error'
    ? () => { void useAccountStore.getState().checkCredentials(); }
    : undefined;
  const polyVenue = useMemo(
    () => polyReadiness({
      status: polyPositionsState.status, snapshotConfirmed: polyPositionsState.snapshotConfirmed,
      unavailableCode: polyPositionsState.unavailableCode, error: polyPositionsState.error,
      positionCount: polyPositionsState.positions.length,
    }),
    [polyPositionsState.status, polyPositionsState.snapshotConfirmed, polyPositionsState.unavailableCode,
      polyPositionsState.error, polyPositionsState.positions.length],
  );
  // #1006 — Polymarket US is a third venue for admission only: a readable US
  // account keeps the page up when both venues above are unavailable. Its
  // figures stay out of the K/P tiles (see UsTrackerSection).
  const { readiness: usVenue, sessionKey: usSessionKey, retry: retryUsVenue, retrying: retryingUsVenue } = useUsTrackerReadiness();
  // #1060: the page/placeholder choice of the last render, kept while a venue
  // that could change it is still loading (see trackerAdmission). It belongs
  // to one sign-in: another login or org decides afresh.
  const [admittedBefore, setAdmittedBefore] = useState<{ key: string; admitted: boolean } | null>(null);
  const venueAdmission = trackerAdmission(kalshiVenue, polyVenue, usVenue,
    admittedBefore?.key === usSessionKey ? admittedBefore.admitted : null);
  if (admittedBefore?.key !== usSessionKey || admittedBefore.admitted !== venueAdmission.admitted) {
    setAdmittedBefore({ key: usSessionKey, admitted: venueAdmission.admitted });
  }
  // #1006 — US orders in Cancel all: read and frozen when the confirmation
  // opens, sent per order after the Kalshi/Polymarket sweep.
  const usBulk = useUsBulkCancel(usSessionKey, usVenue);
  // #1226 — the pooled US read: every own-view account's working orders and
  // held positions, which become rows of the shared lists below. Tagged with
  // the session, so a late report from a previous login never paints.
  const [usReadState, setUsReadState] = useState<{ key: string; read: UsTrackerRead | null }>({ key: usSessionKey, read: null });
  const onUsRead = useCallback((read: UsTrackerRead | null) => setUsReadState({ key: usSessionKey, read }), [usSessionKey]);
  const usRead = usReadState.key === usSessionKey ? usReadState.read : null;
  // The US catalogue behind the rows' chips, links, sort and Close (one
  // classifier and one serving reader per session).
  const usMarkets = useUsTrackerMarkets(usSessionKey);
  useUsMarketRequests(usRead, usMarkets);
  // Hold the complete inventory union independently of sport/view/pin filters.
  // Stream marks are display evidence; Close keeps its account-bound catalogue
  // readiness below, so a live price cannot grant execution authority.
  const usHeldSlugs = useMemo(() => (usRead?.accounts ?? []).flatMap(account =>
    account.context && account.snapshot ? account.openPositions.map(position => position.target.marketSlug) : []), [usRead]);
  const usPositionMarks = useUsPositionMarks('position-tracker-us-positions', usHeldSlugs, usMarkets.serving, usMarkets.servingVersion);
  const usViews = useMemo(() => new Map((usRead?.accounts ?? []).map((a) => [a.accountId, a] as const)), [usRead]);
  const usRealized = useMemo(() => usRealizedOf(usRead), [usRead]);
  // Some US account has answered: its rows (or their absence) are a reading.
  // So is a list with no own-view account on it (every visible account is
  // desk-only; its rows are the desk's, on Org Detail): nothing is read here,
  // and nothing is "reading".
  const usAnswered = !!usRead && (usRead.accounts.some((a) => a.snapshot !== null)
    || (usRead.list.state === 'listed' && usRead.ownAccounts === 0));
  const usReading = usVenue.readable && !usAnswered && usRead?.list.state !== 'failed'
    && !(usRead?.list.state === 'listed' && usRead.accounts.length > 0 && usRead.accounts.every((a) => a.openError !== null || a.readError !== null));
  // #1238 5a — a US read that failed with nothing to show is "unavailable"
  // (the K/P word), never "No open …": the page's own account-list read
  // (usReadinessUnavailable; the notice carries its Retry) or the feed's
  // reads (usListsUnavailable), held through the feed's 10 s re-read.
  const [usUnavailableLatch, setUsUnavailableLatch] = useState<{ key: string; code: string | null }>({ key: usSessionKey, code: null });
  const usUnavailable = usReadinessUnavailable(usVenue)
    ?? (usVenue.readable ? usListsUnavailable(usRead, usUnavailableLatch.key === usSessionKey ? usUnavailableLatch.code : null) : null);
  const usLatchNext = usVenue.readable ? usUnavailable?.code ?? null : null;
  if (usUnavailableLatch.key !== usSessionKey || usUnavailableLatch.code !== usLatchNext) {
    setUsUnavailableLatch({ key: usSessionKey, code: usLatchNext });
  }
  const usPinTransfer = useCallback((previousKey: string, replacementKey: string) =>
    useTrackerPins.getState().transfer(previousKey, replacementKey, pinViewerId), [pinViewerId]);
  // #1034 — one US read for the page: the docked rail shows it, and Cancel
  // all names how many of your US orders it would send. Before that read has
  // answered nothing is claimed for US ("Cancel all (0 + US)" on first load,
  // Brendan's #1029 follow-up); the dialog's own read stays the authority.
  // An account no read has seen whole is unknown, never 0: the button says
  // "+ US" without a number and the dialog's own read decides (it names what
  // it could not read). An account a later read could not see (or a read that
  // failed outright) keeps its last count, marked stale in the hover, rather
  // than flipping the label to "+ US" and back on every failed poll (see
  // usCancelAllHint). Remembered counts belong to one sign-in and are dropped
  // after a sweep that touched US orders.
  const usRail = useUsRail(true);
  const usCountKey = `${usSessionKey}#${usBulk.refreshSignal}`;
  const [usCountMemory, setUsCountMemory] = useState<UsCancelCountMemory>(
    () => ({ key: usCountKey, counts: new Map(), ignore: null }));
  const nextUsCountMemory = rememberUsCancelCounts(usCountMemory, usCountKey, usRail.read);
  if (nextUsCountMemory !== usCountMemory) setUsCountMemory(nextUsCountMemory);
  const usCancel = usCancelAllHint({ venueReadable: usVenue.readable, hasUs: usRail.hasUs, status: usRail.status,
    error: usRail.error, read: usRail.read, memory: nextUsCountMemory.counts,
    ignore: nextUsCountMemory.ignore });
  const usCancelHint = usCancel.kind !== 'none';
  const usCancelSource = usCancel.kind === 'count' ? usCancelCountSource(usCancel) : '';
  const { refresh: refreshUsRail } = usRail;
  useEffect(() => {
    // After a Cancel all touched US orders, re-read so the count follows.
    if (usBulk.refreshSignal > 0) refreshUsRail();
  }, [usBulk.refreshSignal, refreshUsRail]);
  useEffect(() => {
    // The dialog's own read is the fresher answer: re-read so the button
    // agrees with it (this replaces #1006's one-minute "+ US" suppression).
    if (usBulk.prepared.phase === 'ready') refreshUsRail();
  }, [usBulk.prepared, refreshUsRail]);
  // Kalshi without any credential has no ledger this page can read, so the
  // combined figures cover Polymarket alone and say so. Polymarket's persisted
  // ledger is readable without trading credentials, so its `unavailable` is a
  // failed read and keeps withholding (docs/260915TrackerVenueReadiness.md).
  const kalshiExcluded = kalshiVenue.state === 'not-configured';
  // #1060: the realized-by-venue tile is an org-key tile. Until the key check
  // says which mode this login is in, an org member keeps its place (most
  // org members trade on org keys), so the tiles never re-flow from three
  // columns to four under the pointer; a personal-key login loses it once.
  const trackerOrgId = useAuthStore((s) => s.user?.orgId ?? null);
  const realizedTileShown = keyMode === 'org' || (keyMode == null && credentialsStatus !== 'checked' && trackerOrgId != null);
  // Its Kalshi figure is a claim only once Kalshi is readable: before that
  // the store's realized total is its initial 0, not a reading.
  const kalshiRealizedKnown = !kalshiExcluded && kalshiVenue.readable;
  const coverageLabel = venueAdmission.coverage === 'none' ? 'K+P' : venueAdmission.coverage;
  // Polymarket US counts in these tiles like any venue (Oct 16): its working
  // orders and held positions are rows of the shared lists, so the lists'
  // lengths already include them (#1226). Value and unrealized P&L use exact
  // current marks and recorded cost/fee coverage (#1199). A
  // US-only page (neither Kalshi nor Polymarket readable) counts US alone;
  // #1060: only once both have answered.
  const usCounted = usVenue.readable;
  const usOnlyTiles = usCounted && venueAdmission.coverage === 'none'
    && kalshiVenue.state !== 'loading' && polyVenue.state !== 'loading';
  const tileCoverage = usOnlyTiles ? 'US' : usCounted ? `${coverageLabel}+US` : coverageLabel;
  // #1060: what the counts and empty states may claim. A venue whose read has
  // not answered has told us nothing: no count from it is a zero, and "No
  // open orders/positions" waits for every venue that could hold one. Kalshi
  // orders are known from the orders read itself (`ordersSync`), not from the
  // positions read that decides Kalshi's readiness.
  const kalshiOrdersKnown = kalshiExcluded || ordersSync.lastSuccessMs !== null;
  const kalshiOrdersFailed = !kalshiOrdersKnown
    && (ordersSync.consecutiveFailures > 0 || (kalshiVenue.state !== 'loading' && !kalshiVenue.readable));
  const kalshiOrdersPending = !kalshiOrdersKnown && !kalshiOrdersFailed;
  const polyOrdersPending = polyOrdersState.status === 'idle' || polyOrdersState.status === 'loading';
  const ordersPending = kalshiOrdersPending || polyOrdersPending;
  // These cards hold Kalshi and Polymarket rows; the US section's rows sit
  // below them. When US shows rows the empty card is not drawn (the page is
  // not empty); otherwise one plain empty state, as for any venue. The cards
  // stay for the K/P loading and failure lines.
  // K/P words win; US speaks only when K/P have nothing to say. The US Retry
  // shows exactly when the card shows the US "unavailable" line (#1238).
  const kpOrdersEmptyText = kalshiOrdersPending ? 'Reading Kalshi orders…'
    : polyOrdersPending ? 'Reading Polymarket orders…'
      : kalshiOrdersFailed ? 'Kalshi orders unavailable'
        : polyOrdersState.status === 'error' ? 'Polymarket orders unavailable'
          : null;
  const ordersUsUnavailable = kpOrdersEmptyText === null ? usUnavailable : null;
  const ordersEmptyText = kpOrdersEmptyText
    ?? (ordersUsUnavailable ? 'Polymarket US orders unavailable'
      : usReading ? 'Reading Polymarket US orders…'
        : 'No open orders');
  const kpPositionsEmptyText = !(kalshiSnapshotConfirmed || kalshiExcluded)
    ? 'Positions unavailable until the Kalshi snapshot is confirmed'
    : polyVenue.state === 'loading' ? 'Reading Polymarket positions…'
      : !polyVenue.readable && polyVenue.state !== 'not-configured' ? 'Polymarket positions unavailable'
        : null;
  const positionsUsUnavailable = kpPositionsEmptyText === null ? usUnavailable : null;
  const positionsEmptyText = kpPositionsEmptyText
    ?? (positionsUsUnavailable ? 'Polymarket US holdings unavailable'
      : usReading ? 'Reading Polymarket US holdings…'
        : 'No open positions');
  // Neither Kalshi nor Polymarket readable yet (or at all): no figure, not 0.
  const kpCountUnknown = venueAdmission.coverage === 'none';
  // Order figures (the Open Orders tile and the summary): "—" while the order
  // read of a venue the tiles cover ("(K+P)", "(K)", "(P)") has not answered
  // or failed. A venue outside the coverage never blanks the others' count.
  const orderFiguresUnknown = kpCountUnknown
    || (kalshiVenue.readable && !kalshiOrdersKnown)
    || (polyVenue.readable && (polyOrdersPending || polyOrdersState.status === 'error'));
  const tileVenuesTitle = `${usOnlyTiles ? 'Polymarket US only: neither Kalshi nor Polymarket is readable' : coverageLabel === 'K+P' ? 'Kalshi and Polymarket' : coverageLabel === 'K' ? 'Kalshi only' : 'Polymarket only'}${
    usCounted && !usOnlyTiles ? ', plus Polymarket US' : ''}`;
  const usPositionsConfirmed = !usCounted || usPositionsComplete(usRead);
  const positionsSnapshotConfirmed = (kalshiSnapshotConfirmed || kalshiExcluded) && polySnapshotConfirmed && usPositionsConfirmed;
  const positionsSnapshotWarning = !kalshiSnapshotConfirmed && !kalshiExcluded
    ? 'Unrealized P&L withheld until the Kalshi positions snapshot is confirmed'
    : !polySnapshotConfirmed ? 'Unrealized P&L withheld until the Polymarket positions snapshot is confirmed'
      : 'Unrealized P&L withheld until the Polymarket US holdings read is complete';
  const refreshPolyOrders = polyOrdersState.refresh;
  const refreshPolyPositions = polyPositionsState.refresh;
  const polyRealizedPnl = polyPositionsState.pnl;
  const polyRealizedFeesCents = polyRealizedPnl?.realizedFeesCents ?? null;
  const polyRealizedPnlNetCents = polyRealizedPnl?.realizedPnlNetCents ?? null;
  const displayPolyRealizedPnlCents =
    polyRealizedPnl == null
      ? null
      : (polyRealizedPnlNetCents ?? polyRealizedPnl.realizedPnlCents);
  // Only show currently-held positions in the Open Positions table —
  // netCount=0 rows are closed-out history and surface in MyTrades.
  // closedPolyPositionFences additionally suppresses rows whose close was
  // VERIFIED flat this session: the fills ledger lags the CLOB by a poller
  // cycle, and a stale row with a live Close button is a double-sell
  // invitation. Each fence carries the close-time lifetime buy count so a
  // continuously-positive close-and-re-buy is provable (#429). Release is
  // gated on an authoritative ('ready') snapshot; the filter deliberately is
  // not — holding a fence through an outage is the safe direction.
  const [closedPolyPositionFences, setClosedPolyPositionFences] =
    useState<ClosedPolyPositionFences>(() => new Map());
  const polyOpenPositions = useMemo(
    () =>
      polyPositionsState.positions.filter(
        (p) =>
          p.netCount > 0 &&
          !isClosedPolyPositionSuppressed(closedPolyPositionFences, p),
      ),
    [polyPositionsState.positions, closedPolyPositionFences],
  );
  const polyFeeConditionIds = useMemo(
    () => polyOpenPositions.map((position) => position.ticker),
    [polyOpenPositions],
  );
  const { schedules: polyFeeSchedules } = usePolyFeeSchedules(polyFeeConditionIds);
  useEffect(() => {
    const next = reconcileClosedPolyPositionFences(
      closedPolyPositionFences,
      polyPositionsState.positions,
      polyPositionsState.status === 'ready',
    );
    if (next !== closedPolyPositionFences) {
      setClosedPolyPositionFences(next);
    }
  }, [polyPositionsState.positions, polyPositionsState.status, closedPolyPositionFences]);

  // Tell hydra to stream orderbook frames for the user's open-order tickers —
  // the cumulative queue calc needs the live book, and PositionTracker may
  // be opened standalone (without ValueDashboard already subscribing). Use a
  // stable key so the effect only fires when the ticker set actually changes.
  const orderTickersKey = useMemo(() => orderTickers.slice().sort().join('|'), [orderTickers]);
  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('position-tracker', orderTickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('position-tracker', []);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderTickersKey]);

  const handlePositionSort = useCallback((key: string) => {
    // #97: choosing a sort unlocks — the trader asked to resort, so honour it
    // (visible: the Lock toggle flips back to unlocked).
    setPositionsLockKeys(null);
    const nextKey: PositionSortKey = key === 'size' ? 'size' : key === 'velocity' ? 'velocity' : 'default';
    setPositionSort((prev) => {
      if (nextKey === 'default') return { key: 'default', dir: 'asc' };
      return {
        key: nextKey,
        dir: prev.key === nextKey && prev.dir === 'desc' ? 'asc' : 'desc',
      };
    });
    // `setPositionSort` MUST be a dependency, not a capture. A `useState` setter
    // is stable so omitting it was harmless before; a working-view setter closes
    // over one history entry and is silently REFUSED once superseded, which
    // would leave the sort buttons inert after any navigation (#826 hit exactly
    // this on the Dashboard's market-type chip).
  }, [setPositionSort]);

  const handleOrderSort = useCallback((key: string) => {
    setOrdersLockKeys(null); // #97: resorting unlocks (see handlePositionSort)
    const nextKey: OrderSortKey =
      key === 'velocity'
        ? 'velocity'
        : key === 'queue'
          ? 'queue'
          : key === 'size'
            ? 'size'
            : 'default';
    setOrderSort((prev) => {
      if (nextKey === 'default') return { key: 'default', dir: 'asc' };
      if (nextKey === 'size') {
        return {
          key: nextKey,
          dir: prev.key === nextKey && prev.dir === 'desc' ? 'asc' : 'desc',
        };
      }
      if (nextKey === 'velocity') {
        return {
          key: nextKey,
          dir: prev.key === nextKey && prev.dir === 'desc' ? 'asc' : 'desc',
        };
      }
      return {
        key: nextKey,
        dir: prev.key === nextKey && prev.dir === 'asc' ? 'desc' : 'asc',
      };
    });
  }, []);

  // Unified sort key: (eventStartTime asc, ticker asc). Same start time +
  // alpha ticker keeps all bets on one game adjacent — e.g. three moneyline
  // legs and a totals on the same Yankees game land in one block instead of
  // being scattered. Missing start times sentinel to +Infinity so they fall
  // to the end. The canonical-key resolver propagates event start to
  // sibling markets (NO legs, spreads, totals), while direct market
  // `eventStartTime` covers new Kalshi sports without a canonical key.
  // Poly relay tickers (CLOB asset ids) usually won't resolve, so they
  // sort to the tail. Poly is NOT moneyline-only — hydra serves poly spread
  // and total frames (#428), so don't reintroduce a moneyline-only shortcut
  // here; poly positions join the event-start sort via polySortStartMs.
  const sortStartMs = useCallback(
    (ticker: string): number => {
      void marketVersion;
      const ms = getEventStartTimeMsByTicker(ticker);
      return ms != null ? ms : Number.POSITIVE_INFINITY;
    },
    [getEventStartTimeMsByTicker, marketVersion],
  );

  type SortedPosRow =
    | {
        kind: 'kalshi';
        sport: Sport | null;
        sortStart: number;
        sortTicker: string;
        pos: PositionWithPnl;
        /** #949 — contracts on resting orders that close this position. */
        restingCloseQty: number;
        /** #980 — listed only because its market family is still pending
         *  under an active family chip (false under All markets). */
        familyPending: boolean;
      }
    | {
        kind: 'poly';
        sport: Sport | null;
        sortStart: number;
        sortTicker: string;
        pos: PolyRelayPosition;
        familyPending: boolean;
      }
    /** #1226 — a Polymarket US holding, one row of the same list. */
    | UsSortedPosRow;

  const baseSortedPositions = useMemo<SortedPosRow[]>(() => {
    // #428/#980: the family classifiers read the market store imperatively, so
    // this memo must re-derive on market ticks. Do not rely on sortStartMs's marketVersion
    // dep to drag it along — stabilizing that callback would silently freeze
    // every poly row's kind at its first-render null and stop the chips
    // filtering them at all.
    void marketVersion;
    const rows: SortedPosRow[] = [];
    for (const pos of positionsWithPnl) {
      // V58: the trader can hide their RFQ bot's positions (mixed ones stay).
      if (!showRfq && pos.origin === 'rfq') continue;
      const { sport, visible, familyPending } = kalshiRowFilterState(pos.ticker, sportFilter, posKindFilter);
      if (!visible) continue;
      const closes = workingCloses(pos, closeCountOrders, kalshiCancelStates);
      rows.push({
        kind: 'kalshi',
        sport,
        sortStart: sortStartMs(pos.ticker),
        sortTicker: pos.ticker,
        pos,
        restingCloseQty: closes.qty,
        familyPending,
      });
    }
    for (const pos of polyOpenPositions) {
      // pos.ticker is the market conditionId — hex, so sportFromTicker is
      // near-always null. Resolve through the market-level lookup so the
      // rows filter CORRECTLY when the market is known; rows whose sport
      // still can't be resolved must pass an include-mode filter rather
      // than vanish (real-money rows — same rule as the poly order loop).
      const marketDesc = describePolyMarketCached(pos.ticker);
      const sport = marketDesc?.sport ?? sportFromTicker(pos.ticker);
      if (!rowSportMatchesFilter(sport, sportFilter)) continue;
      // #428: family resolves from the live frame, never from the conditionId
      // string; a pending family is ADMITTED for the same reason an
      // unresolved sport is.
      const family = polyPositionTrackerFamily(pos.ticker);
      if (!familyMatchesFilter(family, posKindFilter)) continue;
      rows.push({
        kind: 'poly',
        sport,
        familyPending: family == null && posKindFilter.size > 0,
        // conditionId never resolves in the start cache; the cached
        // description's slug PM- ticker does (to the same start the game's
        // Kalshi rows resolve to), so poly positions join the event-start
        // Default sort instead of tailing. No network — pmTicker is cached.
        sortStart: polySortStartMs(marketDesc?.pmTicker, sortStartMs),
        sortTicker: pos.ticker,
        pos,
      });
    }
    // #1226 — every own-view US account's holdings, under the same chips
    // (their sport and family from the US catalogue; unclassified rows pass).
    for (const view of usViews.values()) {
      rows.push(...usHoldingRowsOf(view, usMarkets, sportFilter, posKindFilter));
    }
    rows.sort(compareDefaultSort);
    return rows;
  }, [
    positionsWithPnl,
    polyOpenPositions,
    closeCountOrders,
    kalshiCancelStates,
    sortStartMs,
    sportFilter,
    posKindFilter,
    marketVersion,
    showRfq,
    usViews,
    usMarkets,
  ]);

  // Position "velocity" = how fast the position's MARKET price is moving (¢/min).
  // Positions have no intrinsic/queue velocity, so this samples getMarketPrice on
  // a throttled tick (same rationale as the order-sort stabilize: don't re-order
  // the list on every market tick) and reports |Δprice| over a rolling window.
  // Kalshi-only — getMarketPrice can't resolve poly condition ids, so poly
  // positions get no velocity and sort last (matches the order-sort's kalshi
  // scope). It's net displacement over the window, so a swing out-and-back within
  // it reads ~0 — intentional.
  const positionVelSamplesRef = useRef<Map<string, { ms: number; price: number }[]>>(new Map());
  const [{ tick: posVelTick, nowMs: positionClockNow }, setPositionClock] = useState(() => ({ tick: 0, nowMs: Date.now() }));
  useEffect(() => {
    const id = window.setInterval(() => setPositionClock(clock => ({ tick: clock.tick + 1, nowMs: Date.now() })), ORDER_SORT_STABILIZE_MS);
    return () => window.clearInterval(id);
  }, []);
  // Reuse the page's existing clock to expire display marks even if the feed
  // goes silent and an account refresh is pending. No new poll or timer.
  const usMarkNow = usHeldSlugs.length ? positionClockNow : 0;
  const stablePositionVelocity = useMemo(() => {
    const samples = positionVelSamplesRef.current;
    const now = Date.now();
    const WINDOW_MS = 60_000;
    const out = new Map<string, number>();
    const live = new Set<string>();
    for (const row of baseSortedPositions) {
      if (row.kind !== 'kalshi') continue; // poly: no resolvable market price → sorts last
      const ticker = row.sortTicker;
      live.add(ticker);
      const price = getMarketPrice(ticker);
      if (price == null) continue;
      const arr = samples.get(ticker) ?? [];
      arr.push({ ms: now, price });
      while (arr.length > 1 && now - arr[0].ms > WINDOW_MS) arr.shift();
      samples.set(ticker, arr);
      if (arr.length >= 2) {
        const dtMin = (arr[arr.length - 1].ms - arr[0].ms) / 60_000;
        if (dtMin > 0) out.set(ticker, Math.abs(arr[arr.length - 1].price - arr[0].price) / dtMin);
      }
    }
    for (const t of [...samples.keys()]) if (!live.has(t)) samples.delete(t);
    return out;
    // Throttled on purpose: sample + recompute only on the tick, not every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posVelTick]);

  // Stable identity per position row for card-order Lock (#97) — mirrors the
  // card VM key (positionCardModel): kalshi "<ticker>-<side>", poly
  // "poly-pos-<conditionId>-<side>".
  const posRowKey = useCallback(
    (row: SortedPosRow): string =>
      row.kind === 'poly'
        ? `poly-pos-${row.pos.ticker}-${row.pos.side}`
        : row.kind === 'us'
          ? usHoldingPinKey(row.accountId, row.pos.target.marketSlug, row.pos.target.outcome)
          : `${row.pos.ticker}-${row.pos.side}`,
    [],
  );

  const sortedPositions = useMemo<SortedPosRow[]>(() => {
    if (positionSort.key === 'default') return baseSortedPositions;
    const dir = positionSort.dir === 'asc' ? 1 : -1;
    const sizeOf = (row: SortedPosRow): number =>
      row.kind === 'poly' ? row.pos.netCount : row.kind === 'us' ? NaN : Math.abs(row.pos.position);
    // #1226 — a US holding's size is exact decimal text (a short holding's
    // negative): when either row is US both sizes compare as exact units
    // (BigInt); K/P pairs keep the numeric comparison. An unknown size keeps
    // the tail in both directions.
    const unitsOf = (row: SortedPosRow): bigint | null =>
      row.kind === 'us' ? (row.pos.heldQuantity === null ? null : signedDecimalUnits(row.pos.heldQuantity))
        : decimalUnits(sizeOf(row).toFixed(6));
    const compareSize = (a: SortedPosRow, b: SortedPosRow): number => {
      if (a.kind !== 'us' && b.kind !== 'us') {
        const sizeDelta = sizeOf(a) - sizeOf(b);
        if (sizeDelta !== 0) return sizeDelta * dir;
        return compareDefaultSort(a, b);
      }
      return compareExactSizes(unitsOf(a), unitsOf(b), dir, () => compareDefaultSort(a, b));
    };
    const velocityOf = (row: SortedPosRow): number | null =>
      stablePositionVelocity.get(row.sortTicker) ?? null;
    const rows = [...baseSortedPositions];
    rows.sort((a, b) => {
      if (positionSort.key === 'velocity') {
        const va = velocityOf(a);
        const vb = velocityOf(b);
        if (va == null && vb == null) return compareDefaultSort(a, b);
        if (va == null) return 1;
        if (vb == null) return -1;
        const d = va - vb;
        if (d !== 0) return d * dir;
        return compareDefaultSort(a, b);
      }
      return compareSize(a, b);
    });
    return rows;
  }, [baseSortedPositions, positionSort, stablePositionVelocity]);

  // #97 Lock: when locked, hold the captured order while card contents keep
  // updating; unlocked returns the live sort verbatim. Consumed by the render
  // paths (card VMs + table) below.
  const displayedPositions = useMemo(
    () => applyLockedOrder(sortedPositions, positionsLockKeys, posRowKey),
    [sortedPositions, positionsLockKeys, posRowKey],
  );
  // #102 review: Lock is card-only. The card/compact/mini views consume the
  // locked order (via displayedPositions → positionCardVMs); the TABLE always
  // renders the live sort, since its Lock control is hidden and a frozen table
  // would leave the trader no visible way to unlock.
  const positionTableRows = useMemo(
    () => orderingForView(positionViewMode === 'table', displayedPositions, sortedPositions),
    [positionViewMode, displayedPositions, sortedPositions],
  );

  const baseSortedOrders = useMemo<SortedOrderRow[]>(() => {
    // #980: the family classifiers read the store imperatively, so a family
    // that arrives late must re-filter (same reason as positions).
    void marketVersion;
    const rows: SortedOrderRow[] = [];
    for (const order of openOrders) {
      const { sport, visible, familyPending } = kalshiRowFilterState(
        order.ticker,
        orderSportFilter,
        orderKindFilter,
      );
      if (!visible) continue;
      rows.push({
        kind: 'kalshi',
        sport,
        sortStart: sortStartMs(order.ticker),
        sortTicker: order.ticker,
        order,
        familyPending,
      });
    }
    for (const order of polyOrdersState.orders) {
      const assetId = String(order.asset_id ?? order.market ?? order.id ?? order.orderID ?? order.orderId ?? '');
      const orderDesc = describePolyOrderCached(assetId);
      const marketDesc = orderDesc ? undefined : describePolyMarketCached(assetId);
      const sport = orderDesc?.sport ?? marketDesc?.sport ?? sportFromTicker(assetId);
      if (!rowSportMatchesFilter(sport, orderSportFilter)) continue;
      // #980: classify from the live frame via the order's token, falling
      // back to its market conditionId. Pending is admitted, as for positions.
      const family =
        polyOrderTrackerFamily(assetId) ??
        (order.market ? polyPositionTrackerFamily(String(order.market)) : null);
      if (!familyMatchesFilter(family, orderKindFilter)) continue;
      rows.push({
        kind: 'poly',
        sport,
        familyPending: family == null && orderKindFilter.size > 0,
        // The CLOB asset id never resolves in the start cache; the cached
        // description's slug PM- ticker does (to the same start the game's
        // Kalshi rows resolve to), so poly orders join the event-start
        // Default sort instead of tailing. No network — pmTicker is cached.
        sortStart: polySortStartMs(orderDesc?.pmTicker ?? marketDesc?.pmTicker, sortStartMs),
        sortTicker: assetId,
        order,
      });
    }
    // #1226 — every own-view US account's working orders, under the same chips.
    for (const view of usViews.values()) {
      rows.push(...usOrderRowsOf(view, usMarkets, orderSportFilter, orderKindFilter));
    }
    // Id-stable variant: same-token poly ladder orders tie completely in
    // compareDefaultSort and would otherwise follow raw CLOB response order,
    // reshuffling every poll.
    rows.sort(compareOrderRowsDefault);
    return rows;
  }, [openOrders, polyOrdersState.orders, sortStartMs, orderSportFilter, orderKindFilter, marketVersion, usViews, usMarkets]);
  // #980 — listed rows admitted only because their family is still pending.
  const unclassifiedPositionCount = useMemo(
    () => baseSortedPositions.filter((r) => r.familyPending).length,
    [baseSortedPositions],
  );
  const unclassifiedOrderCount = useMemo(
    () => baseSortedOrders.filter((r) => r.familyPending).length,
    [baseSortedOrders],
  );

  const recentFilledQtyByOrderId = useMemo(() => {
    const map = new Map<string, number>();
    for (const fill of recentFills) {
      if (!fill.orderId) continue;
      map.set(fill.orderId, (map.get(fill.orderId) ?? 0) + Math.max(0, Math.round(fill.count)));
    }
    return map;
  }, [recentFills]);

  // Per-order queue metrics (Professor's spec):
  //   queue        = atLevel + better. Your absolute spot in the global
  //                  FIFO — every contract that fills before yours.
  //   queueAbove   = better only. Contracts resting at strictly better
  //                  prices; these clear before your level is touched.
  //                  0 = top of book (you're at the best price).
  //   queueBehind  = atLevelDepth − atLevel − own remaining size.
  //                  Contracts at your exact price level resting behind you.
  //   total        = both sides combined (book depth), matching
  //                  marketStore's `bookDepth` concept. The percentile
  //                  denominator for tone helpers; we use both sides so
  //                  the color reads against total market liquidity, not
  //                  one side that may be hollow.
  // When the book hasn't loaded, queue degrades to at-level (a lower
  // bound); above/behind stay null so the cells render "—" instead of
  // misleading zeros.
  // Floor-selection-churn (2026-10-22): these metrics read only the open
  // orders' books, so they depend on those books' identities (plus the
  // display snapshot's retention state), sampled at the 4 Hz clock — not on
  // the clock itself. A market-frame tick (an hc1 floor replay streams
  // ~1,200 rows/s for 7–45 s) no longer rebuilds them. See queueBookStamp.
  const queueBookStampKey = useMemo(() => {
    void marketVersion;
    return queueBookStamp(openOrders, getOrderbookRaw, getOrderbookDisplay);
  }, [openOrders, getOrderbookRaw, getOrderbookDisplay, marketVersion]);
  const { current: queueMetricsByOrderId, display: displayQueueMetricsByOrderId,
    recovering: recoveringBookOrderIds, sources: bookSourcesByOrderId } = useMemo(() => {
    void queueBookStampKey;
    // Group open orders by same-level key once (ticker|side|action|grain-exact
    // price) so each displayed order scans only its own group on the 4Hz rebuild
    // — not every open order (avoids O(n²) across the account). #84.
    const siblingGroups = new Map<string, SiblingOrder[]>();
    for (const o of openOrders) {
      const sib: SiblingOrder = {
        orderId: o.orderId,
        ticker: o.ticker,
        side: o.side,
        action: o.action,
        priceCents: sideNativePriceCents(o),
        queuePosition: humanOrderSliceById.get(o.orderId)?.queuePosition ?? null,
        remainingCount: o.remainingCount,
      };
      const key = siblingGroupKey(sib);
      if (key == null) continue;
      const g = siblingGroups.get(key);
      if (g) g.push(sib);
      else siblingGroups.set(key, [sib]);
    }
    const calculate = (readBook: typeof getOrderbookRaw, current?: Map<string, { queue: number | null; queueAbove: number | null; queueBehind: number | null; total: number | null }>) => {
      const map = new Map<
        string,
        {
          queue: number | null;
          queueAbove: number | null;
          queueBehind: number | null;
          total: number | null;
        }
      >();
      for (const order of openOrders) {
        if (current && !getOrderbookDisplay(order.ticker).retained) {
          map.set(order.orderId, current.get(order.orderId)!); continue;
        }
        const slice = humanOrderSliceById.get(order.orderId);
        const atLevel = slice?.queuePosition ?? null;
        if (atLevel == null) {
          map.set(order.orderId, {
            queue: null,
            queueAbove: null,
            queueBehind: null,
            total: null,
          });
          continue;
        }
        const book = readBook(order.ticker);
        if (!book) {
          map.set(order.orderId, {
            queue: atLevel,
            queueAbove: null,
            queueBehind: null,
            total: null,
          });
          continue;
        }
        // The order's own price (side-native cents, grain-exact via mirrorCents).
        const priceCents = sideNativePriceCents(order);
        if (priceCents == null) {
          map.set(order.orderId, {
            queue: atLevel,
            queueAbove: null,
            queueBehind: null,
            total: null,
          });
          continue;
        }
        // "Better" levels (fill before this order):
        //   • BUY  → same-side bids at strictly higher prices.
        //   • SELL → opposite-side bids at strictly higher 100-prices, which
        //            represent asks at lower prices than this sell.
        const isSell = order.action === 'sell';
        const refLevels = isSell
          ? (order.side === 'yes' ? book.no : book.yes)
          : (order.side === 'yes' ? book.yes : book.no);
        // Exact mirror — the `lvl.price === threshold` at-level match below is
        // FLOAT EQUALITY; a drifted 100−p threshold silently misses the level
        // on sub-cent books and the queue column lies.
        const threshold = isSell ? mirrorCents(priceCents) : priceCents;
        let better = 0;
        let atLevelDepth = 0;
        for (const lvl of refLevels) {
          if (lvl.price > threshold) better += lvl.size;
          else if (lvl.price === threshold) atLevelDepth += lvl.size;
        }
        const total =
          book.yes.reduce((s, l) => s + l.size, 0) +
          book.no.reduce((s, l) => s + l.size, 0);
        // #84: atLevelDepth includes the user's OWN other orders resting at this
        // same (ticker, side, action, price). An own sibling BEHIND this order
        // would otherwise be counted as market depth behind it. Look up only this
        // order's group and sum the siblings provably behind (queue position past
        // this order's whole block); unknown positions are left in (never subtract
        // on a guess).
        const selfSib: SiblingOrder = {
          orderId: order.orderId,
          ticker: order.ticker,
          side: order.side,
          action: order.action,
          priceCents,
          queuePosition: atLevel,
          remainingCount: order.remainingCount,
        };
        const selfKey = siblingGroupKey(selfSib);
        const ownSiblingBehind =
          selfKey == null ? 0 : ownSiblingBehindQty(selfSib, siblingGroups.get(selfKey) ?? []);
        map.set(order.orderId, {
          queue: atLevel + better,
          queueAbove: better,
          // Math.max guards stale-book races where Kalshi's atLevel slot
          // outruns the book snapshot.
          queueBehind: queueBehindAtPriceLevel(
            atLevelDepth,
            atLevel,
            order.remainingCount,
            ownSiblingBehind,
          ),
          total,
        });
      }
      return map;
    };
    const current = calculate(getOrderbookRaw);
    // Prime viewed books; only derive a second map during a qualified resync.
    const recovering = new Map<string, { deadline: number; proof: SourceProof }>();
    const sources = new Map<string, SourceProof>();
    let retained = false;
    for (const order of openOrders) {
      const book = getOrderbookDisplay(order.ticker);
      retained ||= book.retained;
      if (book.continuity) sources.set(order.orderId, book.continuity);
      if (book.retained && book.retentionExpiresAtMonoMs != null && book.continuity) {
        recovering.set(order.orderId, {deadline: book.retentionExpiresAtMonoMs, proof: book.continuity});
      }
    }
    const display = retained ? calculate(ticker => getOrderbookDisplay(ticker).book, current) : current;
    return { current, display, recovering, sources };
  }, [openOrders, humanOrderSliceById, getOrderbookRaw, getOrderbookDisplay, queueBookStampKey]);
  const queueAlertMetricsByOrderId = useMemo(
    () => queueAlertEvidence(queueMetricsByOrderId, strictOrderSliceById, recoveringBookOrderIds),
    [queueMetricsByOrderId, strictOrderSliceById, recoveringBookOrderIds],
  );

  // Throttle the SORT KEYS for live-metric sorts so the orders table doesn't
  // re-order on every market tick (the "spazzing" report). Cells keep rendering
  // live values from queueMetricsByOrderId; only the row ORDER settles on the
  // ORDER_SORT_STABILIZE_MS cadence. default/size sorts are already stable.
  const [sortStabilizeTick, setSortStabilizeTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(
      () => setSortStabilizeTick((t) => t + 1),
      ORDER_SORT_STABILIZE_MS,
    );
    return () => window.clearInterval(id);
  }, []);
  const stableOrderSortKeys = useMemo(() => {
    const m = new Map<string, OrderSortLiveKeys>();
    for (const order of openOrders) {
      const queue = displayQueueMetricsByOrderId.get(order.orderId)?.queue ?? null;
      const v = orderVelocity(order.orderId).m1;
      const velocity =
        v.filledDelta != null && v.filledDelta > 0
          ? v.filledDelta
          : v.queueDelta != null
            ? Math.abs(v.queueDelta)
            : null;
      m.set(order.orderId, { queue, velocity });
    }
    // Poly rows: same stabilize cadence, same map. Queue mirrors the row
    // cell's recipe EXACTLY (polyOrderLiveData → polyOrderQueueFromBook) so
    // the sort can never disagree with the number displayed — but note the
    // comparability caveat on OrderSortLiveKeys: Kalshi queue is the venue's
    // authoritative own-slot FIFO, poly is the pessimistic whole-level-ahead
    // upper bound. Sells / dead book / unresolved market → null (tail).
    // Velocity has no poly source yet (the cell dashes) → null.
    for (const o of polyOrdersState.orders) {
      const id = String(o.id ?? o.orderID ?? o.orderId ?? '');
      if (!id) continue;
      const assetId = String(o.asset_id ?? o.market ?? id);
      const desc = describePolyOrderCached(assetId);
      const sideStr = String(o.side ?? '').toUpperCase();
      const priceFrac = typeof o.price === 'number' ? o.price : Number(o.price ?? NaN);
      // Centi-cent grain — whole-cent rounding breaks the level match on
      // fine-tick books (book levels are decimal now).
      const priceCents = Number.isFinite(priceFrac) ? Math.round(priceFrac * 10000) / 100 : null;
      let queue: number | null = null;
      if (sideStr !== 'SELL' && priceCents != null && desc) {
        const live = polyOrderLiveData(assetId, desc.pmTicker);
        queue = polyOrderQueueFromBook(live.sideBids, priceCents).queue;
      }
      m.set(id, { queue, velocity: null });
    }
    return m;
    // Throttled ON PURPOSE: recompute only on the stabilize tick (latest values at
    // that moment), NOT on every queueMetricsByOrderId/openOrders/polyOrdersState
    // change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortStabilizeTick]);

  const sortedOrders = useMemo<SortedOrderRow[]>(() => {
    if (orderSort.key === 'default') return baseSortedOrders;
    const dir = orderSort.dir === 'asc' ? 1 : -1;
    const rows = [...baseSortedOrders];
    rows.sort(makeOrderRowComparator(orderSort.key, dir, stableOrderSortKeys));
    return rows;
  }, [baseSortedOrders, orderSort, stableOrderSortKeys]);

  // #97 Lock: hold the captured order (keyed by orderRowId, the same id the
  // stabilize map uses) while queue/size/status cells keep ticking.
  const displayedOrders = useMemo(
    () => applyLockedOrder(sortedOrders, ordersLockKeys, orderRowId),
    [sortedOrders, ordersLockKeys],
  );
  // #102 review: the Open Orders TABLE renders the live sort (Lock is card-only,
  // its control is hidden in table mode); the card view keeps the locked order.
  const orderTableRows = useMemo(
    () => orderingForView(orderViewMode === 'table', displayedOrders, sortedOrders),
    [orderViewMode, displayedOrders, sortedOrders],
  );

  const togglePositionsLock = useCallback(() => {
    setPositionsLockKeys((prev) => (prev ? null : sortedPositions.map(posRowKey)));
  }, [sortedPositions, posRowKey]);
  const toggleOrdersLock = useCallback(() => {
    setOrdersLockKeys((prev) => (prev ? null : sortedOrders.map(orderRowId)));
  }, [sortedOrders]);

  // Headline Unrealized P&L includes all admitted venues. A row contributes only when
  // every input required by its row recipe is known. Missing marks, Poly
  // open-share fees, or Poly close-fee schedules withhold the headline and
  // expose coverage instead of silently turning the row into zero.
  const visiblePnlCoverage = useMemo(() => {
    void marketVersion; // poly marks read the store imperatively — re-derive on ticks
    const reasons: Array<UnpricedReason | undefined> = [];
    const values = sortedPositions.map((row) => {
      if (row.kind === 'us') {
        const view = usViews.get(row.accountId);
        const economics = usPositionEconomics(row.pos, usPositionMarks.getMarket(row.pos.target.marketSlug), usMarkNow);
        if (economics.pnlDollars === null) reasons.push(economics.nowCents === null ? 'no-mark'
          : economics.costDollars === null ? 'us-entry-basis' : 'us-close-fee');
        return view?.readError || view?.openError ? null : economics.pnlDollars === null ? null : economics.pnlDollars * 100;
      }
      if (row.kind === 'poly') {
        const nowCents = polyPositionLiveData(row.pos.ticker, row.pos.side).nowCents;
        if (nowCents == null || row.pos.openFeesCents == null) {
          reasons.push(nowCents == null ? 'no-mark' : 'poly-fee-allocation');
          return null;
        }
        // Exact relay basis, not avg×qty (whole-cent avg re-multiplied
        // drifts — the I-035 class).
        const entryCost =
          row.pos.costBasisCents ?? row.pos.avgEntryPriceCents * row.pos.netCount;
        // Row-recipe parity (Jul-14 audit): OPEN-share entry fees (the
        // bucket total double-counts the sold share against realized-net)
        // and the taker close-fee estimate when the schedule resolves —
        // the same inputs the row cells below subtract, so the tile can't
        // drift from Σ(rows) on fee-enabled markets.
        const totalCost = entryCost + row.pos.openFeesCents;
        const closeFee =
          estimatePolyOrderFeeCents({
            priceCents: nowCents,
            count: row.pos.netCount,
            role: 'taker',
            schedule: polyFeeSchedules.get(row.pos.ticker),
          });
        if (closeFee == null) { reasons.push('poly-fee-schedule'); return null; }
        return nowCents * row.pos.netCount - closeFee - totalCost;
      }
      if (row.pos.unrealizedPnl == null) reasons.push(row.pos.unpricedReason);
      return row.pos.unrealizedPnl;
    });
    return { ...summarizeUnrealizedCoverage(values), unpricedSummary: describeUnpricedReasons(reasons) };
  }, [sortedPositions, marketVersion, polyFeeSchedules, usPositionMarks, usViews, usMarkNow]);
  const visibleTotalPnl = positionsSnapshotConfirmed ? visiblePnlCoverage.valueCents : null;

  const visiblePositionValueDollars = useMemo(() => {
    void marketVersion;
    return sortedPositions.reduce<number | null>((sum, row) => {
      if (sum === null) return null;
      if (row.kind === 'us') {
        const view = usViews.get(row.accountId);
        const economics = usPositionEconomics(row.pos, usPositionMarks.getMarket(row.pos.target.marketSlug), usMarkNow);
        return view?.readError || view?.openError || economics.valueDollars === null ? null : sum + economics.valueDollars;
      }
      if (row.kind === 'poly') {
        // Mark-to-market when the slug market is live; entry-cost fallback
        // (the old behavior) when it isn't.
        const nowCents = polyPositionLiveData(row.pos.ticker, row.pos.side).nowCents;
        const valueCents =
          nowCents != null
            ? nowCents * row.pos.netCount
            : (row.pos.costBasisCents ?? row.pos.avgEntryPriceCents * row.pos.netCount);
        return sum + valueCents / 100;
      }
      const valueCents =
        row.pos.totalValueCents ?? row.pos.averagePrice * Math.abs(row.pos.position);
      return sum + valueCents / 100;
    }, 0);
  }, [sortedPositions, marketVersion, usPositionMarks, usViews, usMarkNow]);

  const latestVisibleTotalPnlRef = useRef(visibleTotalPnl);
  const previousSportFilterRef = useRef(sportFilterKey);
  const didPrimePnlCardRef = useRef(sortedPositions.length > 0);
  const [displayTotalPnl, setDisplayTotalPnl] = useState(visibleTotalPnl);
  const [pnlCardAnchorMs, setPnlCardAnchorMs] = useState(() => Date.now());

  useEffect(() => {
    latestVisibleTotalPnlRef.current = visibleTotalPnl;
    // Invalid evidence must withdraw the cached total immediately. Recovery
    // starts with the newly complete value, never the pre-failure total.
    setDisplayTotalPnl(previous => visibleTotalPnl == null || previous == null ? visibleTotalPnl : previous);
  }, [visibleTotalPnl]);

  useEffect(() => {
    if (didPrimePnlCardRef.current || sortedPositions.length === 0) return;
    didPrimePnlCardRef.current = true;
    setDisplayTotalPnl(visibleTotalPnl);
    setPnlCardAnchorMs(Date.now());
  }, [sortedPositions.length, visibleTotalPnl]);

  useEffect(() => {
    if (previousSportFilterRef.current === sportFilterKey) return;
    previousSportFilterRef.current = sportFilterKey;
    latestVisibleTotalPnlRef.current = visibleTotalPnl;
    setDisplayTotalPnl(visibleTotalPnl);
    setPnlCardAnchorMs(Date.now());
  }, [sportFilterKey, visibleTotalPnl]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setDisplayTotalPnl(latestVisibleTotalPnlRef.current);
      setPnlCardAnchorMs(Date.now());
    }, PNL_CARD_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  // Same-game highlight: hovering any card/row lights up every other card/row for
  // the same game, across both venues.
  //
  // MOUSE-ONLY, deliberately. Keyboard/focus support was added here unprompted and
  // then cut: it isn't in the AC, and two bugs lived inside it (dead <tr> focus
  // handlers, and React focus events bubbling from the nested Cancel/Edit/pin
  // buttons, which set and cleared the highlight spuriously). Half-bolted-on a11y
  // is worse than none — it deserves its own ticket, not a corner of this one.
  //
  // Hover, not click: a card click already opens the order/trade panel, so we don't
  // overload it with a pin toggle. A persistent pinned mode, if wanted later, gets
  // its own dedicated control (Brendan — separate follow-up, not this ticket).
  //
  // We hold the hovered ROW, not its game key, and re-resolve the key every
  // render. Holding the key broke two ways:
  //   - a row whose market hadn't loaded resolved to null on mouseenter, and
  //     when the market landed ~3s later the cursor was still on it — no new
  //     mouseenter fires, so nothing ever lit up;
  //   - a row that unmounts under the cursor (order fills on the poll, the user
  //     hits Cancel inside the row, a filter drops it) never fires mouseleave,
  //     so the highlight stayed stuck on with the cursor nowhere near it.
  // Re-resolving fixes the first; the effect below clears a stranded hover.
  const [hoveredRow, setHoveredRow] = useState<SameGameRowRef | null>(null);
  const activeGameKey = hoveredRow ? rowGameKey(hoveredRow) : null;

  // Card-view VMs — the same sorted rows the tables consume, projected into
  // the card layout. Built here because TraderView owns the market-store
  // accessors and the queue metrics.
  // #1226 — a US row's view-model, from its account's reader and the catalogue
  // (its link, event start and sport). Shared by the lists and the pinned strip.
  const usOrderVM = useCallback((view: UsAccountView, order: UsOrderRow, sport: Sport | null): OrderCardVM => {
    const link = usRowLink(usMarkets, order.target, view.accountId);
    const rowKey = usOrderKey(order);
    return buildUsOrderVM(order, {
      accountId: view.accountId, accountLabel: view.label, context: view.context!, pending: view.pending, busy: view.busy,
      error: view.errors.get(rowKey) ?? null, nowMs: view.nowMs, eventStartMs: link.startMs,
      eventStartLabel: formatStartTimeLocal(link.startMs), sport, teams: link.teams, rowKey,
    });
  }, [usMarkets]);
  const usPositionVM = useCallback((view: UsAccountView, pos: UsPositionRow, sport: Sport | null): PositionCardVM => {
    const link = usRowLink(usMarkets, pos.target, view.accountId);
    return buildUsPositionVM(pos, {
      accountId: view.accountId, accountLabel: view.label, eventStartLabel: formatStartTimeLocal(link.startMs), sport, teams: link.teams,
      closeState: view.closeStates.get(usPositionKey(pos)) ?? null, busy: view.busy,
      market: view.readError || view.openError ? null : usPositionMarks.getMarket(pos.target.marketSlug), nowMs: usMarkNow,
    });
  }, [usMarkets, usPositionMarks, usMarkNow]);
  const orderCardVMsRaw = useMemo<OrderCardVM[]>(() => {
    void marketVersion;
    void feeSeriesRevision;
    void feeEventRevision;
    return displayedOrders.map((row) => withFamilyPending(orderRowVM(row), row.familyPending));
    function orderRowVM(row: SortedOrderRow): OrderCardVM {
      if (row.kind === 'poly') return buildPolyOrderVM(row.order);
      if (row.kind === 'us') return usOrderVM(usViews.get(row.accountId)!, row.order, row.sport);
      const metrics = queueMetricsByOrderId.get(row.order.orderId);
      const eventStartMs = Number.isFinite(row.sortStart) ? row.sortStart : null;
      return buildKalshiOrderVM(row.order, {
        lastTradeYesCents: getLastTradeYesCents(row.order.ticker),
        book: getOrderbookRaw(row.order.ticker),
        queue: metrics?.queue ?? null,
        queueAbove: metrics?.queueAbove ?? null,
        queueBehind: metrics?.queueBehind ?? null,
        queueTotal: metrics?.total ?? null,
        // Preserve undefined so kalshiOrderFillStats can use its documented
        // count-minus-remaining fallback when no session-window data exists.
        filledQty: recentFilledQtyByOrderId.get(row.order.orderId),
        feeAdjustedOdds,
        quoteMakerRebatePct: quoteRebatePct,
        priceVelocityM1YesCents: priceVelocityWithCurrentPrice(
          row.order.ticker,
          getMarketPrice(row.order.ticker),
        ).m1.priceDelta,
        eventStartMs,
        eventStartLabel: formatStartTimeLocal(eventStartMs),
        gameDate: resolvedGameDate(row.order.ticker),
      });
    }
  }, [
    displayedOrders,
    queueMetricsByOrderId,
    getLastTradeYesCents,
    getOrderbookRaw,
    feeAdjustedOdds,
    quoteRebatePct,
    feeSeriesRevision,
    feeEventRevision,
    marketVersion,
    getMarketPrice,
    recentFilledQtyByOrderId,
    usOrderVM,
    usViews,
  ]);

  /**
   * #731 — hand an unchanged row back its PREVIOUS view-model object.
   *
   * The builder above runs on every 4 Hz publication and produces a fresh
   * object per row whether or not that row's market moved; at 858 open orders
   * that is ~3,400 new objects a second, each one invalidating its row. The
   * store compares by value and reuses the older reference only when the two
   * are interchangeable — nothing is withheld, delayed, or served stale, and
   * anything it cannot prove equal counts as changed. See `stableVm.ts`.
   */
  const [orderVmIdentity] = useState(() => createVmIdentityStore<OrderCardVM>());
  const orderCardVMs = useMemo(
    () => orderVmIdentity.stabilize(orderCardVMsRaw.map(vm => withOrderBookDisplay(
      vm, getOrderbookDisplay(vm.ticker), displayQueueMetricsByOrderId.get(vm.key),
    ))),
    [orderVmIdentity, orderCardVMsRaw, getOrderbookDisplay, displayQueueMetricsByOrderId],
  );

  // One feed subscription and one coarse market tick for the whole Open
  // Orders surface. Cards, compact rows, table rows, and the edit modal all
  // consume the same pure certainty contract without adding a subscription
  // for every rendered order.
  const eventStateFeed = useEventStateFeedSnapshotState();
  const orderGameStateKeys = useMemo(() => {
    void marketVersion;
    const keys = new Set<string>();
    for (const vm of orderCardVMs) {
      const key = gameStateKeyForTickerOrToken(vm.ticker);
      if (key) keys.add(key);
    }
    return Array.from(keys);
  }, [marketVersion, orderCardVMs]);
  useGameStatesPoll(orderGameStateKeys);
  // Recompute only when lifecycle authority changes, not on every score tick.
  // The resolver reads the full current record imperatively after this stable
  // signature proves something relevant changed. The signature reads the
  // store the resolver reads (Core in primary mode, legacy otherwise).
  const orderGameStateSignature = useGameStateLifecycleSignature(orderGameStateKeys);
  const eventStateCertaintyByTicker = useMemo(() => {
    void marketVersion;
    void orderGameStateSignature;
    const byTicker = new Map<
      string,
      import('../../lib/sport/eventStateCertainty').EventStateCertainty
    >();
    for (const vm of orderCardVMs) {
      if (!byTicker.has(vm.ticker)) {
        byTicker.set(
          vm.ticker,
          resolveOrderEventStateCertainty(vm.ticker, eventStateFeed),
        );
      }
    }
    return byTicker;
  }, [eventStateFeed, marketVersion, orderCardVMs, orderGameStateSignature]);

  const positionCardVMsRaw = useMemo<PositionCardVM[]>(() => {
    // Poly marks read the store imperatively (polyPositionLiveData — the
    // same source as the table's Now cell) — re-derive on the throttled
    // hydra clock so card/compact/mini P&L ticks like the table does.
    void marketVersion;
    return displayedPositions.map((row) => {
      const vm =
        row.kind === 'us'
          ? usPositionVM(usViews.get(row.accountId)!, row.pos, row.sport)
          : row.kind === 'poly'
          ? buildPolyPositionVM(
              row.pos,
              formatStartTimeLocal(Number.isFinite(row.sortStart) ? row.sortStart : null),
              polyPositionLiveData(row.pos.ticker, row.pos.side).nowCents,
              polyFeeSchedules.get(row.pos.ticker),
            )
          : buildKalshiPositionVM(
              row.pos,
              row.restingCloseQty,
              formatStartTimeLocal(Number.isFinite(row.sortStart) ? row.sortStart : null),
              resolvedGameDate(row.pos.ticker),
            );
      // Volume on the tiles (Joshua's Jun 8 call notes). Snapshot at list
      // build — volume moves slowly; not worth per-frame VM churn.
      // #59: poly VMs carry the CONDITION ID, but _markets keys by the PM-
      // slug — resolve first or the lookup is a guaranteed miss and poly
      // cards never show volume.
      // #958: a Kalshi volume not yet observed since the feed's current
      // generation is absent here too — the dash the dashboard's volume cells show.
      const store = useMarketStore.getState();
      const volTicker =
        vm.venue === 'polymarket'
          ? (store.getTickerByConditionId(vm.ticker) ?? vm.ticker)
          : vm.ticker;
      return withFamilyPending(
        {
          ...vm,
          volume: store.getTickerSourceValidity(volTicker) === 'unconfirmed'
            ? null
            : (store.getMarket(volTicker)?.volume ?? null),
        },
        row.familyPending,
      );
    });
  }, [displayedPositions, marketVersion, polyFeeSchedules, usPositionVM, usViews]);

  /**
   * #731 — same identity treatment as the order rows. Measured before this:
   * every position row re-rendered on every publication, including ticks where
   * no position's data moved at all.
   */
  const [positionVmIdentity] = useState(() => createVmIdentityStore<PositionCardVM>());
  const positionCardVMs = useMemo(
    () => positionVmIdentity.stabilize(positionCardVMsRaw),
    [positionVmIdentity, positionCardVMsRaw],
  );
  // #1226 — the table's US rows read the same stabilised view-models the
  // cards render (the table follows the live sort; the VMs the locked one;
  // the set is the same).
  const usPositionVmByKey = useMemo(() => {
    const map = new Map<string, PositionCardVM>();
    for (const vm of positionCardVMs) if (vm.us) map.set(vm.key, vm);
    return map;
  }, [positionCardVMs]);

  // An 800+ order account used to ask React and the browser to mount every
  // row in the first navigation commit. Keep all derivation, alerts, summaries,
  // and cancel targets on the complete live arrays, but admit rendered rows in
  // bounded animation-frame batches. This yields between DOM batches without
  // debouncing or caching any order/position truth.
  //
  // #1331: admission is by row identity over the list the active view renders
  // (the table follows the live sort, the cards the locked order). A numeric
  // prefix unmounted rows already on screen whenever a later account's read
  // sorted ahead of them. The account scope restarts admission per sign-in,
  // org and key mode.
  const positionRenderKey = `${positionViewMode}:${positionSort.key}:${positionSort.dir}:${sportFilterKey}`;
  const orderRenderKey = `${orderViewMode}:${orderSort.key}:${orderSort.dir}:${orderFilterKey}`;
  const rowAdmissionScope = `${usSessionKey}|${keyMode ?? ''}`;
  const positionTableActive = positionViewMode === 'table';
  const orderTableActive = orderViewMode === 'table';
  const positionRowIds = useMemo(
    () => (positionTableActive ? positionTableRows.map(posRowKey) : positionCardVMs.map((vm) => vm.key)),
    [positionTableActive, positionTableRows, positionCardVMs, posRowKey],
  );
  const orderRowIds = useMemo(
    () => (orderTableActive ? orderTableRows.map(orderRowId) : orderCardVMs.map((vm) => vm.key)),
    [orderTableActive, orderTableRows, orderCardVMs],
  );
  const positionAdmission = useProgressiveAdmission(positionRowIds, `${positionRenderKey}|${rowAdmissionScope}`);
  const orderAdmission = useProgressiveAdmission(orderRowIds, `${orderRenderKey}|${rowAdmissionScope}`);

  // ── Reading position (#828, on #829's primitive) ────────────────────
  // Bound to the page's REAL overflow element below, never the window.
  //
  // The occluding band is resolved positionally by `readingOcclusion` above:
  // whichever of the two toolbars and the opted-in pin strip is actually
  // covering the top edge when the hook asks.
  //
  // The two render keys already ARE the row-set identity — view mode, sort key,
  // sort direction and the filter chips, per surface. Anything that changes
  // which rows exist or their order makes this a different reading position
  // rather than a stale one to seek back to.
  const readingScrollContext = `${positionRenderKey}|${orderRenderKey}`;
  const readingScrollRef = useWorkingScroll({
    pane: 'position-tracker-main',
    instance: 'tracker',
    context: readingScrollContext,
    occlusion: readingOcclusion,
    // Readiness stays the CONSUMER's job (docs/260909WorkingScrollRestoration.md):
    // the hook retries a browser-clamped restore inside its own window, but it
    // cannot know that this page is still admitting rows. `useProgressiveAdmission`
    // releases them in bounded animation-frame batches, so an anchor past the
    // first batch is not in the DOM at first paint and a restore would give up
    // on a row that was about to exist.
    ready: positionAdmission.complete && orderAdmission.complete,
  });
  // Only the active view's list is admitted and rendered; the other renders nothing.
  const renderedPositionCardVMs = useMemo(
    () => (positionTableActive ? NO_ROWS : admitRows(positionCardVMs, positionAdmission)),
    [positionTableActive, positionCardVMs, positionAdmission],
  );
  const renderedPositionTableRows = useMemo(
    () => (positionTableActive ? admitRows(positionTableRows, positionAdmission) : NO_ROWS),
    [positionTableActive, positionTableRows, positionAdmission],
  );
  const renderedOrderCardVMs = useMemo(
    () => (orderTableActive ? NO_ROWS : admitRows(orderCardVMs, orderAdmission)),
    [orderTableActive, orderCardVMs, orderAdmission],
  );
  const renderedOrderTableRows = useMemo(
    () => (orderTableActive ? admitRows(orderTableRows, orderAdmission) : NO_ROWS),
    [orderTableActive, orderTableRows, orderAdmission],
  );

  // Same-game highlight — canonical game key per card VM. `rowGameKey` reads
  // marketStore NON-REACTIVELY, so this memo is only as fresh as its dependency
  // list makes it, and it must stay fresh: a market that loads — or gains its
  // `gameNo` — after first paint has to re-key its rows, or its highlight is
  // dead for the life of the page.
  //
  // Before #731 that freshness was transitive: the VM arrays were rebuilt on
  // every ~4Hz tick, so this recomputed as a side effect of their churn.
  // #731 removed the churn, so the real input is now named directly. See the
  // note inside.
  const gameKeyByVmKey = useMemo(() => {
    // See the dependency note below — `void` is this file's existing idiom for
    // a real-but-unreferenced input (cf. orderCardVMsRaw).
    void marketVersion;
    const m = new Map<string, string | null>();
    for (const vm of positionCardVMs) m.set(vm.key, rowGameKey(vm));
    for (const vm of orderCardVMs) m.set(vm.key, rowGameKey(vm));
    return m;
    // #731 — `marketVersion` is listed EXPLICITLY, and removing it is a silent
    // bug, not a cleanup: this map would freeze and take the same-game
    // highlight with it. It was ALWAYS a real dependency, just an invisible
    // one. Naming it keeps the recompute cadence exactly as it was — one per
    // market publication — with nothing left transitive.
    // Guarded by renderContract.test.tsx, because no rendered byte changes.
  }, [positionCardVMs, orderCardVMs, marketVersion]);

  /** Highlight props for a card VM. Mouse-only — keyboard/a11y is out of scope
   *  for this ticket and is offered as a follow-up. */
  const sameGameProps = useCallback(
    (vm: SameGameRowRef) => {
      const gk = gameKeyByVmKey.has(vm.key)
        ? (gameKeyByVmKey.get(vm.key) ?? null)
        : rowGameKey(vm);
      const enter = () => setHoveredRow({ venue: vm.venue, ticker: vm.ticker, key: vm.key });
      const leave = () => setHoveredRow(null);
      return {
        highlighted: isSameGameHighlighted(gk, activeGameKey),
        onMouseEnter: enter,
        onMouseLeave: leave,
      };
    },
    [gameKeyByVmKey, activeGameKey],
  );

  /** Same-game highlight for plain rows (table `<tr>`s) — the accent class plus
   *  the hover handlers. Table rows aren't built from card VMs, so they pass a row
   *  ref and the key resolves the same way. A null key never lights.
   *
   *  `activeGameKey &&` is load-bearing, not a micro-optimisation: with nobody
   *  hovering — the normal state of the page — it skips rowGameKey entirely. This
   *  runs from four table sites, per row, on every render, at the market clock's
   *  ~4Hz, on the view whose own comments call it the main-thread saturator. */
  const sameGameRow = useCallback(
    (row: SameGameRowRef) => {
      const enter = () => setHoveredRow(row);
      const leave = () => setHoveredRow(null);
      return {
        className:
          activeGameKey && isSameGameHighlighted(rowGameKey(row), activeGameKey)
            ? SAME_GAME_HIGHLIGHT
            : '',
        onMouseEnter: enter,
        onMouseLeave: leave,
      };
    },
    [activeGameKey],
  );

  // Pinned strip rows — resolved from the UNFILTERED sources, not from
  // sortedPositions/sortedOrders, so a pin survives the section filters
  // (acceptance: a pinned NFL order stays put while the trader narrows the
  // sections to Soccer). Same VM builders + live inputs as the section
  // renders, so both copies of a pinned item tick together. vm === null →
  // the item left the open set (closed / filled / canceled) → tombstone
  // row. Note: during the initial fetch the sources are briefly empty, so a
  // pin can flash its tombstone for a beat before the live row lands —
  // self-correcting, deliberately not worth a per-source loading gate.
  const pinnedRows = useMemo(() => {
    void feeSeriesRevision;
    void feeEventRevision;
    void marketVersion;
    if (pinnedEntries.length === 0) {
      return [] as {
        entry: PinnedEntry;
        posVM: PositionCardVM | null;
        orderVM: OrderCardVM | null;
      }[];
    }
    const posByKey = new Map<string, PositionCardVM>();
    for (const pos of positionsWithPnl) {
      const key = `${pos.ticker}-${pos.side}`;
      if (!pinnedKeySet.has(key)) continue;
      const closes = workingCloses(pos, closeCountOrders, kalshiCancelStates);
      const startMs = sortStartMs(pos.ticker);
      posByKey.set(
        key,
        buildKalshiPositionVM(
          pos,
          closes.qty,
          formatStartTimeLocal(Number.isFinite(startMs) ? startMs : null),
          resolvedGameDate(pos.ticker),
        ),
      );
    }
    for (const pos of polyOpenPositions) {
      const key = `poly-pos-${pos.ticker}-${pos.side}`;
      if (!pinnedKeySet.has(key)) continue;
      // Same slug-ticker resolution the table sort uses — a pinned poly
      // position must show the SAME start label as its row (conditionId
      // never resolves; the cached description's PM- slug does).
      const startMs = polySortStartMs(describePolyMarketCached(pos.ticker)?.pmTicker, sortStartMs);
      posByKey.set(
        key,
        buildPolyPositionVM(
          pos,
          formatStartTimeLocal(Number.isFinite(startMs) ? startMs : null),
          polyPositionLiveData(pos.ticker, pos.side).nowCents,
          polyFeeSchedules.get(pos.ticker),
        ),
      );
    }
    const orderByKey = new Map<string, OrderCardVM>();
    for (const order of openOrders) {
      if (!pinnedKeySet.has(order.orderId)) continue;
      const metrics = queueMetricsByOrderId.get(order.orderId);
      const startMs = sortStartMs(order.ticker);
      const eventStartMs = Number.isFinite(startMs) ? startMs : null;
      orderByKey.set(
        order.orderId,
        buildKalshiOrderVM(order, {
          lastTradeYesCents: getLastTradeYesCents(order.ticker),
          book: getOrderbookRaw(order.ticker),
          queue: metrics?.queue ?? null,
          queueAbove: metrics?.queueAbove ?? null,
          queueBehind: metrics?.queueBehind ?? null,
          queueTotal: metrics?.total ?? null,
          // Preserve undefined so kalshiOrderFillStats can use its documented
          // count-minus-remaining fallback when no session-window data exists.
          filledQty: recentFilledQtyByOrderId.get(order.orderId),
          feeAdjustedOdds,
          quoteMakerRebatePct: quoteRebatePct,
          priceVelocityM1YesCents: priceVelocityWithCurrentPrice(
            order.ticker,
            getMarketPrice(order.ticker),
          ).m1.priceDelta,
          eventStartMs,
          eventStartLabel: formatStartTimeLocal(eventStartMs),
          gameDate: resolvedGameDate(order.ticker),
        }),
      );
      const vm = orderByKey.get(order.orderId)!;
      orderByKey.set(order.orderId, withOrderBookDisplay(vm, getOrderbookDisplay(order.ticker), displayQueueMetricsByOrderId.get(order.orderId)));
    }
    for (const order of polyOrdersState.orders) {
      const vm = buildPolyOrderVM(order);
      if (!pinnedKeySet.has(vm.key)) continue;
      orderByKey.set(vm.key, vm);
    }
    // #1226 — a pinned US row is drawn from its account's whole read (a
    // chip never hides a pin), through the same builders as its list row.
    for (const view of usViews.values()) {
      if (!view.context || !view.snapshot) continue;
      for (const order of view.snapshot.orders) {
        const key = usOrderPinKey(view.accountId, order.intentId);
        if (!pinnedKeySet.has(key)) continue;
        orderByKey.set(key, usOrderVM(view, order, usMarkets.classifier.get(order.target.marketSlug)?.sport ?? null));
      }
      for (const pos of view.snapshot.positions) {
        const key = usHoldingPinKey(view.accountId, pos.target.marketSlug, pos.target.outcome);
        if (!pinnedKeySet.has(key)) continue;
        posByKey.set(key, usPositionVM(view, pos, usMarkets.classifier.get(pos.target.marketSlug)?.sport ?? null));
      }
    }
    return pinnedEntries.map((entry) => ({
      entry,
      posVM: entry.kind === 'position' ? (posByKey.get(entry.key) ?? null) : null,
      orderVM: entry.kind === 'order' ? (orderByKey.get(entry.key) ?? null) : null,
    }));
  }, [
    pinnedEntries,
    pinnedKeySet,
    positionsWithPnl,
    polyOpenPositions,
    openOrders,
    closeCountOrders,
    kalshiCancelStates,
    polyOrdersState.orders,
    queueMetricsByOrderId,
    recentFilledQtyByOrderId,
    getLastTradeYesCents,
    getOrderbookRaw,
    getOrderbookDisplay,
    displayQueueMetricsByOrderId,
    feeAdjustedOdds,
    quoteRebatePct,
    feeSeriesRevision,
    feeEventRevision,
    getMarketPrice,
    sortStartMs,
    marketVersion,
    polyFeeSchedules,
    usViews,
    usMarkets,
    usOrderVM,
    usPositionVM,
  ]);

  // Clear a STRANDED hover: the row under the cursor unmounted (an order filled on
  // the poll, or was cancelled from inside the row it was hovering), so no
  // mouseleave ever fired and the highlight would stay lit indefinitely with the
  // cursor nowhere near it.
  //
  // Matched by the row's UNIQUE key, not venue+ticker. A position and an order on
  // one ticker share a venue+ticker but are different rows: hover the order, it
  // fills and unmounts, and a venue+ticker check finds the surviving position and
  // wrongly keeps the whole game lit with nothing hovered (Brendan, PR #62).
  //
  // Must check the PINNED rows too, not just the section lists. The section VMs are
  // filtered by sport/kind; the pinned strip is built from the UNFILTERED sources by
  // design ("a pinned NFL order stays put while the trader narrows the sections to
  // Soccer"). Checking only the sections declared a filtered-out pinned row dead on
  // the very next commit — it flashed cyan, went dark, and could not be re-hovered
  // without moving the cursor away and back. The question is "does this row still
  // exist AT ALL", not "is it in the current filtered view".
  //
  // Lives below pinnedRows because it reads it. Cannot loop: the early return makes
  // the only setState drive the state to a value that short-circuits the next pass.
  useEffect(() => {
    if (!hoveredRow) return;
    const liveRowKeys = [
      ...positionCardVMs.map((v) => v.key),
      ...orderCardVMs.map((v) => v.key),
      ...pinnedRows.flatMap((r) => [r.posVM?.key, r.orderVM?.key]),
    ];
    if (!isHoveredRowPresent(hoveredRow.key, liveRowKeys)) setHoveredRow(null);
  }, [positionCardVMs, orderCardVMs, pinnedRows, hoveredRow]);

  // Tallies for the order card-view summary strip.
  const orderSummary = useMemo(() => {
    let atFront = 0;
    let queueFront = 0;
    let inSale = 0;
    let behind = 0;
    let wayBehind = 0;
    for (const vm of orderCardVMs) {
      if (vm.activeInSale) inSale += 1;
      if (vm.bookDiff === 0) atFront += 1;
      else if (vm.bookDiff != null && vm.bookDiff > 0) {
        if (
          vm.queueAbove != null
          && vm.queueAbove >= WAY_BEHIND_QUEUE_ABOVE_THRESHOLD
        ) {
          wayBehind += 1;
        } else {
          behind += 1;
        }
      }
      if (vm.queue === 0) queueFront += 1;
    }
    return {
      retained: orderCardVMs.some(vm => vm.bookRetained),
      total: orderCardVMs.length,
      atFront,
      queueFront,
      inSale,
      behind,
      wayBehind,
    };
  }, [orderCardVMs]);

  // Feed the per-ticker price velocity tracker. Sample once per effect-tick
  // for every distinct Kalshi ticker currently visible across open orders
  // and open positions. marketVersion is in the dep array so a fresh hydra
  // tick re-runs this effect and the 1m / 5m chips stay live as the market
  // moves. Polymarket isn't sampled — marketStore doesn't surface poly
  // current price on the position-card VM yet, so there's no source.
  useEffect(() => {
    const tickers = new Set<string>();
    for (const vm of orderCardVMs) {
      if (vm.venue === 'kalshi') tickers.add(vm.ticker);
    }
    for (const vm of positionCardVMs) {
      if (vm.venue === 'kalshi') tickers.add(vm.ticker);
    }
    for (const ticker of tickers) {
      const yesPrice = getMarketPrice(ticker);
      if (yesPrice != null) recordPriceSample(ticker, yesPrice);
    }
    prunePriceVelocity(tickers);
  }, [orderCardVMs, positionCardVMs, marketVersion, getMarketPrice]);

  // ── Cross-venue divergence (Poly vs Kalshi) ────────────────────────────────
  // Dan's ask: warn when Polymarket's mid pulls >= 2¢ from Kalshi on a market
  // you're in. Keyed by Kalshi ticker; the cards/rows look up their own ticker.
  const heldKalshiTickers = useMemo(() => {
    const s = new Set<string>();
    for (const vm of orderCardVMs) if (vm.venue === 'kalshi') s.add(vm.ticker);
    for (const vm of positionCardVMs) if (vm.venue === 'kalshi') s.add(vm.ticker);
    return s;
  }, [orderCardVMs, positionCardVMs]);
  // Content signature for the held set — lets the divergence sweep skip
  // recompute on frames where the holdings didn't actually change. Mirrors
  // orderTickersKey above. (Before #731 the Set above took a fresh reference on
  // every market tick and this key was the only thing standing between the
  // sweep and a 4 Hz recompute; the Set is now stable too, so this is belt and
  // braces rather than load-bearing — but it is still the correct guard, since
  // a Set identity says nothing about its contents.)
  const heldKalshiKey = useMemo(
    () => [...heldKalshiTickers].sort().join('|'),
    [heldKalshiTickers],
  );
  const hasHeldProps = useMemo(() => [...heldKalshiTickers].some(isPropTicker), [heldKalshiTickers]);
  useEffect(() => {
    // Lift the legacy prop discard only for an account that actually needs it.
    // Versioned filters continue to use trackerFeedIds as the exact allowlist.
    if (hasHeldProps) requestPropsFeed();
  }, [hasHeldProps]);

  // Coarse clock for the divergence recompute (see DIVERGENCE_REFRESH_MS). The
  // sweep keys on this tick + the held set, NOT marketVersion, so it runs ~1/s
  // instead of ~30/s. A holdings change re-keys immediately via heldKalshiKey.
  const [divergenceTick, setDivergenceTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(
      () => setDivergenceTick((t) => t + 1),
      DIVERGENCE_REFRESH_MS,
    );
    return () => window.clearInterval(id);
  }, []);

  // Build ticker -> divergence. getGames already pairs Kalshi<->Poly per game
  // side via canonical key; we only sweep the sports the held tickers touch (not
  // all 14) and only keep entries for markets the trader actually holds. Poly
  // freshness is decided inside computeDivergence (getOrderbookAgeMs), so a
  // stale poly feed yields a !fresh entry that never alerts. (Poly market data
  // DOES reach both lanes over the hydra WS — the Polymarket geoblock is
  // order-POST-only — so this is live on g1.pm staging too whenever the poly
  // feed is up; it's UI/sound-only, so harmless there.)
  const divergenceByTicker = useMemo(() => {
    const map = new Map<string, DivergenceInfo>();
    if (heldKalshiTickers.size === 0) return map;
    const sports = new Set<Sport>();
    for (const t of heldKalshiTickers) {
      const sp = sportFromTicker(t);
      if (sp) sports.add(sp);
    }
    for (const sp of sports) {
      const games = getGames(sp, 'moneyline', { dateFilter: 'all', statusFilter: 'all' });
      for (const g of games) {
        if (!g.kalshi || !g.polymarket) continue;
        for (const side of ['away', 'home'] as const) {
          const kBook = g.kalshi[side];
          const kTicker = kBook?.marketTicker;
          if (!kTicker || !heldKalshiTickers.has(kTicker)) continue;
          const pBook = g.polymarket[side];
          const polyTicker = pBook?.marketTicker ?? '';
          const polyAgeMs = polyTicker ? getOrderbookAgeMs(polyTicker) : null;
          const info = computeDivergence(kBook, pBook, polyTicker, polyAgeMs);
          if (info) map.set(kTicker, info);
        }
      }
    }
    return map;
    // heldKalshiTickers is captured via the stable heldKalshiKey; divergenceTick
    // is the coarse refresh clock. (exhaustive-deps would re-add marketVersion.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heldKalshiKey, divergenceTick, getGames, getOrderbookAgeMs]);

  // ── Poly mid (was "U/P"; the Unabated U half retired in #1373) ──────────
  // Feeds the positions table's poly column, the position card's poly stat
  // (replaced the positions 1m Δ chip), and the ORDER card's poly stat (rides
  // beside its 1m Δ — Dan, Jul 8). No consensus read: do not re-add one, or
  // substitute OddsPapi, for the retired U value (#1192/#1373). These refs
  // also drive trackerFeedIds and polyBookNeeds below — keep them even if
  // the display goes. Keyed by the row/VM key
  // (`${ticker}-${side}` kalshi position, `poly-pos-${ticker}-${side}` poly
  // position, `orderId` kalshi order, `poly-${id}` poly order), values
  // already side-native. Moneyline-only by construction — the sweep walks
  // getGames moneyline rows, so spread/total/period entries get no entry
  // and render '—'.
  const upOddsRefs = useMemo(() => {
    const kalshi = new Map<string, { key: string; side: 'yes' | 'no' }[]>();
    const poly = new Map<string, { key: string; side: 'yes' | 'no' }[]>();
    // Poly OPEN ORDERS identify by CLOB token id, not slug ticker — the
    // sweep matches these against each game slot's tokenId/noTokenId (the
    // token itself encodes the side, so no side field here).
    const polyTokens = new Map<string, { key: string }[]>();
    const sports = new Set<Sport>();
    for (const row of baseSortedPositions) {
      if (row.sport) sports.add(row.sport);
      // #1226 — a US row's market is not in the store's K/P pairing.
      if (row.kind === 'us') continue;
      if (row.kind === 'kalshi') {
        const list = kalshi.get(row.pos.ticker) ?? [];
        list.push({ key: `${row.pos.ticker}-${row.pos.side}`, side: row.pos.side });
        kalshi.set(row.pos.ticker, list);
      } else {
        const list = poly.get(row.pos.ticker) ?? [];
        list.push({ key: `poly-pos-${row.pos.ticker}-${row.pos.side}`, side: row.pos.side });
        poly.set(row.pos.ticker, list);
      }
    }
    for (const row of sortedOrders) {
      if (row.sport) sports.add(row.sport);
      if (row.kind === 'us') continue;
      if (row.kind === 'kalshi') {
        const list = kalshi.get(row.order.ticker) ?? [];
        list.push({ key: row.order.orderId, side: row.order.side });
        kalshi.set(row.order.ticker, list);
      } else {
        const o = row.order;
        const id = String(o.id ?? o.orderID ?? o.orderId ?? '');
        const assetId = String(o.asset_id ?? o.market ?? id);
        if (!assetId) continue;
        const list = polyTokens.get(assetId) ?? [];
        list.push({ key: `poly-${id}` });
        polyTokens.set(assetId, list);
      }
    }
    return { kalshi, poly, polyTokens, sports };
  }, [baseSortedPositions, sortedOrders]);
  // Stable identity for the swept set (both source lists get fresh references
  // every market tick via sortStartMs) — mirrors heldKalshiKey.
  const upOddsRefsKey = useMemo(() => {
    const keys: string[] = [];
    for (const list of upOddsRefs.kalshi.values()) for (const h of list) keys.push(h.key);
    for (const list of upOddsRefs.poly.values()) for (const h of list) keys.push(h.key);
    for (const list of upOddsRefs.polyTokens.values()) for (const h of list) keys.push(h.key);
    return keys.sort().join('|');
  }, [upOddsRefs]);

  const upOddsByKey = useMemo(() => {
    const map = new Map<string, UPOdds>();
    const { kalshi: heldK, poly: heldP, polyTokens, sports } = upOddsRefs;
    if (heldK.size === 0 && heldP.size === 0 && polyTokens.size === 0) return map;
    const store = useMarketStore.getState();
    // #59: the position ledger keys poly rows by CONDITION ID, but the game
    // slots carry the PM- slug ticker — without resolving here the two key
    // spaces never intersect, and the poly stat sits permanently
    // dead on poly positions (same defect class as the
    // resolvePolyCloseTarget conditionId miss that shipped Cash Out
    // disabled). Resolved per sweep tick so late-arriving frames heal.
    const heldPBySlug = new Map<string, { key: string; side: 'yes' | 'no' }[]>();
    for (const [cond, list] of heldP) {
      const slug = store.getTickerByConditionId(cond) ?? cond;
      const prev = heldPBySlug.get(slug);
      if (prev) prev.push(...list);
      else heldPBySlug.set(slug, [...list]);
    }
    for (const sp of sports) {
      const games = getGames(sp, 'moneyline', { dateFilter: 'all', statusFilter: 'all' });
      for (const g of games) {
        const kAway = g.kalshi?.away.marketTicker;
        const kHome = g.kalshi?.home.marketTicker;
        const pAway = g.polymarket?.away.marketTicker;
        const pHome = g.polymarket?.home.marketTicker;
        const touchesToken = (b: VenueBook | undefined): boolean =>
          b != null &&
          ((b.tokenId != null && polyTokens.has(b.tokenId)) ||
            (b.noTokenId != null && polyTokens.has(b.noTokenId)));
        const touchesHeld =
          (kAway != null && heldK.has(kAway)) ||
          (kHome != null && heldK.has(kHome)) ||
          (pAway != null && heldPBySlug.has(pAway)) ||
          (pHome != null && heldPBySlug.has(pHome)) ||
          touchesToken(g.polymarket?.away) ||
          touchesToken(g.polymarket?.home);
        if (!touchesHeld) continue;
        for (const teamSide of ['away', 'home'] as const) {
          const pBook = g.polymarket?.[teamSide];
          const polyMid = bookMid(pBook);
          const polyAgeMs = pBook ? getOrderbookAgeMs(pBook.marketTicker) : null;
          const polyFresh = polyAgeMs != null && polyAgeMs <= POLY_STALE_MS;
          // Kalshi holdings: exact ticker match on this team slot.
          const kTicker = g.kalshi?.[teamSide]?.marketTicker;
          for (const h of (kTicker != null && heldK.get(kTicker)) || []) {
            map.set(h.key, {
              pCents: sideNativeProbCents(polyMid, h.side),
              polyFresh,
            });
          }
          // Poly holdings: the same slug ticker sits on BOTH team slots, so a
          // ticker match alone arms the opposite team ~half the time — resolve
          // the held token against this slot's yes/no token ids instead (same
          // rule as resolveBuyPanelTarget).
          if (pBook) {
            for (const h of heldPBySlug.get(pBook.marketTicker) ?? []) {
              const mkt = store.getMarket(pBook.marketTicker);
              if (!mkt) continue;
              const heldToken = h.side === 'yes' ? mkt.yesTokenId : mkt.noTokenId;
              if (!heldToken) continue;
              const effSide =
                pBook.tokenId === heldToken
                  ? ('yes' as const)
                  : pBook.noTokenId === heldToken
                    ? ('no' as const)
                    : null;
              if (!effSide) continue;
              map.set(h.key, {
                pCents: sideNativeProbCents(polyMid, effSide),
                polyFresh,
              });
            }
            // Poly OPEN ORDERS: token-keyed refs — the order's asset_id IS
            // one of this slot's tokens, so the match itself decides the side.
            for (const ref of (pBook.tokenId != null && polyTokens.get(pBook.tokenId)) || []) {
              map.set(ref.key, {
                pCents: sideNativeProbCents(polyMid, 'yes'),
                polyFresh,
              });
            }
            for (const ref of (pBook.noTokenId != null && polyTokens.get(pBook.noTokenId)) || []) {
              map.set(ref.key, {
                pCents: sideNativeProbCents(polyMid, 'no'),
                polyFresh,
              });
            }
          }
        }
      }
    }
    return map;
    // upOddsRefs is captured via the stable upOddsRefsKey; divergenceTick is
    // the coarse 1s refresh clock (a cross-venue mid doesn't need
    // the ~30Hz market tick — same rationale as divergence).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upOddsRefsKey, divergenceTick, getGames, getOrderbookAgeMs]);

  // Poly orderbook subscriptions for the poly-mid sweep + divergence badges. It is
  // a BOOK mid, and orderbooks are subscription-gated (unlike the broad
  // market feed) — this page's only subscription was the Kalshi open-order
  // set, so the paired POLY books only streamed when some other view had
  // already subscribed them. A fresh page load then rendered "57/—" and
  // silently starved the poly divergence freshness gate (Dan, Jul 8 — masked
  // until a deploy reload cleared the tab's accidental cache). Collect the
  // paired poly ticker for every game the sweep refs touch (+ held poly
  // tickers directly); re-derived on the sweep's 1s tick so late-arriving
  // pairing still subscribes, effect fires only when the set changes.
  const polyBookNeeds = useMemo(() => {
    const needs = new Set<string>();
    const { kalshi: heldK, poly: heldP, polyTokens, sports } = upOddsRefs;
    if (heldK.size === 0 && heldP.size === 0 && polyTokens.size === 0) return [] as string[];
    void divergenceTick;
    for (const t of heldP.keys()) needs.add(t);
    const touchesToken = (b: VenueBook | undefined): boolean =>
      b != null &&
      ((b.tokenId != null && polyTokens.has(b.tokenId)) ||
        (b.noTokenId != null && polyTokens.has(b.noTokenId)));
    for (const sp of sports) {
      const games = getGames(sp, 'moneyline', { dateFilter: 'all', statusFilter: 'all' });
      for (const g of games) {
        const pAway = g.polymarket?.away;
        const pHome = g.polymarket?.home;
        const touches =
          (g.kalshi?.away.marketTicker != null && heldK.has(g.kalshi.away.marketTicker)) ||
          (g.kalshi?.home.marketTicker != null && heldK.has(g.kalshi.home.marketTicker)) ||
          (pAway != null && heldP.has(pAway.marketTicker)) ||
          (pHome != null && heldP.has(pHome.marketTicker)) ||
          touchesToken(pAway) ||
          touchesToken(pHome);
        if (!touches) continue;
        if (pAway) needs.add(pAway.marketTicker);
        if (pHome) needs.add(pHome.marketTicker);
      }
    }
    return [...needs].sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upOddsRefsKey, divergenceTick, getGames]);
  const polyBookNeedsKey = polyBookNeeds.join('|');
  // Consumer feed filters (feed-filters-per-view): the rows this view marks,
  // BY IDENTITY — held Kalshi positions + resting orders, held Poly
  // positions (slugs), resting Poly orders (token ids, resolved by the
  // helper), and the paired Poly leg of every held game (the poly mid + book
  // sweeps above). Alone on its connection this is the whole edge filter:
  // a tracker with 20 positions draws 20 rows at browser cadence instead
  // of every row of every sport it touches (~1,800 frames/s on Saturday
  // CFB, the he2 governor's whole load). Beside a broad sibling tab the
  // fold carries the held games instead. Classes stay fully opted in, as
  // before, for the buy panel's ladders (see TickerNeedsOptions.classes).
  const trackerFeedIds = useMemo(
    () => [
      ...heldKalshiTickers,
      ...upOddsRefs.poly.keys(),
      ...upOddsRefs.polyTokens.keys(),
      ...polyBookNeeds,
    ],
    // Content keys: the Set/Map identities churn per tick, the keys do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [heldKalshiKey, upOddsRefsKey, polyBookNeedsKey],
  );
  useTickerFeedNeeds('position-tracker', trackerFeedIds, { fold: 'view', classes: 'all' });
  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('position-tracker-poly', polyBookNeeds);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('position-tracker-poly', []);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polyBookNeedsKey]);

  const [divergenceFlashTickers, setDivergenceFlashTickers] = useState<Set<string>>(
    () => new Set(),
  );
  const divergenceFlashTimersRef = useRef<Map<string, number>>(new Map());
  const triggerDivergenceFlash = useCallback((tickers: string[]) => {
    if (tickers.length === 0) return;
    setDivergenceFlashTickers((prev) => {
      const next = new Set(prev);
      for (const t of tickers) next.add(t);
      return next;
    });
    for (const t of tickers) {
      const existing = divergenceFlashTimersRef.current.get(t);
      if (existing != null) window.clearTimeout(existing);
      const timer = window.setTimeout(() => {
        setDivergenceFlashTickers((prev) => {
          if (!prev.has(t)) return prev;
          const next = new Set(prev);
          next.delete(t);
          return next;
        });
        divergenceFlashTimersRef.current.delete(t);
      }, DIVERGENCE_FLASH_MS);
      divergenceFlashTimersRef.current.set(t, timer);
    }
  }, []);
  useEffect(
    () => () => {
      for (const timer of divergenceFlashTimersRef.current.values()) {
        window.clearTimeout(timer);
      }
      divergenceFlashTimersRef.current.clear();
    },
    [],
  );

  // Edge-detect crossings into the alert band. The latch rules (silent
  // first-sight seed, ring once on false→true, fresh-gated re-arm with
  // hysteresis, prune-by-holdings) live in the unit-tested pure helper
  // stepDivergenceLatch — see its tests for the arm/re-arm/stale/one-sided-book
  // cases. Here we just persist the latch across ticks and fan the fired set out
  // to the flash + sound.
  //
  // #233 splits this into TWO latches, because sound and visual can now arm at
  // different gaps:
  //
  //  - the CANONICAL latch stays on the fixed 2¢ / 1.5¢ contract and drives the
  //    chime, matching the badge and isDivergenceActive;
  //  - a PERSONAL VISUAL latch runs at the user's threshold with a rearm floor
  //    derived from it, and drives only the flash.
  //
  // At the 2¢ default the two are bit-identical. Both advance every tick even
  // when their output is muted, so unmuting never replays a stale edge.
  const divergenceArmedRef = useRef<Map<string, boolean>>(new Map());
  const divergenceVisualArmedRef = useRef<Map<string, boolean>>(new Map());

  // A threshold change makes every existing latch state meaningless (it was
  // computed against the old band), so drop it and let the next tick re-seed
  // SILENTLY via the helper's first-sight rule — rather than firing every
  // already-diverged ticker the moment the user drags a slider.
  const divergenceVisualThreshold =
    alertPrefs.channels.divergence.deliveryThreshold ?? DIVERGENCE_THRESHOLD_CENTS;
  useEffect(() => {
    divergenceVisualArmedRef.current = new Map();
  }, [divergenceVisualThreshold]);

  useEffect(() => {
    const p = prefsRef.current;
    const divergenceCh = p.channels.divergence;

    // Canonical latch → SOUND. Fixed 2¢, unchanged.
    const canonical = stepDivergenceLatch(
      divergenceArmedRef.current,
      divergenceByTicker,
      heldKalshiTickers,
    );
    divergenceArmedRef.current = canonical.nextArmed;

    // Personal latch → FLASH. Deliberately NOT live-game gated: the flash fires
    // for started games today and #233 preserves that asymmetry.
    const visual = stepDivergenceLatch(
      divergenceVisualArmedRef.current,
      divergenceByTicker,
      heldKalshiTickers,
      divergenceCh.deliveryThreshold ?? DIVERGENCE_THRESHOLD_CENTS,
    );
    divergenceVisualArmedRef.current = visual.nextArmed;

    if (visual.fired.length > 0 && divergenceCh.visualEnabled) {
      triggerDivergenceFlash(visual.fired);
    }

    if (canonical.fired.length > 0 && divergenceCh.soundEnabled) {
      // The CHIME is additionally gated on the live-game preference — in-play
      // books legitimately diverge, so by default a game that has started
      // flashes silently (divergenceRingTickers).
      const ringable = divergenceRingTickers(
        canonical.fired,
        p.channels.divergenceLiveGame.soundEnabled,
        (t) => useMarketStore.getState().getEventStartTimeMsByTicker(t),
        Date.now(),
      );
      if (ringable.length > 0) playDivergenceTone(divergenceCh.volume);
    }
    // heldKalshiTickers is read via the divergenceByTicker render (same set that
    // built the map); adding it to deps would just re-run on every market tick.
    // Preferences are read through prefsRef for the same reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divergenceByTicker, triggerDivergenceFlash]);

  const [queueCriticalFlashIds, setQueueCriticalFlashIds] = useState<Set<string>>(
    () => new Set(),
  );
  const queueCriticalFlashTimersRef = useRef<Map<string, number>>(new Map());

  const triggerQueueCriticalFlash = useCallback((orderIds: string[]) => {
    if (orderIds.length === 0) return;

    setQueueCriticalFlashIds((prev) => {
      const next = new Set(prev);
      for (const orderId of orderIds) next.add(orderId);
      return next;
    });

    for (const orderId of orderIds) {
      const existing = queueCriticalFlashTimersRef.current.get(orderId);
      if (existing != null) window.clearTimeout(existing);
      const timer = window.setTimeout(() => {
        setQueueCriticalFlashIds((prev) => {
          if (!prev.has(orderId)) return prev;
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
        queueCriticalFlashTimersRef.current.delete(orderId);
      }, QUEUE_CRITICAL_FLASH_MS);
      queueCriticalFlashTimersRef.current.set(orderId, timer);
    }
  }, []);

  const [fellBehindFlashIds, setFellBehindFlashIds] = useState<Set<string>>(
    () => new Set(),
  );
  const fellBehindFlashTimersRef = useRef<Map<string, number>>(new Map());

  const triggerFellBehindFlash = useCallback((orderIds: string[]) => {
    if (orderIds.length === 0) return;

    setFellBehindFlashIds((prev) => {
      const next = new Set(prev);
      for (const orderId of orderIds) next.add(orderId);
      return next;
    });

    for (const orderId of orderIds) {
      const existing = fellBehindFlashTimersRef.current.get(orderId);
      if (existing != null) window.clearTimeout(existing);
      const timer = window.setTimeout(() => {
        setFellBehindFlashIds((prev) => {
          if (!prev.has(orderId)) return prev;
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
        fellBehindFlashTimersRef.current.delete(orderId);
      }, FELL_BEHIND_FLASH_MS);
      fellBehindFlashTimersRef.current.set(orderId, timer);
    }
  }, []);

  const [queueWarningFlashIds, setQueueWarningFlashIds] = useState<Set<string>>(
    () => new Set(),
  );
  const queueWarningFlashTimersRef = useRef<Map<string, number>>(new Map());

  const triggerQueueWarningFlash = useCallback((orderIds: string[]) => {
    if (orderIds.length === 0) return;

    setQueueWarningFlashIds((prev) => {
      const next = new Set(prev);
      for (const orderId of orderIds) next.add(orderId);
      return next;
    });

    for (const orderId of orderIds) {
      const existing = queueWarningFlashTimersRef.current.get(orderId);
      if (existing != null) window.clearTimeout(existing);
      const timer = window.setTimeout(() => {
        setQueueWarningFlashIds((prev) => {
          if (!prev.has(orderId)) return prev;
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
        queueWarningFlashTimersRef.current.delete(orderId);
      }, QUEUE_WARNING_FLASH_MS);
      queueWarningFlashTimersRef.current.set(orderId, timer);
    }
  }, []);

  useEffect(() => {
    return () => {
      for (const timer of queueCriticalFlashTimersRef.current.values()) {
        window.clearTimeout(timer);
      }
      queueCriticalFlashTimersRef.current.clear();
      for (const timer of queueWarningFlashTimersRef.current.values()) {
        window.clearTimeout(timer);
      }
      queueWarningFlashTimersRef.current.clear();
      for (const timer of fellBehindFlashTimersRef.current.values()) {
        window.clearTimeout(timer);
      }
      fellBehindFlashTimersRef.current.clear();
    };
  }, []);

  // Fire one-shot alerts when an order's cumulative queue position crosses a
  // threshold from above AND a second reading agrees (#275, below). Tracking
  // the previously-seen value per orderId means a freshly observed order
  // already deep in the queue (e.g. on page load) doesn't spuriously alert —
  // only an actual downward crossing does. Critical crossings also flash the
  // card briefly so the trader can see which order triggered the sub-10k
  // sound.
  //
  // The mirror-image direction — a better-priced level forming ABOVE the order,
  // or it slipping way behind — is stepFellBehind's job; its transition and
  // re-arm rules (including the #178 queueAbove 0 -> positive cue) live in
  // lib/queueAlerts/fellBehind.
  //
  // #275: a favourable crossing is held for ONE more sample before it is
  // delivered. Kalshi's bulk queue response oscillates for an unchanged
  // resting order (#240 recorded 4,033 fresh same-ID increases in production),
  // so a lone low sample is not evidence of a real advance. The rules live in
  // lib/queueAlerts/confirmCrossing; the view only carries the state across
  // ticks. The DISPLAYED queue value is untouched and still updates instantly
  // — only the alarm waits.
  const prevQueueRef = useRef<Map<string, number>>(new Map());
  const prevQueueAboveRef = useRef<Map<string, number>>(new Map());
  const queueCandidatesRef = useRef<QueueCrossingConfirmations>(NO_QUEUE_CONFIRMATIONS);
  const pausedBookDeadlinesRef = useRef<typeof recoveringBookOrderIds>(new Map());
  const lastFreshQueueFetchedAtRef = useRef<number | null>(null);
  const lastQueueSourceKindRef = useRef<QueueSourceKind | null>(null);
  const lastQueueObservationRef = useRef(new Map<string, number>());
  // Orders whose CURRENT sample is an unconfirmed favourable outlier. Read by
  // the velocity spike machine below, which would otherwise flash on the very
  // sample this layer is refusing to trust. A ref, not state: this is written
  // on the 4 Hz market clock and must never trigger a render.
  const unconfirmedQueueOrderIdsRef = useRef<ReadonlySet<string>>(new Set());
  useEffect(() => {
    const resetQueueDeliveryState = () => {
      prevQueueRef.current = new Map();
      prevQueueAboveRef.current = new Map();
      queueCandidatesRef.current = NO_QUEUE_CONFIRMATIONS;
      pausedBookDeadlinesRef.current = new Map();
      unconfirmedQueueOrderIdsRef.current = new Set();
    };
    if (!queueSafetyFresh) {
      // Normal time between successful source reads merely pauses this state
      // machine: two genuinely fresh samples may still confirm one another.
      // A retained/failed read is different; discard its pending comparison so
      // recovery is a silent first sight rather than an outage-spanning edge.
      if (queueHardFailure) {
        resetQueueDeliveryState();
        lastFreshQueueFetchedAtRef.current = null;
        lastQueueSourceKindRef.current = null;
      }
      return;
    }
    const currentFetchedAt = queueStatus?.fetchedAt ?? null;
    const currentSourceKind = currentQueueSourceKind;
    if (
      (lastQueueSourceKindRef.current !== null &&
        lastQueueSourceKindRef.current !== currentSourceKind) ||
      (!queueStatus?.perOrder && queueSourceGapBreaksContinuity(
        lastFreshQueueFetchedAtRef.current,
        currentFetchedAt,
      ))
    ) {
      // A backgrounded/suspended client can miss the retained/failure frame.
      // Never pair a pre-sleep candidate with the first post-wake sample.
      resetQueueDeliveryState();
    }
    // A suspended tab can miss an omission frame. Break only that row's
    // comparison when its physical source observations jump across the horizon.
    const nextObservations = new Map<string, number>();
    const brokenQueueIds = new Set<string>();
    for (const [id, order] of strictOrderSliceById) {
      if (order.queuePosition == null || order.queueObservedAtMs == null) continue;
      if (queueSourceGapBreaksContinuity(lastQueueObservationRef.current.get(id) ?? null, order.queueObservedAtMs)) {
        prevQueueRef.current.delete(id);
        prevQueueAboveRef.current.delete(id);
        brokenQueueIds.add(id);
      }
      nextObservations.set(id, order.queueObservedAtMs);
    }
    if (brokenQueueIds.size) queueCandidatesRef.current = {
      warning: new Map([...queueCandidatesRef.current.warning].filter(([id]) => !brokenQueueIds.has(id))),
      critical: new Map([...queueCandidatesRef.current.critical].filter(([id]) => !brokenQueueIds.has(id))),
    };
    lastQueueObservationRef.current = nextObservations;
    lastQueueSourceKindRef.current = currentSourceKind;
    if (currentFetchedAt != null) lastFreshQueueFetchedAtRef.current = currentFetchedAt;
    // A suspended tab may miss the store's expiry render and wake directly
    // into a recovered book. Enforce the ORIGINAL monotonic deadline and exact
    // source identity before comparing; no intermediate null render is required.
    const expiredBookPauses = new Set<string>();
    const nowMono = performance.now();
    for (const [orderId, {deadline, proof}] of pausedBookDeadlinesRef.current) {
      const current = bookSourcesByOrderId.get(orderId);
      if (nowMono >= deadline || !current || current.session !== proof.session ||
        current.feed !== proof.feed || current.row.sourceGen !== proof.row.sourceGen ||
        current.row.sourceEpoch !== proof.row.sourceEpoch || current.row.sport !== proof.row.sport ||
        current.row.lane !== proof.row.lane) {
        expiredBookPauses.add(orderId);
        prevQueueRef.current.delete(orderId);
        prevQueueAboveRef.current.delete(orderId);
      }
    }
    if (expiredBookPauses.size) {
      queueCandidatesRef.current = {
        warning: new Map([...queueCandidatesRef.current.warning].filter(([id]) => !expiredBookPauses.has(id))),
        critical: new Map([...queueCandidatesRef.current.critical].filter(([id]) => !expiredBookPauses.has(id))),
      };
    }
    pausedBookDeadlinesRef.current = recoveringBookOrderIds;
    // Read through the ref, not `prefs` — this effect is driven by the market
    // feed (queueMetricsByOrderId recomputes several times a second) and must
    // not re-run merely because a preference changed. The ref is same-tab
    // fresh via subscribeAlertPreferences, so this is emit-time truth without
    // a per-tick readAlertPreferences() (a synchronous getItem + JSON.parse +
    // 18-channel normalize at feed cadence, for zero added freshness).
    const p = prefsRef.current;
    const ch = p.channels;

    const { events, nextPending } = stepConfirmedQueueDelivery(
      prevQueueRef.current,
      prevQueueAboveRef.current,
      queueCandidatesRef.current,
      queueAlertMetricsByOrderId,
      {
        warning: ch.queueWarning.deliveryThreshold ?? QUEUE_WARNING_THRESHOLD,
        critical: ch.queueCritical.deliveryThreshold ?? QUEUE_CRITICAL_THRESHOLD,
        fellBehind: ch.fellBehind.deliveryThreshold ?? WAY_BEHIND_QUEUE_ABOVE_THRESHOLD,
      },
      // The SOURCE-READING token. This effect re-runs on the 4 Hz market clock
      // (queueMetricsByOrderId folds in the live book), but a queue position
      // only moves when the relay performs a physical queue read. New relays
      // identify each row with its observedAt; fetchedAt is only the fallback
      // for older relays. Another order's read cannot confirm this one.
      // `snapshotSeq` is the legacy
      // fallback; the Map itself is not identity because ticker-set changes
      // can rebuild it without any new source evidence.
      queueSampleToken,
    );

    // Transition state advances UNCONDITIONALLY, before any gate. A muted
    // channel must still track crossings, or unmuting would replay a stale
    // edge — and the seeded maps are shared by all three cues. The pending
    // candidates advance here for the same reason: a muted channel that still
    // tracks its edges must also still track its confirmations.
    prevQueueRef.current = events.nextQueue;
    prevQueueAboveRef.current = events.nextAbove;
    queueCandidatesRef.current = nextPending;
    // Either tier awaiting confirmation means this order's current sample is
    // the one we do not trust — that is what the velocity flash must skip.
    unconfirmedQueueOrderIdsRef.current = new Set([
      ...nextPending.warning.keys(),
      ...nextPending.critical.keys(),
    ]);

    // Sound and visual are independent gates: neither can suppress the other.
    const soundId = selectQueueSoundChannel(events, ch);
    if (soundId) playToneFor(soundId, ch[soundId].volume);

    triggerQueueWarningFlash(ch.queueWarning.visualEnabled ? events.warningOrderIds : []);
    triggerQueueCriticalFlash(ch.queueCritical.visualEnabled ? events.criticalOrderIds : []);
    triggerFellBehindFlash(ch.fellBehind.visualEnabled ? events.fellBehindOrderIds : []);
  }, [
    queueAlertMetricsByOrderId,
    strictOrderSliceById,
    queueStatus?.perOrder,
    recoveringBookOrderIds,
    bookSourcesByOrderId,
    queueHardFailure,
    queueSafetyFresh,
    currentQueueSourceKind,
    queueStatus?.fetchedAt,
    queueSampleToken,
    triggerQueueWarningFlash,
    triggerQueueCriticalFlash,
    triggerFellBehindFlash,
  ]);

  // Queue-velocity spike alerts — tracker, flash map, mute set, and the
  // per-cadence feed effect all live in the hook (see useVelocitySpikeFlash).
  // Delivery settings come from the SAME single snapshot taken at the top of
  // the view — not a second useAlertPreferences() call, and not a storage read.
  // Memoized on the four primitives so a toggle elsewhere in prefs doesn't
  // churn the hook's threshold/gate effects.
  //
  // Declared AFTER the queue-delivery effect on purpose (#275). Effects run in
  // declaration order, so the confirmation layer above has already classified
  // this tick's sample by the time the hook's feed effect reads
  // `unconfirmedQueueOrderIdsRef` — the alternative is a flash decided from a
  // sample the tier next door has just refused to trust. Every consumer of the
  // hook's return values renders far below, so the move is position-only.
  const velocityDelivery = useMemo(
    () => ({
      warningVisualEnabled: alertPrefs.channels.velocityWarning.visualEnabled,
      criticalVisualEnabled: alertPrefs.channels.velocityCritical.visualEnabled,
      warningThreshold:
        alertPrefs.channels.velocityWarning.deliveryThreshold ?? DEFAULT_VELOCITY_WARN,
      criticalThreshold:
        alertPrefs.channels.velocityCritical.deliveryThreshold ?? DEFAULT_VELOCITY_ALERT,
    }),
    [
      alertPrefs.channels.velocityWarning.visualEnabled,
      alertPrefs.channels.velocityCritical.visualEnabled,
      alertPrefs.channels.velocityWarning.deliveryThreshold,
      alertPrefs.channels.velocityCritical.deliveryThreshold,
    ],
  );

  /**
   * ⚠️ #731 — deliberately fed from `orderCardVMsRaw`, NOT the stabilised
   * `orderCardVMs`.
   *
   * `useVelocitySpikeFlash`'s sampling effect documents itself as running "at
   * the 4 Hz marketVersion cadence", and #275 leans on that: when an order's
   * queue sample is still unconfirmed its flash is withheld, to be delivered
   * "on the next cadence once it is confirmed". Confirmation flips
   * independently of the view-model, so handing this hook a stabilised array
   * would delete the next cadence and with it the withheld flash — a queue
   * velocity alert that silently never fires.
   *
   * Row identity is what this ticket bounds; the alert clock is not. This hook
   * renders no rows, so its churn costs nothing measurable, and the tracker's
   * own MIN_GAP_MS (~2s) boundary still governs when history advances.
   */
  const velocitySampleVMs = useMemo(
    () =>
      orderCardVMsRaw.map((vm) => vm.venue === 'kalshi'
        ? { ...vm, queue: queueHardFailure ? null : queueAlertMetricsByOrderId.get(vm.key)?.queue ?? null }
        : vm),
    [orderCardVMsRaw, queueHardFailure, queueAlertMetricsByOrderId],
  );
  const { velocitySpikeFlash, velocityMutedIds, toggleVelocityMute } = useVelocitySpikeFlash(
    velocitySampleVMs,
    userId,
    velocityDelivery,
    unconfirmedQueueOrderIdsRef,
    queueSafetyFresh || queueHardFailure,
  );

  // A disabled cue's IN-FLIGHT flashes disappear at once. The gates above only
  // refuse NEW entries, which was invisible until #234 put these toggles one
  // click away: turning off "Queue critical / Flash" to stop a card that is
  // already flashing left it flashing for up to QUEUE_CRITICAL_FLASH_MS (24s),
  // so the switch read as broken. Derived during render, and the underlying
  // entry plus its one-shot timer are deliberately left alone — exactly the
  // shape `useVelocitySpikeFlash` already uses for the velocity tiers, so
  // re-enabling inside the window resumes the remainder of a genuinely live
  // flash rather than restarting it.
  const visibleQueueWarningFlashIds = alertPrefs.channels.queueWarning.visualEnabled
    ? queueWarningFlashIds
    : EMPTY_FLASH_IDS;
  const visibleQueueCriticalFlashIds = alertPrefs.channels.queueCritical.visualEnabled
    ? queueCriticalFlashIds
    : EMPTY_FLASH_IDS;
  const visibleFellBehindFlashIds = alertPrefs.channels.fellBehind.visualEnabled
    ? fellBehindFlashIds
    : EMPTY_FLASH_IDS;
  const visibleDivergenceFlashTickers = alertPrefs.channels.divergence.visualEnabled
    ? divergenceFlashTickers
    : EMPTY_FLASH_IDS;

  const [editPosition, setEditPosition] = useState<EditPositionData | null>(null);
  // #748 — was `useState<string | null>` cleared in a `finally`, i.e. the
  // moment the DELETE settled. That is the same mistake #241 removed from the
  // Kalshi path: it re-enabled the row while the fence still (correctly) held
  // the order, and it was invisible to every other mount. The fence is now the
  // single source, and it outlives the request by design.
  const polyCancelStates = usePolyCancelStates();
  /**
   * #748 — the cancel-control props for ONE row, both venues.
   *
   * A helper rather than four inline ternaries because the first cut passed
   * only `canceling` and silently dropped the timed-out state: the row went
   * back to an ordinary ✕ with no explanation, which is the exact thing the
   * ticket forbids. Deriving all three together makes that omission
   * impossible to repeat at a fifth call site.
   */
  const rowCancelProps = useCallback(
    (venue: TradingVenue, cancelId: string) => {
      // #1226 — a Polymarket US row's cancel state is in its facts (`vm.us`),
      // resolved by `usOrderActionProps`; nothing here applies to it.
      if (venue === 'polymarket_us') {
        return { canceling: false, cancelUnconfirmed: false, cancelTitle: undefined };
      }
      if (venue === 'kalshi') {
        return {
          canceling: isKalshiCancelPending(kalshiCancelStates[cancelId]),
          cancelUnconfirmed: false,
          cancelTitle: undefined,
        };
      }
      const row = describePolyCancelRow(polyCancelStates[cancelId]);
      return {
        canceling: row.pending,
        cancelUnconfirmed: row.unconfirmed,
        cancelTitle: row.message ?? undefined,
      };
    },
    [kalshiCancelStates, polyCancelStates],
  );

  /**
   * #731 step 2 — resolve each table row's market inputs ONCE, here, and keep
   * the identity of an unchanged row's bundle. This is what lets the row be
   * pure with respect to its props: without it the row read the store
   * imperatively and needed `marketVersion`, which changes every publication,
   * so its memo could never bail and every one of the 858 rows re-ran for one
   * moved ticker.
   *
   * `void marketVersion` for the same reason the VM builders use it — the reads
   * inside the resolver are non-reactive, so the clock is a real input even
   * though it is not referenced.
   */
  const [tableMarketIdentity] = useState(() =>
    createVmIdentityStore<OrderTableRowMarket>(),
  );
  // #1226 — each US row's facts for the table, from the same view-models the
  // cards render (`orderCardVMsRaw` is built for every displayed row).
  const usFactsByKey = useMemo(() => {
    const map = new Map<string, NonNullable<OrderCardVM['us']>>();
    for (const vm of orderCardVMsRaw) if (vm.us) map.set(vm.key, vm.us);
    return map;
  }, [orderCardVMsRaw]);
  /**
   * #731 — the row WRAPPER needs the same treatment as its market bundle.
   * `orderTableRows` rebuilds `{ kind, order, sortStart, … }` on every
   * publication even when the order inside is untouched, and a fresh wrapper
   * invalidates the row just as surely as fresh market data would. Measured:
   * with the bundle stabilised but the wrapper not, all 858 still ran.
   */
  const [tableRowIdentity] = useState(() =>
    createKeyedIdentityStore<(typeof renderedOrderTableRows)[number]>((r) => orderRowId(r)),
  );
  const stableTableRows = useMemo(
    () => tableRowIdentity.stabilize(renderedOrderTableRows),
    [tableRowIdentity, renderedOrderTableRows],
  );
  const tableRowMarkets = useMemo(() => {
    void marketVersion;
    void feeSeriesRevision;
    void feeEventRevision;
    const deps = {
      getLastTradeYesCents,
      getMakerRate: makerRateForTicker,
      getPriceVelocityM1YesCents: (ticker: string) =>
        priceVelocityWithCurrentPrice(
          ticker,
          getMarketPrice(ticker),
        ).m1.priceDelta,
      getOrderbookRaw: (ticker: string) => getOrderbookDisplay(ticker).book,
      isBookRetained: (ticker: string) => getOrderbookDisplay(ticker).retained,
      queueMetricsByOrderId: displayQueueMetricsByOrderId,
      recentFilledQtyByOrderId,
      divergenceByTicker,
      eventStateCertaintyByTicker,
      velocitySpikeFlash,
      velocityMutedIds,
      visibleDivergenceFlashTickers,
      pinnedKeySet,
      kalshiCancelStates,
      polyCancelStates,
      usFactsByKey,
      usLinkFor: (row: Extract<SortedOrderRow, { kind: 'us' }>) => usRowLink(usMarkets, row.order.target, row.accountId),
    };
    const resolved = stableTableRows.map((row) =>
      resolveOrderTableRowMarket(row, orderRowId(row), deps),
    );
    /**
     * Returned POSITIONALLY, not as a map keyed by `orderRowId`.
     *
     * All three of a poly order's id fields are optional on
     * `PolyRelayOpenOrder`, so `orderRowId` can legitimately return `''`. Two
     * such orders already collide as React keys today — harmless — but a
     * keyed lookup here would have made them collide on their MARKET DATA,
     * rendering one row's live price, queue position and divergence on the
     * other. That is a wrong number on a trading row, not a warning.
     *
     * `resolved` is `stableTableRows.map(...)` and `stabilize` emits exactly
     * one entry per input in order, so index alignment holds by construction —
     * a stronger guarantee than any id could give. (This is positional
     * ZIPPING within one pass, not persisted state keyed by index.)
     */
    return tableMarketIdentity.stabilize(resolved);
  }, [
    tableMarketIdentity,
    stableTableRows,
    marketVersion,
    getLastTradeYesCents,
    feeSeriesRevision,
    feeEventRevision,
    getMarketPrice,
    getOrderbookDisplay,
    displayQueueMetricsByOrderId,
    recentFilledQtyByOrderId,
    divergenceByTicker,
    eventStateCertaintyByTicker,
    velocitySpikeFlash,
    velocityMutedIds,
    visibleDivergenceFlashTickers,
    pinnedKeySet,
    kalshiCancelStates,
    polyCancelStates,
    usFactsByKey,
    usMarkets,
  ]);
  const [pendingCancelOrder, setPendingCancelOrder] = useState<PendingCancelOrder | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // Edit-price modal target. One modal for both venues; venue-specific replace
  // logic dispatches via handleReplaceFromModal below.
  const [editOrder, setEditOrder] = useState<EditOrderData | null>(null);

  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      // #241: no local flag and no `finally` reset — the store fences the ID
      // until an authoritative snapshot drops it. Clearing here on DELETE
      // success is exactly what re-enabled the row before removal.
      try {
        await cancelOrder(orderId);
      } catch (err) {
        console.error('Cancel order failed:', err);
        throw err instanceof Error ? err : new Error('Cancel order failed');
      }
    },
    [cancelOrder],
  );

  /**
   * #731 — the measured root of the steady-state fan-out.
   *
   * `usePolymarketRelayOrders()` returns `{ ...current, refresh, cancel,
   * replace }`, a fresh object literal on EVERY render. Closing over it made
   * this handler unstable, which made `executeCancelOrder` unstable, which made
   * `requestCancelOrder` unstable — and that is a dependency of every order
   * row's handler bundle. One object literal therefore re-rendered all 858 rows
   * on every 4 Hz publication. It was the single churning dependency; the other
   * nine were already stable.
   *
   * The latest-ref pattern (the one `useDismissibleLayer` uses) fixes it here
   * rather than at the hook, which eleven other view families consume. For a
   * click handler this is also the more correct read: the cancel runs against
   * the state at CLICK time, not the state captured when the row last rendered.
   */
  const polyOrdersStateRef = useRef(polyOrdersState);
  useEffect(() => {
    // Synced in an effect, never during render — a render-phase ref write trips
    // the compiler's `react-hooks/refs` diagnostic (see #141).
    polyOrdersStateRef.current = polyOrdersState;
  }, [polyOrdersState]);

  const handleCancelPolyOrder = useCallback(
    async (orderId: string) => {
      // No local pending flag: `cancelPolyOrderOnce` claims the ID before the
      // DELETE goes out and holds it until an authoritative snapshot drops it
      // (or the sync window elapses). Clearing anything here would re-enable
      // the row ahead of the fence — the #241 defect, one venue over.
      const out = await polyOrdersStateRef.current.cancel(orderId);
      if (!out.ok) {
        console.error('Cancel poly order failed:', out.error);
        throw new Error(out.error);
      }
    },
    [],
  );

  // In-page buy window (Joshua, Jun 11 standup: "click on a game [on the
  // position tracker], it brings up the buying window on the right").
  // Stores the resolved game's KEY, not the row — the live row is re-derived
  // from marketStore every render (below) so panel prices tick; the snapshot
  // taken at click time anchors identity if the live row's key reshapes.
  // It is never used as an executable fallback when that row disappears.
  const [buyPanel, setBuyPanel] = useState<{
    gameKey: string;
    snapshot: GameRow;
    intent: TradeIntent;
    ticker: string;
    href: string | null;
    /** A US row's panel: re-derives it from the live board (usPanelTarget). */
    usLive?: () => GameRow | null;
  } | null>(null);
  // Page-level YES/NO context for the panel's pills — seeded from the
  // clicked position's held side, flipped by clicks inside the panel.
  const [panelMarketSide, setPanelMarketSide] = useState<'yes' | 'no'>('no');
  const [unavailableBook, setUnavailableBook] = useState<{ ticker: string; href: string | null; side?: 'yes' | 'no' } | null>(null);
  // #1184 — a held Kalshi position whose game the live store does not resolve
  // opens its read-only account record (from the notice's "View record" or the
  // game chip) instead of an empty Game Detail. Keyed `side:ticker` and
  // resolved from the CURRENT positions read each render; the record closes
  // when the position leaves it or the viewer changes (useAccountRecord).
  const positionRecord = useAccountRecord();
  const openPositionRecord = positionRecord.open;
  const recordPosition = useOpenRecordRow(positionRecord, (key) => {
    const at = key.indexOf(':');
    return heldKalshiPosition(key.slice(at + 1), key.slice(0, at) as 'yes' | 'no');
  });
  useEffect(() => {
    const clearSelection = () => { setBuyPanel(null); setUnavailableBook(null); };
    const offAuth = useAuthStore.subscribe((next, previous) => {
      if (next.user?.id !== previous.user?.id || next.user?.orgId !== previous.user?.orgId
        || next.user?.role !== previous.user?.role) clearSelection();
    });
    const offOrg = useOrgScope.subscribe((next, previous) => {
      if (next.selectedOrgId !== previous.selectedOrgId) clearSelection();
    });
    const offAccount = useAccountStore.subscribe((next, previous) => {
      if (next.keyMode !== previous.keyMode || (next.status === 'disconnected' && previous.status !== 'disconnected')) clearSelection();
    });
    return () => { offAuth(); offOrg(); offAccount(); };
  }, []);

  // Dash-wide click contract (Dan, Jul 2026): clicking a game/event row or
  // card opens the ORDER SCREEN (in-page buy panel on the right); the
  // GameDetailLink icon beside the market label is the separate, explicit
  // path to the /game/... detail page. Unavailable books get a visible notice
  // with that separate link, while the previous execution panel is closed.
  const openOrderPanel = useCallback(
    (ticker: string, side: 'yes' | 'no' | null | undefined) => {
      if (!side) {
        setBuyPanel(null);
        setUnavailableBook({ ticker, href: null });
        return;
      }
      const { panel, href } = positionClickTarget(ticker, side);
      if (panel) {
        setUnavailableBook(null);
        setBuyPanel({ gameKey: panel.game.key, snapshot: panel.game, intent: panel.intent, ticker, href });
        // Totals resolve the Over/Under side themselves (Poly reads it from the
        // held token); moneyline/spread pass the held side straight through.
        setPanelMarketSide(panel.intent.initialOrderSide ?? side);
        return;
      }
      setBuyPanel(null);
      setUnavailableBook({ ticker, href, side });
    },
    [],
  );

  // #1006 — a US row: the same docked panel, opened on its US tab for the row's
  // exact outcome and account (UsTrackerSection resolves the game exactly).
  const openUsOrderPanel = useCallback<UsOpenPanel>(({ game, intent, live, marketSlug, href }) => {
    setUnavailableBook(null);
    setBuyPanel({ gameKey: game.key, snapshot: game, intent, ticker: marketSlug, href, usLive: live });
    setPanelMarketSide(intent.initialOrderSide ?? 'yes');
  }, []);
  // A US row the panel cannot open on: the same notice as an international
  // order's, naming the row's market and its exact game page, if any.
  const showUsUnavailable = useCallback<UsUnavailablePanel>(({ marketSlug, href }) => {
    setBuyPanel(null);
    if (href?.startsWith('/positions?')) {
      setUnavailableBook(null);
      navigate(href);
    } else setUnavailableBook({ ticker: marketSlug, href });
  }, [navigate]);
  // #1226 — the stable handlers every US row calls (`useUsRowDispatch`).
  const usDispatch = useUsRowDispatch(usRead, usMarkets, openUsOrderPanel, showUsUnavailable);
  /** A row's game link: a US row's from the catalogue, a K/P row's from the store. */
  const positionLegacyHref = useCallback((vm: PositionCardVM): string | null => vm.us
    ? usRowLink(usMarkets, { marketSlug: vm.us.marketSlug, outcome: vm.us.outcome }, vm.us.accountId).href
    : gameDetailHref(vm.ticker, vm.side), [usMarkets]);
  const orderLegacyHref = useCallback((vm: OrderCardVM): string | null => vm.us
    ? usRowLink(usMarkets, { marketSlug: vm.ticker, outcome: vm.side ?? 'yes' }, vm.us.accountId).href
    : vm.side ? gameDetailHref(vm.ticker, vm.side) : null, [usMarkets]);
  /** A position row's click: the US tab for a US row, the K/P panel otherwise. */
  const openPositionRow = useCallback((vm: PositionCardVM) => {
    if (vm.us) usDispatch.open({ accountId: vm.us.accountId, marketSlug: vm.us.marketSlug, outcome: vm.us.outcome });
    else openOrderPanel(vm.ticker, vm.side);
  }, [usDispatch, openOrderPanel]);

  // Poly sibling of openOrderPanel — poly open orders carry only the CLOB
  // token id. Same panel-first / detail-nav-fallback contract. Totals orders
  // (issue #96) resolve to the exact totals line first; a miss falls back to the
  // moneyline token resolver, then to detail-nav.
  const openPolyOrderPanel = useCallback(
    (assetId: string, fallbackHref: string | null) => {
      const target =
        resolvePolyTotalOrderTarget(assetId)
        ?? resolvePolyPropOrderTarget(assetId)
        ?? resolvePolyOrderPanelTarget(assetId);
      if (target) {
        setUnavailableBook(null);
        setBuyPanel({ gameKey: target.game.key, snapshot: target.game, intent: target.intent, ticker: assetId, href: fallbackHref });
        setPanelMarketSide(target.intent.initialOrderSide ?? 'yes');
        return;
      }
      setBuyPanel(null);
      setUnavailableBook({ ticker: assetId, href: fallbackHref });
    },
    [],
  );

  // Bound form of orderCardOpenHandler — every Open Orders render path (section
  // card, section table row, pinned card, pinned compact, pinned table row)
  // routes its click through this one call.
  const orderCardOpen = useCallback(
    (vm: Pick<OrderCardVM, 'venue' | 'ticker' | 'side'>, href: string | null) =>
      orderCardOpenHandler(vm, href, openOrderPanel, openPolyOrderPanel),
    [openOrderPanel, openPolyOrderPanel],
  );
  /** #1226 — an order row's click: the US tab for a US row, `orderCardOpen` otherwise. */
  const openOrderRow = useCallback(
    (vm: Pick<OrderCardVM, 'venue' | 'ticker' | 'side' | 'us'>, href: string | null) => vm.us
      ? () => usDispatch.open({ accountId: vm.us!.accountId, marketSlug: vm.ticker, outcome: vm.side ?? 'yes' })
      : orderCardOpen(vm, href),
    [orderCardOpen, usDispatch],
  );
  // The serving answer the US Close rule reads (re-derived when it lands).
  const usServed = useCallback((slug: string) => usMarkets.serving.get(slug), [usMarkets.serving]);

  // Live row for the open panel — same re-derive-per-render pattern the
  // dashboard uses for expandedGame, so prices/books tick with the feed.
  // feedVersion IS the page's throttled 4 Hz clock (was a second raw-counter
  // subscription); the buy panel's one-sport getGames sweep runs ≤4×/s.
  const feedVersion = marketVersion;
  // Prop panels: ONE ladder rebuild per tick, shared by the panel's game row and
  // its threshold strip. Deriving them separately walked every MLB frame twice
  // (7.3ms/tick, 29ms/s at 4Hz on a 5,040-market slate) for identical data.
  // Null for every non-prop panel, so nothing else pays for this.
  const propPanelLive = useMemo(() => {
    void feedVersion;
    return buyPanel && hasPropSynthKey(buyPanel.gameKey)
      ? livePropPanel(buyPanel.gameKey, buyPanel.intent.venue === 'kalshi' ? buyPanel.snapshot.kalshi?.away.marketTicker : undefined)
      : null;
  }, [buyPanel, feedVersion]);
  const spreadPanelLive = useMemo(() => {
    void feedVersion;
    return buyPanel && !buyPanel.intent.polymarketUs && buyPanel.gameKey.includes('|spread|')
      ? liveSpreadPanel(buyPanel.gameKey, buyPanel.snapshot.sport)
      : null;
  }, [buyPanel, feedVersion]);
  const multiPanelLive = useMemo(() => {
    void feedVersion;
    return buyPanel && !buyPanel.intent.polymarketUs && buyPanel.gameKey.includes('|multi|')
      ? resolveMultiOutcomePanelTarget(buyPanel.ticker, buyPanel.intent.initialOrderSide ?? 'yes')
      : null;
  }, [buyPanel, feedVersion]);
  const buyPanelGame = useMemo(() => {
    if (!buyPanel) return null;
    void feedVersion;
    // #1006/#1030 — a US row's panel trades no K/P book, and its key may name
    // a US-only contract (`|1H-ml`, `|total|pmus-<slug>`, `|us-player|…`,
    // `|us-game-prop|…`) that no K/P branch below can find. Re-derive it from
    // the live board row with the builder that opened it; a game that left the
    // board, or is no longer this exact game and slot, closes the panel. Never
    // a K/P total line, the moneyline row in its place, or the click-time copy.
    if (buyPanel.intent.polymarketUs) {
      return exactLiveCardGame(buyPanel.usLive?.(), buyPanel.snapshot, buyPanel.intent);
    }
    if (buyPanel.gameKey.includes('|multi|')) {
      return exactLiveCardGame(multiPanelLive?.game, buyPanel.snapshot, buyPanel.intent);
    }
    // One sport only — this runs on every RAF-coalesced feed tick while
    // the panel is open, and a full all-sports getGames sweep is ~10ms+
    // against a session-grown _markets map (review catch). Key match is
    // normalized because per-day row keys flip shape when hydra tags
    // canonicalGameKey late — exact-match would strand the panel on the
    // click-time snapshot forever.
    // Advance rows (knockout "To Advance" / stamped cup-winner legs, #98) are
    // keyed `<gameKeyV2>|advance` and never appear in getGames — re-derive
    // them from their own getter so the panel's books keep ticking instead of
    // freezing on the click-time snapshot.
    if (buyPanel.gameKey.endsWith('|advance')) {
      const adv = useMarketStore
        .getState()
        .getAdvanceGame(buyPanel.gameKey.slice(0, -'|advance'.length));
      return exactLiveCardGame(adv, buyPanel.snapshot, buyPanel.intent);
    }
    // Totals synth rows (issue #96) are keyed `<eventKey>|total|<lineKey>` and
    // never appear in getGames — re-derive from the live event + line so the
    // order ticket's book keeps ticking instead of freezing at click time
    // (mirrors GameDetail's findLiveSynthGameRow). Last segment is the lineKey.
    if (buyPanel.gameKey.includes('|total|')) {
      const lineKey = buyPanel.gameKey.slice(buyPanel.gameKey.lastIndexOf('|') + 1);
      const hit = findTotalEventLine((line) => totalLineKey(line) === lineKey);
      return exactLiveCardGame(hit ? synthesizeFromTotal(hit.event, hit.event.totals[hit.idx]) : null, buyPanel.snapshot, buyPanel.intent);
    }
    // Prop rung synths (#184) are keyed `<gameKeyV2>|prop-<stat>-<player>-<n>`
    // and never appear in getGames either — re-derive from the live ladder for
    // the same reason: the ticket's book must keep ticking, not freeze at click.
    if (hasPropSynthKey(buyPanel.gameKey)) {
      return exactLiveCardGame(propPanelLive?.game, buyPanel.snapshot, buyPanel.intent);
    }
    if (buyPanel.gameKey.includes('|spread|')) {
      return exactLiveCardGame(spreadPanelLive?.game, buyPanel.snapshot, buyPanel.intent);
    }
    const want = normalizeGameKey(buyPanel.gameKey);
    const games = useMarketStore
      .getState()
      .getGames(buyPanel.snapshot.sport, 'moneyline', { dateFilter: 'all', statusFilter: 'all' });
    // A normalized key may reshape, but it drops gameNo. Require the original
    // execution identity even on an exact key so neither a doubleheader sibling
    // nor a reclassified row can replace the selected contract.
    const hit =
      games.find((g) => g.key === buyPanel.gameKey && exactLiveCardGame(g, buyPanel.snapshot, buyPanel.intent)) ??
      games.find((g) => normalizeGameKey(g.key) === want && exactLiveCardGame(g, buyPanel.snapshot, buyPanel.intent));
    return hit ?? null;
  }, [buyPanel, feedVersion, propPanelLive, spreadPanelLive, multiPanelLive]);
  const cardBookNotice = unavailableBook ?? (buyPanel && !buyPanelGame ? { ticker: buyPanel.ticker, href: buyPanel.href } : null);
  const noticePosition = cardBookNotice
    ? heldKalshiPosition(cardBookNotice.ticker, 'side' in cardBookNotice ? cardBookNotice.side : undefined)
    : null;

  // Totals chip-strip context for the open panel, DERIVED (not stored) so it
  // rebuilds on every feed tick — this is what keeps an alternate line's chip
  // price and getGameForIdx book live instead of frozen at click time (review:
  // Brendan). Rebuilding is safe for the panel's own chip selection because
  // selectedIdx stays the held line's constant index (GameDetailPanel keys
  // panelChipIdx on that value, not on betLineContext identity). Null for
  // moneyline panels, which stay in team-selector mode. Spreads use their own strip.
  const buyPanelBetLineContext = useMemo(() => {
    void feedVersion;
    if (!buyPanel) return null;
    // Spread caption, totals strip or prop threshold strip — one shape per key.
    // so the order is arbitrary; both are null for a plain moneyline panel. The
    // prop side reuses the shared per-tick rebuild above rather than repeating it.
    return multiPanelLive?.context ?? spreadPanelLive?.context ?? liveTotalBetLineContext(buyPanel.gameKey) ?? propPanelLive?.betLineContext ?? null;
  }, [buyPanel, feedVersion, propPanelLive, spreadPanelLive, multiPanelLive]);

  const executeCancelOrder = useCallback(
    async (target: PendingCancelOrder) => {
      setCancelError(null);
      try {
        if (target.venue === 'kalshi') {
          await handleCancelOrder(target.orderId);
        } else if (target.venue === 'polymarket') {
          await handleCancelPolyOrder(target.orderId);
        } else if (target.venue === 'polymarket_us') {
          // #1226 — a US row's cancel goes through its account reader
          // (`usDispatch.run`), never this path: nothing to send here.
          throw new Error('Polymarket US orders cancel from their row actions');
        } else {
          unsupportedVenue(target.venue, 'Cancel');
        }
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'Cancel failed. Check open orders and try again.';
        setCancelError(message);
      }
    },
    [handleCancelOrder, handleCancelPolyOrder],
  );

  // Bulk cancel sweeps. Two scopes share one engine:
  //   - Cancel ALL (Kevin, Jun 2: end-of-day sign-off) — sweeps the
  //     UNFILTERED order lists; sport filter chips must never silently
  //     shrink an "ALL" sweep (orders left resting overnight).
  //   - Cancel VISIBLE (Dan, Jul 8) — sweeps exactly `sortedOrders`, the
  //     list the table/cards are rendering under the section's sport +
  //     market-family filters. Scoped sibling for "clear this slate but
  //     keep my other sports resting".
  // Per-order endpoints on both venues, so a trader on shared org keys can
  // never nuke teammates' orders (the relay's poly DELETE-all is
  // org-credential-wide; deliberately not used here).
  //
  // #272 replaced the inline raw sweep that used to live here. It called
  // `apiFetch` directly to avoid a refresh-per-order storm, and in doing so it
  // ALSO bypassed #241's cancel fence: a sweep could re-DELETE an ID a row click
  // already owned, and it re-read the live `openOrders`/`sortedOrders` at
  // confirm time instead of the set the trader approved. Both scopes now freeze
  // a snapshot when their modal opens and run it through the one shared
  // executor, which keeps the single-refresh-per-venue property AND the fence.
  // Always confirms, regardless of the per-order confirmOrderCancel preference.
  const [pendingBulkCancel, setPendingBulkCancel] = useState<FrozenCancelSnapshot | null>(null);
  // #980 — how many of a frozen Cancel VISIBLE snapshot's targets are rows
  // shown only because their market family is still pending. Frozen with the
  // snapshot; 0 for every other scope.
  const [pendingBulkUnclassified, setPendingBulkUnclassified] = useState(0);
  // #1060: venues whose orders were not read when the confirmation opened —
  // their orders are not in it, and the confirmation says so.
  const [pendingBulkUnread, setPendingBulkUnread] = useState<string[]>([]);
  const unreadOrderVenues = useCallback(() => [
    ...(kalshiOrdersPending ? ['Kalshi orders were still being read'] : kalshiOrdersFailed ? ['Kalshi orders could not be read'] : []),
    ...(polyOrdersPending ? ['Polymarket orders were still being read']
      : polyOrdersState.status === 'error' ? ['Polymarket orders could not be read'] : []),
  ], [kalshiOrdersPending, kalshiOrdersFailed, polyOrdersPending, polyOrdersState.status]);
  const [bulkCancelReport, setBulkCancelReport] = useState<BulkCancelReport | null>(null);
  const [cancelingAll, setCancelingAll] = useState(false);
  const bulkRunningRef = useRef(false);
  const pinnedOpenKalshiCount = openOrders.filter((o) => pinnedKalshiIds.has(o.orderId)).length;
  const cancelAllTargetCount =
    openOrders.length -
    pinnedOpenKalshiCount +
    (polyOrdersState.unavailableCode ? 0 : polyOrdersState.orders.length);

  const runBulkCancel = useCallback(
    async (snapshot: FrozenCancelSnapshot) => {
      if (bulkRunningRef.current) {
        setCancelError('A bulk cancellation is already running. No second attempt was submitted.');
        return;
      }
      bulkRunningRef.current = true;
      setCancelError(null);
      setCancelingAll(true);
      try {
        const report = await executeBulkOrderCancel(snapshot, {
          // refreshAfter:false — the executor owns the single per-venue refresh.
          cancelKalshiOrders: (ids) => cancelOrders(ids, { refreshAfter: false }),
          requestPolymarketCancel: cancelPolyOrderViaRelay,
          refreshKalshi: () => refreshOrders(),
          refreshPolymarket: async () => {
            // The executor cancels via the relay DIRECTLY (not the hook's
            // cancel), so it must invalidate the shared snapshot itself —
            // otherwise the forced refresh coalesces onto a same-generation
            // pre-sweep poll and re-shows everything just cancelled.
            invalidatePolyOrdersShare();
            await refreshPolyOrders(true);
          },
          recordAttempt: recordBulkCancelAttempt,
        });
        setBulkCancelReport(report);
        if (snapshot.targets.length === 0) {
          setCancelError(
            snapshot.skippedPinned.length > 0
              ? describeAllPinned(snapshot)
              : 'No open orders were captured for this cancellation attempt.',
          );
        }
      } finally {
        bulkRunningRef.current = false;
        setCancelingAll(false);
      }
    },
    [cancelOrders, refreshOrders, refreshPolyOrders],
  );

  /** Cancel ALL (Kevin, Jun 2: end-of-day sign-off) — the UNFILTERED lists.
   *  Sport filter chips must never silently shrink an "ALL" sweep. */
  const { prepare: prepareUsBulk, discard: discardUsBulk } = usBulk;
  const openCancelAll = useCallback(() => {
    setBulkCancelReport(null);
    setPendingBulkUnclassified(0);
    setPendingBulkUnread(unreadOrderVenues());
    prepareUsBulk();
    setPendingBulkCancel(
      freezeCancelSnapshot({
        scope: 'all',
        label: 'every open Kalshi and Polymarket order',
        targets: buildAllCancelTargets({
          kalshiOrders: openOrders,
          polyOrders: polyOrdersState.orders,
          polyUnavailable: !!polyOrdersState.unavailableCode,
        }),
        pinnedKalshiOrderIds: pinnedKalshiIds,
      }),
    );
  }, [openOrders, polyOrdersState.orders, polyOrdersState.unavailableCode, pinnedKalshiIds, prepareUsBulk, unreadOrderVenues]);

  /** Cancel VISIBLE (Dan, Jul 8) — exactly `sortedOrders`, the list the
   *  table/cards render under the section's sport + market-family filters.
   *  Frozen at click time so a filter change mid-confirm cannot grow it. */
  // One figure over every venue (the lists below are one set). "+" after it
  // means more is still being counted: the Kalshi/Polymarket read has not
  // answered, or a US account was not read whole; the hover says which. With
  // nothing counted yet the figure is a dash.
  const cancelAllLabel = ((): { count: string; more: boolean; title: string } => {
    const kpPending = ordersPending && cancelAllTargetCount === 0;
    const us = usCancel.kind === 'count' ? usCancel.count : 0;
    const usUnknown = usCancel.kind === 'unknown';
    const known = (kpPending ? 0 : cancelAllTargetCount) + us;
    const more = kpPending || usUnknown;
    const title = usUnknown
      ? 'Some Polymarket US orders could not be counted yet; the confirmation checks'
      : usCancel.kind === 'count'
        ? `includes ${usCancel.count} Polymarket US (${usCancelSource})${kpPending ? '; Kalshi and Polymarket orders are still being read' : ''}`
        : 'Kalshi and Polymarket orders are still being read';
    return { count: known === 0 && more ? '—' : String(known), more: known > 0 && more, title };
  })();
  const { dismissReport: dismissUsBulkReport, prepareVisible: prepareVisibleUsBulk } = usBulk;
  // #1226 — the US orders rendered in the shared list under the chips, by
  // account, for Cancel visible (frozen when its confirmation opens). Only a
  // first cancel counts here, which does not depend on the clock.
  const usShown = useMemo(() => usShownOrdersOf(sortedOrders.filter((r) => r.kind === 'us'), usViews), [sortedOrders, usViews]);
  const usVisibleCount = usShown.length ? usVisibleInventoryAll(usShown, 0).inventory.targets.length : 0;
  const openCancelVisible = useCallback(() => {
    setBulkCancelReport(null);
    dismissUsBulkReport();
    // #1006 — the US orders shown under the chips, frozen now.
    prepareVisibleUsBulk(usShown);
    const snapshot = freezeCancelSnapshot({
      scope: 'visible',
      label: 'the orders currently visible',
      // Read from the RENDERED rows at click time; the freeze then pins them,
      // so a filter change while the confirmation is open cannot grow the set.
      targets: buildVisibleCancelTargets(sortedOrders as never),
      pinnedKalshiOrderIds: pinnedKalshiIds,
    });
    // #980: pending rows pass every family chip, so a "cancel my props" sweep
    // can include orders of unknown family. Name them in the confirmation.
    const unclassifiedKeys = new Set(
      buildVisibleCancelTargets(sortedOrders.filter((r) => r.familyPending) as never).map((t) => t.key),
    );
    setPendingBulkUnclassified(snapshot.targets.filter((t) => unclassifiedKeys.has(t.key)).length);
    setPendingBulkUnread(unreadOrderVenues());
    setPendingBulkCancel(snapshot);
  }, [sortedOrders, pinnedKalshiIds, prepareVisibleUsBulk, dismissUsBulkReport, usShown, unreadOrderVenues]);

  const { frozen: usBulkFrozen, run: runUsBulk, reading: usBulkReading, skipReading: skipUsBulkReading } = usBulk;
  // US targets frozen for this confirmation (Cancel all's read, or Cancel
  // visible's shown rows); none while the Cancel all read is still running.
  const bulkConfirmCount = (pendingBulkCancel?.targets.length ?? 0) + (usBulkFrozen?.targets.length ?? 0);
  const confirmBulkCancel = useCallback(() => {
    const snapshot = pendingBulkCancel;
    const us = usBulkFrozen;
    // Confirmed while US was still reading: K/P goes now; US is left out and
    // the report says so, instead of the K/P sweep waiting on a slow read.
    if (usBulkReading) skipUsBulkReading();
    // Closed before the first await — the confirm button cannot fire twice.
    setPendingBulkCancel(null);
    if (!snapshot) return;
    const usTargets = us?.targets.length ?? 0;
    void (async () => {
      try {
        // A US-only sweep skips the K/P runner, which would otherwise report
        // "No open orders were captured" for a sweep that has US orders.
        // With US orders to send, an all-pinned K/P side is not "Nothing was
        // sent": its pins were named in the confirmation already.
        if (snapshot.targets.length > 0 || usTargets === 0) {
          await runBulkCancel(snapshot);
        }
      } finally {
        // The trader confirmed both halves: a K/P failure never drops US.
        if (us && usTargets > 0) await runUsBulk(us);
      }
    })();
  }, [pendingBulkCancel, runBulkCancel, usBulkFrozen, runUsBulk, usBulkReading, skipUsBulkReading]);

  const retryFailedBulkCancels = useCallback(() => {
    const failed = bulkCancelReport?.failed ?? [];
    if (failed.length === 0 || cancelingAll) return;
    // Failures ONLY — resending a submitted ID re-DELETEs a cancellation that
    // already worked, and it fails misleadingly (#241).
    const snapshot = freezeCancelSnapshot({
      scope: bulkCancelReport?.snapshot.scope ?? 'all',
      label: 'the orders that failed to cancel',
      targets: failed.map((row) => row.target),
      // Pinned since the first attempt → still never bulk-cancelled.
      pinnedKalshiOrderIds: pinnedKalshiIds,
    });
    // Every failure has been pinned since: nothing to confirm (#943 review).
    if (snapshot.targets.length === 0) {
      setCancelError(describeAllPinned(snapshot));
      return;
    }
    setPendingBulkUnclassified(0);
    setPendingBulkUnread([]);
    setPendingBulkCancel(snapshot);
  }, [bulkCancelReport, cancelingAll, pinnedKalshiIds]);
  // The scoped button only earns its slot when the filters are actually
  // narrowing the list — otherwise it duplicates Cancel all.
  // Both counts exclude pinned orders (#943) — the sets the two sweeps send.
  const visibleCancelTargetCount = useMemo(
    () =>
      buildVisibleCancelTargets(sortedOrders as never).filter(
        (t) => !(t.venue === 'kalshi' && pinnedKalshiIds.has(t.orderId)),
      ).length,
    [sortedOrders, pinnedKalshiIds],
  );
  const orderFiltersActive = orderSportFilter.size > 0 || orderKindFilter.size > 0;
  // US orders visible under the chips earn Cancel visible too (#1006).
  const orderFiltersNarrowing = shouldOfferCancelVisible({
    filtersActive: orderFiltersActive,
    visibleCount: visibleCancelTargetCount,
    allCount: cancelAllTargetCount,
  }) || (orderFiltersActive && usVisibleCount > 0);

  // Polymarket position close — verify-and-retry FAK sweep (parity with
  // the Kalshi close in EditPositionModal). Confirmed per-position; the
  // outcome (flat / residual / unverified / error) lands in a toast.
  // closingPolyRef is the synchronous re-entry guard: ConfirmModal fires
  // onConfirm+onClose in one tick and state updates are async, so a
  // double-confirm would otherwise launch two concurrent full-size sweeps
  // against the org's SHARED funder account (a real oversell, not a
  // bounce). closedPolyPositionFences suppresses verified-flat rows locally
  // until the fills poller catches up — the ledger lags the CLOB, and a stale
  // row with a live Close button is a double-sell invitation. buyBaseline is
  // the row's lifetime totalBoughtCount captured at Close-button click (the
  // same instant as qty/label): looking the row up after the async close can
  // observe a later ledger generation and record the re-buy count as the old
  // baseline, recreating #429's indefinite suppression.
  const [pendingPolyClose, setPendingPolyClose] = useState<{
    conditionId: string;
    side: 'yes' | 'no';
    qty: number;
    label: string;
    buyBaseline: number | null;
  } | null>(null);
  const [closingPolyKey, setClosingPolyKey] = useState<string | null>(null);
  const closingPolyRef = useRef(false);
  const [polyCloseMsg, setPolyCloseMsg] = useState<{ ok: boolean; msg: string } | null>(null);
  const polyCloseToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handlePolyClose = useCallback(async () => {
    if (!pendingPolyClose || closingPolyRef.current) return;
    const { conditionId, side, qty, label, buyBaseline } = pendingPolyClose;
    // A merge or its reconciliation may change inventory while this dialog is
    // open. Never submit the quantity captured before that newer reading.
    const current = polyPositionsState.positions.find((p) => p.ticker === conditionId && p.side === side);
    if (!current || current.netCount !== qty || ((current.mergeConflictCount ?? 0) > 0 || (current.splitConflictCount ?? 0) > 0)) {
      setPendingPolyClose(null);
      setPolyCloseMsg({ ok: false, msg: 'Position quantity changed or accounting is unavailable. Review the current position before closing.' });
      return;
    }
    closingPolyRef.current = true;
    const rowKey = `${conditionId}-${side}`;
    setPendingPolyClose(null);
    setClosingPolyKey(rowKey);
    setPolyCloseMsg(null);
    if (polyCloseToastTimerRef.current) clearTimeout(polyCloseToastTimerRef.current);
    try {
      const out = await closePolyPosition({ conditionId, side, quantity: qty });
      setPolyCloseMsg({ ok: out.ok, msg: `${label}: ${out.msg}` });
      if (out.ok && out.residual === 0) {
        setClosedPolyPositionFences((previous) => {
          const next = new Map(previous);
          next.set(rowKey, buyBaseline);
          return next;
        });
      }
      if (out.autoDismiss) {
        polyCloseToastTimerRef.current = setTimeout(() => setPolyCloseMsg(null), 6_000);
      }
    } finally {
      closingPolyRef.current = false;
      setClosingPolyKey(null);
      void refreshPolyPositions();
      // The close mutates via the relay directly — invalidate the shared
      // snapshot so the forced refresh reads post-close truth.
      invalidatePolyOrdersShare();
      void refreshPolyOrders(true);
    }
  }, [pendingPolyClose, polyPositionsState.positions, refreshPolyPositions, refreshPolyOrders]);

  // #1006: every Open Positions layout (table, card, compact, mini, pinned)
  // offers a Polymarket position the same Close through these two. The
  // request reads the position at click time (same instant as qty, label and
  // buy baseline — see pendingPolyClose) and is stable, so memoized rows keep
  // comparing equal.
  const polyOpenByKeyRef = useRef(new Map<string, (typeof polyOpenPositions)[number]>());
  useLayoutEffect(() => {
    polyOpenByKeyRef.current = new Map(polyOpenPositions.map((p) => [`poly-pos-${p.ticker}-${p.side}`, p]));
  }, [polyOpenPositions]);
  const requestPolyClose = useCallback((key: string) => {
    const p = polyOpenByKeyRef.current.get(key);
    if (!p) return;
    const target = resolvePolyCloseTarget(p.ticker, p.side);
    setPendingPolyClose({
      conditionId: p.ticker,
      side: p.side,
      qty: p.netCount,
      label: polyPositionLabel(p.ticker, target ? describePolyOrderCached(target.tokenId) : null),
      buyBaseline: polyBuyBaseline(p.totalBoughtCount),
    });
  }, []);
  const polyCloseStateFor = (conditionId: string, side: 'yes' | 'no', qty: number) =>
    polyCloseState(qty, !!resolvePolyCloseTarget(conditionId, side), closingPolyKey, `${conditionId}-${side}`);
  /** A card's Close: a Polymarket position's FAK sweep, a Polymarket US
   *  holding's US Close window (#1226); Kalshi closes through Edit. */
  const closeFor = (vm: PositionCardVM) =>
    vm.us ? (
      <UsCloseControl facts={vm.us} onClose={() => usDispatch.close(vm.us!)} />
    ) : vm.venue === 'polymarket' ? (
      <PolyCloseControl
        state={polyCloseStateFor(vm.ticker, vm.side, vm.qty)}
        onClose={() => requestPolyClose(vm.key)}
      />
    ) : undefined;

  const requestCancelOrder = useCallback(
    (target: PendingCancelOrder) => {
      setCancelError(null);
      if (confirmOrderCancel) {
        setPendingCancelOrder(target);
        return;
      }
      void executeCancelOrder(target);
    },
    [confirmOrderCancel, executeCancelOrder],
  );

  // Modal dispatch — single onReplace callback that the EditOrderPriceModal
  // calls when the trader hits "Replace order". Picks the right replace
  // implementation based on the order's venue + passes the new qty through.
  // Kalshi's accountStore.replaceOrder smart-routes by default (same-price
  // qty-shrink through /decrease for queue preservation); the modal can
  // opt out via forceCancelReplace when the trader explicitly wants to
  // re-queue. Polymarket is always cancel + place (the forceCancelReplace
  // flag is meaningless there since there's no smart route to override —
  // dropped silently). Throws on failure so the modal surfaces the error
  // inline; resolves on success so the modal closes via setEditOrder(null).
  //
  // Pin follow-through (#188): a cancel+place edit mints a new venue id, so
  // a pinned card keyed on the old id would strand as a tombstone. On a
  // success that resolved with a different id, transfer the pin to the new
  // key BEFORE closing the modal; same-id successes and failures never touch
  // pin state (a failed place after cancel leaves the old pin to tombstone —
  // that's the required failure behavior, the order really is gone).
  const handleReplaceFromModal = useCallback(
    async (
      newPriceCents: number,
      newQuantity: number,
      options?: { forceCancelReplace?: boolean },
    ) => {
      if (!editOrder) throw new Error('No order selected');
      const { venue, orderId: previousOrderId } = editOrder;
      const edit = async (): Promise<{ orderId: string }> => {
        if (venue === 'kalshi') {
          return replaceOrder(
            previousOrderId,
            newPriceCents,
            newQuantity,
            options?.forceCancelReplace,
          );
        }
        if (venue !== 'polymarket') return unsupportedVenue(venue, 'Edit');
        const out = await polyOrdersState.replace(previousOrderId, newPriceCents, {
          tickSize: editOrder.tickSize,
          negRisk: editOrder.negRisk,
          nextQuantity: newQuantity,
        });
        if (!out.ok) throw new Error(out.error);
        return { orderId: out.orderId };
      };
      await editOrderKeepingPin(venue, previousOrderId, edit, pinViewerId);
      setEditOrder(null);
    },
    [editOrder, replaceOrder, polyOrdersState, pinViewerId],
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        refreshPositions(),
        refreshOrders(),
        refreshPnl(),
        // Forced: the user's explicit Refresh must hit the network, not the
        // shared freshness window.
        refreshPolyOrders(true),
        refreshPolyPositions(),
      ]);
      setDisplayTotalPnl(latestVisibleTotalPnlRef.current);
      setPnlCardAnchorMs(Date.now());
    } finally {
      setRefreshing(false);
    }
  }, [
    refreshPositions,
    refreshOrders,
    refreshPnl,
    refreshPolyOrders,
    refreshPolyPositions,
  ]);

  if (!venueAdmission.admitted) {
    // #904 — the page used to require the Kalshi connection even though the
    // Polymarket readers ride their own relay endpoints. Now each venue is
    // admitted on its own: this placeholder stands when NO venue can show
    // anything (neither configured, refused, or failed with nothing
    // retained), and it names each venue's reason so the next step is
    // visible. A Kalshi outage or reconnect with a readable Poly account
    // renders the page with an inline notice instead. #1060: once shown, it
    // also stays while a venue that could still change the answer is loading
    // (e.g. Kalshi reconnecting after a key-check Retry), rather than flashing
    // the page in between.
    return (
      <div className="h-full overflow-auto p-6">
        {/* StreamRift: no BotZones here — the g10edge fleet UI was pruned (f7a711d5). */}
        <VenuePlaceholder kalshi={kalshiVenue} poly={polyVenue} us={usVenue} onRetryUs={retryUsVenue} retryingUs={retryingUsVenue} onRetryKalshi={retryKalshiKeys} />
      </div>
    );
  }

  return (
    /* Value-Dashboard layout (Brendan, Jul 8): the main column scrolls in
       its OWN container while the right rail is a fixed sibling column
       OUTSIDE the scroller. The rail is therefore exactly viewport height
       by construction — always fully on screen. This retires the sticky
       rail and its two failure modes: 100vh sizing overshot the real
       scrollport (~150px of app chrome) and clipped the rail's bottom,
       and sticky's containing-block bound parked the rail short of the
       page bottom. Rail occupants own their interior scrolling. */
    <div className="flex h-full min-h-0">
      {/* LEFT COLUMN: optional top bar (org selector) + the scroller. The
          top bar lives HERE, at the left column's width — not spanning the
          page — so the rail's vertical separator runs unbroken to the very
          top of the layout (Brendan, Jul 8). */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Same glass chrome as the filters/refresh strip below it, so the
            admin org bar reads as part of the page's header system rather
            than a bare block (Brendan, Jul 8). Not sticky — it lives above
            the scroller, permanently in view. */}
        {topBar && (
          /* min-h matches the rail header band exactly so the two bands'
             hairlines meet across the vertical separator as one line.
             relative z-40: the band's quick reference opens downward over
             the scroller's sticky toolbars (z-30). */
          <div className="border-border/70 bg-background/95 supports-[backdrop-filter]:bg-background/85 relative z-40 flex min-h-[52px] shrink-0 items-center border-b px-6 backdrop-blur">
            {topBar}
          </div>
        )}
        <UsInventoryDetail read={usRead} sessionKey={usSessionKey} markets={usMarkets} dispatch={usDispatch}
          orderVM={usOrderVM} positionVM={usPositionVM} />
        {cardBookNotice && (
          <UnavailableCardBookNotice
            ticker={cardBookNotice.ticker}
            href={cardBookNotice.href}
            onDismiss={() => { setUnavailableBook(null); setBuyPanel(null); }}
            onViewRecord={noticePosition
              ? (trigger) => openPositionRecord(`${noticePosition.side}:${noticePosition.ticker}`, trigger)
              : undefined}
          />
        )}
        {/* #828 — the pane the reading position binds to. This is the
            page's real overflow element; the rail is a sibling OUTSIDE it
            and scrolls independently, so the two never fight. */}
        <div
          ref={readingScrollRef}
          data-testid="position-tracker-pane"
          className="min-h-0 flex-1 overflow-y-auto p-6"
        >
        <div className="space-y-5">
          {/* POSITIONS REGION — the positions toolbar's sticky containing
              block. A sticky element can never escape its parent's box, so
              scoping the toolbar to THIS wrapper makes it UN-PIN at the end
              of the positions section and hand the sticky slot to the
              orders toolbar below — previously it pinned forever, so deep
              in the orders list the visible bar filtered POSITIONS
              (Brendan, Jul 9). */}
          <div className="space-y-5">
          {/* Sticky toolbar only (cards scroll). -top-6 matches the -mt-6 so it
              pins flush to the top instead of leaving a gap below the container's
              top padding when scrolled. */}
          <div ref={positionsToolbarRef} className="border-border/70 bg-background/95 supports-[backdrop-filter]:bg-background/85 sticky -top-6 z-30 -mx-6 -mt-6 border-b px-6 pb-3 pt-6 backdrop-blur">
            {/* Header */}
          <div className="flex items-center justify-between gap-3">
            {/* The page title + balance moved into the tracker prong's header
                (Dan, Jul 7) — the toolbar leads with the filter chips. Only
                the paused badge stays as leading page chrome. */}
            <OrderbooksPausedBadge />
            {/* Middle: the shared two-line chip selector, scoped to Open
                Positions. Open Orders mounts its own copy over its own filter
                state in its section header. */}
            <SportMarketFilterRows
              scope="positions"
              className="flex-1"
              sportFilter={sportFilter}
              kindFilter={posKindFilter}
              onAllSports={handleAllSportsFilter}
              onSportToggle={handleSportFilterToggle}
              onAllKinds={handleAllPosKindsFilter}
              onKindToggle={handlePosKindFilterToggle}
              unclassifiedCount={unclassifiedPositionCount}
              migrationNotice={kindMigrationNotice.positions}
              onDismissMigrationNotice={dismissPosKindNotice}
            />
            {/* Right: alert controls + refresh. The Tracker/Fills toggles live
                at the top of the rail column itself (Dan, Jul 7) — anchored
                top-right regardless of docked state instead of crowding the
                chips. The alert launcher shares the refresh button's 26px box,
                so mounting it cannot grow this row; the rail was rejected as
                its anchor because it is `hidden lg:flex` and unmounts entirely
                while the buy panel is open. */}
            <div className="flex shrink-0 items-center gap-2">
              {rfqPositionCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowRfq(!showRfq)}
                  aria-pressed={showRfq}
                  title={showRfq ? 'Hide your RFQ positions' : 'Show your RFQ positions'}
                  className={`rounded border px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                    showRfq
                      ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300 light:text-emerald-700'
                      : 'border-border text-muted-foreground hover:text-foreground'
                  }`}
                >
                  RFQ {rfqPositionCount}
                </button>
              )}
              <AlertControlsMenu
                channels={alertPrefs.channels}
                onSoundEnabled={setChannelSoundEnabled}
                onVisualEnabled={setChannelVisualEnabled}
                onDeliveryThreshold={setChannelDeliveryThreshold}
              />
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 transition-colors"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
          </div>

          {/* #904 — a venue that cannot show rows is named here, above the
              figures it is missing from; the other venue's data stays live. */}
          {/* #1060: settled answers only (a pending read is not named; its
              figures read "—"), each as soon as it is known, so one venue that
              never answers cannot hide another's failure or its Retry. */}
          {(noticeWorthy(kalshiVenue) || noticeWorthy(polyVenue) || usNoticeWorthy(usVenue)) && (
            <VenueCoverageNotice kalshi={kalshiVenue} poly={polyVenue} us={usVenue} onRetryUs={retryUsVenue} retryingUs={retryingUsVenue} onRetryKalshi={retryKalshiKeys} />
          )}

          {/* Summary Cards — scroll normally (no longer pinned, so they can't
              ride over the Open Positions view-mode buttons). */}
          <div className={`grid gap-3 ${realizedTileShown ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <div className="bg-card border-border rounded-lg border px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-1.5">
                  <div
                    className="text-muted-foreground text-xs"
                    title={`${!showPnl ? MTM_PNL_TOOLTIP : `${MTM_PNL_TOOLTIP} ${
                      !positionsSnapshotConfirmed ? positionsSnapshotWarning : visiblePnlCoverage.complete
                        ? `${visiblePnlCoverage.pricedPositions}/${visiblePnlCoverage.totalPositions} positions fully priced`
                        : `Unrealized P&L withheld: ${describeWithheldTotal(visiblePnlCoverage, visiblePnlCoverage.unpricedSummary, formatDollars)}`
                    }`}`}
                  >
                    {/* Keep the heading compact; the hover describes actual
                        current accounting coverage across all admitted venues. */}
                    Unrealized P&L · MTM ({tileCoverage})
                    {showPnl && !visiblePnlCoverage.complete &&
                      ` · ${visiblePnlCoverage.pricedPositions}/${visiblePnlCoverage.totalPositions} priced`}
                  </div>
                </div>
                {/* #1060: the toggle sits with the refresh ring, not after the
                    title, whose text changes with coverage ("(K+P)" → "(P)"):
                    beside it the toggle slid 49 px at 1920 px and wider. */}
                <div className="flex shrink-0 items-center gap-1.5">
                  {/* P&L visibility toggle — hidden by default (Joshua, Jun 2:
                      "sucks to see a negative"); persisted in displaySettings.
                      Color class is also withheld while hidden so the green/red
                      tint can't leak the sign. */}
                  <button
                    onClick={toggleShowPnl}
                    className="text-muted-foreground hover:text-foreground rounded p-1 transition-colors"
                    title={showPnl ? 'Hide P&L dollars' : 'Show P&L dollars'}
                    aria-label={showPnl ? 'Hide P&L dollars' : 'Show P&L dollars'}
                    aria-pressed={showPnl}
                  >
                    {showPnl ? <Eye size={13} /> : <EyeOff size={13} />}
                  </button>
                  <RefreshProgress
                    anchorMs={pnlCardAnchorMs}
                    intervalMs={PNL_CARD_REFRESH_INTERVAL_MS}
                    refreshing={refreshing}
                  />
                </div>
              </div>
              <div
                className={`mt-1 text-lg font-semibold ${
                  !showPnl || !positionsSnapshotConfirmed || !visiblePnlCoverage.complete || displayTotalPnl == null
                    ? 'text-muted-foreground'
                    : displayTotalPnl >= 0
                      ? 'text-positive'
                      : 'text-negative'
                }`}
                title={
                  showPnl
                    ? !positionsSnapshotConfirmed
                      ? positionsSnapshotWarning
                      : visiblePnlCoverage.complete
                      ? `${visiblePnlCoverage.pricedPositions}/${visiblePnlCoverage.totalPositions} positions fully priced`
                      : `Unrealized P&L withheld: ${describeWithheldTotal(visiblePnlCoverage, visiblePnlCoverage.unpricedSummary, formatDollars)}`
                    : 'P&L hidden — click the eye on the Unrealized P&L tile to reveal'
                }
              >
                <span data-testid="tracker-unrealized-pnl">
                {!showPnl
                  ? '•••'
                  : !positionsSnapshotConfirmed ? '—' : visiblePnlCoverage.complete && displayTotalPnl != null
                    ? formatDollars(displayTotalPnl)
                    : withheldHeadlineText(visiblePnlCoverage, formatDollars)}
                </span>
                {!kalshiExcluded && <PositionsRefreshWarning positionsSync={positionsSync} showPnl={showPnl} title="Kalshi positions refresh unconfirmed; total withheld" />}
                {!polySnapshotConfirmed && <span className="text-muted-foreground ml-2 text-xs font-normal">
                  Poly unconfirmed
                </span>}
              </div>
            </div>
            {realizedTileShown && (
              <div className="bg-card border-border rounded-lg border px-4 py-3" data-testid="tracker-realized-tile">
                <div className="text-muted-foreground text-xs">Realized P&L by venue</div>
                <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <div
                    className="flex min-w-0 items-baseline gap-1"
                    title={
                      kalshiExcluded
                        ? 'Kalshi is not configured for this account; no Kalshi realized P&L to show'
                        : !kalshiRealizedKnown
                          ? kalshiVenue.state === 'loading' ? 'Kalshi realized P&L is loading' : 'Kalshi realized P&L is unavailable'
                        : realizedPnlNetCents == null
                          ? "Gross Kalshi realized P&L; recorded fee data is incomplete"
                          : `Net Kalshi realized P&L after ${formatDollars(realizedFeesCents ?? 0)} in recorded realized fees`
                    }
                  >
                    <span className="text-muted-foreground text-[10px]">
                      {!kalshiRealizedKnown ? 'K' : `K ${realizedPnlNetCents == null ? 'gross' : 'net'}`}
                    </span>
                    <span
                      className={`text-lg font-semibold ${
                        !showPnl || !kalshiRealizedKnown
                          ? 'text-muted-foreground'
                          : displayRealizedPnlCents >= 0
                            ? 'text-positive'
                            : 'text-negative'
                      }`}
                      data-testid="tracker-realized-kalshi"
                    >
                      {!showPnl ? '•••' : !kalshiRealizedKnown ? '—' : formatDollars(displayRealizedPnlCents)}
                    </span>
                  </div>
                  <div
                    className="flex min-w-0 items-baseline gap-1"
                    title={
                      polyRealizedPnl == null
                        ? polyPositionsState.status === 'loading' || polyPositionsState.status === 'idle'
                          ? 'Polymarket realized P&L is loading'
                          : 'Polymarket realized P&L is unavailable'
                        : polyRealizedPnlNetCents == null
                          ? 'Gross Polymarket realized P&L; recorded fee data is incomplete'
                          : `Net Polymarket realized P&L after ${formatDollars(polyRealizedFeesCents ?? 0)} in recorded realized fees`
                    }
                  >
                    <span className="text-muted-foreground text-[10px]">
                      {polyRealizedPnl == null
                        ? 'P'
                        : `P ${polyRealizedPnlNetCents == null ? 'gross' : 'net'}`}
                    </span>
                    <span
                      className={`text-lg font-semibold ${
                        !showPnl || displayPolyRealizedPnlCents == null
                          ? 'text-muted-foreground'
                          : displayPolyRealizedPnlCents >= 0
                            ? 'text-positive'
                            : 'text-negative'
                      }`}
                    >
                      {!showPnl
                        ? '•••'
                        : displayPolyRealizedPnlCents == null
                          ? '—'
                          : formatDollars(displayPolyRealizedPnlCents)}
                    </span>
                  </div>
                  {usCounted && <UsRealizedValue view={usRealized} showPnl={showPnl} />}
                </div>
              </div>
            )}
            <div className="bg-card border-border rounded-lg border px-4 py-3">
              <div
                className="text-muted-foreground text-xs"
                title={`${tileVenuesTitle} — value marks Kalshi at hydra mid and Polymarket at the live slug mid (entry-cost fallback when a market isn't live)${
                  usCounted ? '; Polymarket US uses the exact current market midpoint; its unknown value withholds the total' : ''}`}
              >
                Positions ({tileCoverage})
              </div>
              <div className="text-foreground mt-1 text-lg font-semibold" data-testid="tracker-positions-tile">
                {kpCountUnknown && !(usOnlyTiles && usAnswered) ? '—' : <>
                  {sortedPositions.length}
                  {(!kpCountUnknown || usOnlyTiles) && <span className="text-muted-foreground ml-1 text-sm font-normal">
                    / {visiblePositionValueDollars === null || !usPositionsConfirmed ? '—' : fmtUsd(visiblePositionValueDollars)}
                  </span>}
                </>}
              </div>
            </div>
            <div className="bg-card border-border rounded-lg border px-4 py-3">
              <div className="text-muted-foreground text-xs" title={tileVenuesTitle}>
                Open Orders ({tileCoverage})
              </div>
              <div className="text-foreground mt-1 text-lg font-semibold" data-testid="tracker-orders-tile">
                {orderFiguresUnknown && !(usOnlyTiles && usAnswered) ? '—' : sortedOrders.length}
              </div>
            </div>
          </div>

          {/* Open Positions — header is constant page chrome; only the
              item shape (compact list vs rows vs cards) changes. */}
          <section data-view-anchor="open-positions" className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <h2 className="text-foreground text-sm font-semibold">Open Positions</h2>
                <InfoTooltip title="Open Positions">
                  <PositionsHelp />
                </InfoTooltip>
                {ownAccountChip}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {positionViewMode !== 'table' && sortedPositions.length > 0 && (
                  <div className="border-border inline-flex overflow-hidden rounded-md border">
                    <SortSegmentButton
                      active={positionSort.key === 'default'}
                      dir={positionSort.dir}
                      label="Default"
                      title="Sort positions by event time, then ticker"
                      onClick={() => handlePositionSort('default')}
                    />
                    <SortSegmentButton
                      active={positionSort.key === 'size'}
                      dir={positionSort.dir}
                      label="Size"
                      title="Sort positions by contract size"
                      onClick={() => handlePositionSort('size')}
                    />
                    <SortSegmentButton
                      active={positionSort.key === 'velocity'}
                      dir={positionSort.dir}
                      label="Velocity"
                      title="Sort positions by how fast their Kalshi market price is moving (¢/min)"
                      onClick={() => handlePositionSort('velocity')}
                    />
                  </div>
                )}
                {positionViewMode !== 'table' && sortedPositions.length > 0 && (
                  <LockToggleButton
                    locked={positionsLockKeys != null}
                    onClick={togglePositionsLock}
                  />
                )}
                <ViewModeToggle
                  value={positionViewMode}
                  modes={POSITION_VIEW_MODES}
                  onChange={setPositionViewMode}
                />
              </div>
            </div>
            {sortedPositions.length > 0 ? (
              positionViewMode === 'card' || positionViewMode === 'mini' ? (
                <CardGrid variant={positionViewMode === 'mini' ? 'mini' : 'default'}>
                  {renderedPositionCardVMs.map((vm) => {
                    // #248: `detailHref` is the graph chip and nothing else on
                    // this surface reads it, so the chip seam owns the value.
                    const chipHref = positionChipHref(vm, positionLegacyHref(vm));
                    return (
                      <PositionCard
                        showRestingCloseCount={showRestingCloseCount}
                        key={vm.key}
                        viewAnchor={vm.key}
                        vm={vm}
                        close={closeFor(vm)}
                        annotations={annotated ? cardAnnotationsFor({ kind: 'position', venue: vm.venue, marketId: vm.ticker, outcome: vm.side }) : undefined}
                        {...sameGameProps(vm)}
                        mini={positionViewMode === 'mini'}
                        hidePnl={!showPnl}
                        upOdds={upOddsByKey.get(vm.key) ?? null}
                        divergence={divergenceByTicker.get(vm.ticker) ?? null}
                        divergenceFlash={visibleDivergenceFlashTickers.has(vm.ticker)}
                        detailHref={chipHref}
                        onOpen={() => openPositionRow(vm)}
                        onEdit={() =>
                          setEditPosition({
                            ticker: vm.ticker,
                            side: vm.side,
                            position: vm.qty,
                            averagePrice: vm.avgCents ?? 0,
                            currentPriceCents: vm.nowCents,
                          })
                        }
                        pinned={pinnedKeySet.has(vm.key)}
                        onTogglePin={() =>
                          togglePin({
                            key: vm.key,
                            kind: 'position',
                            label: ticketLabel(vm.subtitle ? `${vm.title} — ${vm.subtitle}` : vm.title, vmBet(vm)),
                            ...usPinOriginOf(vm.us),
                          })
                        }
                      />
                    );
                  })}
                </CardGrid>
              ) : positionViewMode === 'compact' ? (
                <CompactListGrid>
                  {renderedPositionCardVMs.map((vm) => {
                    const chipHref = positionChipHref(vm, positionLegacyHref(vm));
                    const label = ticketLabel(vm.subtitle ? `${vm.title} — ${vm.subtitle}` : vm.title, vmBet(vm));
                    return (
                      <PositionCardRow
                        usDispatch={usDispatch}
                        showRestingCloseCount={showRestingCloseCount}
                        key={vm.key}
                        viewAnchor={vm.key}
                        vm={vm}
                        polyClose={vm.venue === 'polymarket' ? polyCloseStateFor(vm.ticker, vm.side, vm.qty) : undefined}
                        requestPolyClose={requestPolyClose}
                        label={label}
                        highlighted={isSameGameHighlighted(
                          gameKeyByVmKey.has(vm.key)
                            ? (gameKeyByVmKey.get(vm.key) ?? null)
                            : rowGameKey(vm),
                          activeGameKey,
                        )}
                        hidePnl={!showPnl}
                        divergence={divergenceByTicker.get(vm.ticker) ?? null}
                        divergenceFlash={visibleDivergenceFlashTickers.has(vm.ticker)}
                        detailHref={chipHref}
                        pinned={pinnedKeySet.has(vm.key)}
                        setHoveredRow={setHoveredRow}
                        openOrderPanel={openOrderPanel}
                        setEditPosition={setEditPosition}
                        togglePin={togglePin}
                      />
                    );
                  })}
                                </CompactListGrid>
              ) : (
                  <div
                    /* overflow-x-auto: wide tables scroll INSIDE the card
                       instead of bleeding past its right edge (Brendan,
                       Jul-9 — the orders row view ran out of its container
                       at rail-squeezed widths). */
                    className="bg-card border-border overflow-x-auto rounded-lg border"
                  >
                    {/* whitespace-nowrap (inherits into every cell): columns
                        take the width their content needs and the wrapper's
                        overflow-x-auto absorbs the excess — WITHOUT it the
                        table squeezes into the container and cell text
                        row-wraps, tripling row height (Brendan, Jul 9). */}
                    <table className="w-full whitespace-nowrap">
                  <thead>
                    <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                      <SortableTh
                        className="px-4 py-2 text-left"
                        sortKey="default"
                        activeSortKey={positionSort.key}
                        activeSortDir={positionSort.dir}
                        onSort={handlePositionSort}
                      >
                        Market
                      </SortableTh>
                      <th className="w-12 px-2 py-2 text-center">Source</th>
                      <th className="px-3 py-2 text-center">Side</th>
                      <SortableTh
                        className="px-3 py-2 text-right"
                        sortKey="size"
                        activeSortKey={positionSort.key}
                        activeSortDir={positionSort.dir}
                        onSort={handlePositionSort}
                        title="Sort by position size"
                      >
                        Qty
                      </SortableTh>
                      <th className="px-3 py-2 text-right">Avg</th>
                    <th className="px-3 py-2 text-right">Last fill</th>
                    <th className="px-3 py-2 text-right">Fees</th>
                    <th
                      className="px-3 py-2 text-right"
                      title="Buy cost + fees"
                    >
                      Cost
                    </th>
                    <th className="px-3 py-2 text-right">Now</th>
                    <th
                      className="px-3 py-2 text-right"
                      title="Polymarket book mid for this market, side-native (a NO position reads 100 minus the YES-team value). Shows a dash when the market isn't a paired moneyline or the book hasn't delivered."
                    >
                      {POLY_MID_COLUMN_LABEL}
                    </th>
                    {/* P&L + Cash Out are bracketed by left/right borders so the
                        eye reads them as a pair: "how that compares to entry" +
                        "what you'd take home now". Cash Out sits on the right
                        so the pencil-edit icon (embedded in that cell) lines
                        up at the row's far edge. */}
                    <th
                      className="border-border/40 border-l px-3 py-2 text-right"
                      title="If closing current contract (Cash Out − Cost)"
                    >
                      P&L
                    </th>
                    <th
                      className="border-border/40 border-r px-3 py-2 text-right"
                      title="Liquidation value: market value minus est. close fee — what actually hits your account on a market sell"
                    >
                      Cash Out
                    </th>
                    <th
                      className="px-3 py-2 text-right"
                      title="If holding to final settlement"
                    >
                      If W
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {renderedPositionTableRows.map((row) => {
                    if (row.kind === 'us') {
                      // #1226 — a Polymarket US holding in the same columns.
                      const key = posRowKey(row);
                      const vm = usPositionVmByKey.get(key);
                      const link = usRowLink(usMarkets, row.pos.target, row.accountId);
                      return vm ? (
                        <UsPositionTableRow key={key} row={row} vm={vm} viewAnchor={key} pinned={pinnedKeySet.has(key)}
                          href={link.href} opens={link.opens} togglePin={togglePin} usDispatch={usDispatch} />
                      ) : null;
                    }
                    if (row.kind === 'poly') {
                      // Polymarket position rows — full Kalshi parity: Now is
                      // the side-native slug-market mid. Cash Out subtracts
                      // the relay/Gamma taker schedule because Close submits a
                      // marketable FAK sell. Unknown schedule withholds the
                      // liquidation result instead of inventing a $0 fee.
                      const p = row.pos;
                      const target = resolvePolyCloseTarget(p.ticker, p.side);
                      const desc = target ? describePolyOrderCached(target.tokenId) : null;
                      const label = polyPositionLabel(p.ticker, desc);
                      const live = polyPositionLiveData(p.ticker, p.side);
                      const nowCents = live.nowCents;
                      const feesPaid = p.feesPaidCents ?? 0;
                      // Exact relay basis; avg×qty only as a fallback for
                      // stale cached payloads (whole-cent avg re-multiplied
                      // drifts — the I-035 class).
                      const costCents = p.costBasisCents ?? p.avgEntryPriceCents * p.netCount;
                      const totalCostCents = costCents + feesPaid;
                      const splitCostUnknown = (p.splitCount ?? 0) > 0 || (p.splitConflictCount ?? 0) > 0 || (p.splitFeeUnknownCount ?? 0) > 0;
                      const totalValueCents =
                        nowCents != null ? nowCents * p.netCount : null;
                      const closeFeeCents = nowCents != null
                        ? estimatePolyOrderFeeCents({
                            priceCents: nowCents,
                            count: p.netCount,
                            role: 'taker',
                            schedule: polyFeeSchedules.get(p.ticker),
                          })
                        : null;
                      const cashOutCents =
                        totalValueCents != null && closeFeeCents != null
                          ? totalValueCents - closeFeeCents
                          : null;
                      const unrealizedPnl =
                        !splitCostUnknown && cashOutCents !== null ? cashOutCents - totalCostCents : null;
                      const ifWinCents = 100 * p.netCount - totalCostCents;
                      // Entry juice on the client accounting convention —
                      // mirrors positionCardModel / the closed-trade card
                      // (fee-inclusive risk vs fee-free winnings), so the
                      // row's juice survives settlement unchanged.
                      const entryJuice = polyTradeEconomics({
                        qty: p.netCount,
                        totalInDollars:
                          p.feesPaidCents == null ? null : totalCostCents / 100,
                        feesDollars:
                          p.feesPaidCents == null ? null : feesPaid / 100,
                      }).juice;
                      // Click target rides the resolved DESCRIPTION (the
                      // ledger's conditionId can't parse and never keys the
                      // game URL directly). Unresolved market → the row is
                      // deliberately not clickable and says why — a pointer
                      // that silently no-ops reads as a bug.
                      const href = polyTableLegacyGameHref(desc);
                      // #248: the graph chip alone re-routes for ATP/WTA. Row
                      // CLICK behavior is untouched — it still rides `href`,
                      // the legacy destination, exactly as before.
                      const chipHref = positionChipHref(
                        { venue: 'polymarket', ticker: p.ticker, side: p.side },
                        href,
                      );
                      // Clickable needs a real destination: the game href
                      // (moneyline descs carry 'A @ B' matchups). Frame-
                      // fallback descs (spread/total) resolve LABELS but
                      // have no trade-panel surface yet — honest tooltip
                      // instead of a dead pointer.
                      const clickable = target !== null && href !== null;
                      // Same-game highlight — same resolution the card views use,
                      // so every view mode agrees on which game a row belongs to.
                      const sgRow = sameGameRow({
                        venue: 'polymarket',
                        ticker: p.ticker,
                        key: `poly-pos-${p.ticker}-${p.side}`,
                      });
                      return (
                        <tr
                          key={`poly-pos-${p.ticker}-${p.side}`}
                          data-view-anchor={posRowKey(row)}
                          onMouseEnter={sgRow.onMouseEnter}
                          onMouseLeave={sgRow.onMouseLeave}
                          className={`border-border/40 hover:bg-muted/30 border-b ${
                            clickable ? 'cursor-pointer' : ''
                          } ${sgRow.className}`}
                          title={
                            clickable
                              ? p.ticker
                              : desc
                                ? `${p.ticker} — no trade panel for this market type yet (spread/total); Close still works from the row`
                                : `${p.ticker} — can't open the trade panel: market not in the live feed (open its sport tab; expired markets can only be closed or held to settlement)`
                          }
                          onClick={
                            clickable
                              ? () => openPolyOrderPanel(target.tokenId, href)
                              : undefined
                          }
                        >
                          <td className="text-foreground px-4 py-2 font-mono text-xs">
                            {label}
                            {row.familyPending && <FamilyPendingMark />}
                            {chipHref && <GameDetailLink href={chipHref} className="-my-1 ml-1" />}
                          </td>
                          <td className="px-2 py-2 text-center">
                            <VenueChip venue="polymarket" />
                          </td>
                          {/* Side chip: when the token resolves, render the
                              DESCRIPTION's (betTeam, side) pair — desc.side
                              means "betTeam wins/loses" for the HELD token.
                              Pairing betTeam with the LEDGER side (slug
                              orientation) reads as the inverse position
                              whenever the slug's YES team is the home team.
                              Unresolved → bare ledger side (still correct,
                              just team-less). */}
                          <td className="px-3 py-2 text-center">
                            <span
                              className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                                (desc?.side ?? p.side) === 'yes'
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-red-500/20 text-red-400'
                              }`}
                            >
                              {desc
                                ? `(${desc.betTeam}) ${desc.side.toUpperCase()}`
                                : p.side.toUpperCase()}
                            </span>
                          </td>
                          <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                            {Number(p.netCount.toFixed(2))}
                          </td>
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            {formatCents(p.avgEntryPriceCents)}
                          </td>
                          <td
                            className="text-muted-foreground px-3 py-2 text-right font-mono text-xs"
                            title={p.lastFillAt ?? undefined}
                          >
                            {fmtLastFillAt(p.lastFillAt)}
                          </td>
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            {p.feesPaidCents != null
                              ? fmtUsd(p.feesPaidCents / 100)
                              : '—'}
                          </td>
                          {/* Cost = entry + fees, same semantics as the Kalshi
                              column ("Buy cost + fees"). */}
                          <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                            {splitCostUnknown ? '—' : fmtUsd(totalCostCents / 100)}
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-mono text-xs ${nowColorClass(nowCents)}`}
                          >
                            {nowCents !== null ? formatCents(nowCents) : '—'}
                          </td>
                          {/* Poly mid — resolved via the paired game slot's token
                              ids (the poly slug rides both team slots, so a
                              token match decides the side). Dash when the
                              relay ticker doesn't resolve in the live store. */}
                          <td className="px-3 py-2 text-right font-mono text-xs">
                            <UPOddsValue
                              odds={upOddsByKey.get(`poly-pos-${p.ticker}-${p.side}`) ?? null}
                            />
                          </td>
                          <td
                            className="border-border/40 border-l px-3 py-2 text-right font-mono text-xs font-medium"
                            title={
                              closeFeeCents == null
                                ? 'Cash Out unavailable: Polymarket close-fee schedule is not established.'
                                : `Cash Out − Cost after estimated taker close fee ${fmtUsd(closeFeeCents / 100)}.`
                            }
                          >
                            <span
                              className={
                                !showPnl || unrealizedPnl === null
                                  ? 'text-muted-foreground'
                                  : unrealizedPnl >= 0
                                    ? 'text-positive'
                                    : 'text-negative'
                              }
                              title={showPnl ? undefined : 'P&L hidden — click the eye on the Unrealized P&L tile to reveal'}
                            >
                              {unrealizedPnl === null
                                ? '—'
                                : showPnl
                                  ? formatDollars(unrealizedPnl)
                                  : '•••'}
                            </span>
                          </td>
                          {/* Cash Out — live value minus estimated taker close fee
                              + the marketable FAK close of the held token
                              (verify-and-retry, lib/polymarket/closePosition).
                              Ledger rows carry the conditionId; the close is
                              only offered once the live store can map it to
                              the held outcome token (sport tab loaded). */}
                          <td
                            className="border-border/40 border-r px-3 py-2 text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-2">
                            <span className="text-foreground font-mono text-xs">
                              {cashOutCents !== null ? fmtUsd(cashOutCents / 100) : '—'}
                            </span>
                            <PinToggle
                              pinned={pinnedKeySet.has(`poly-pos-${p.ticker}-${p.side}`)}
                              onToggle={() =>
                                togglePin({
                                  key: `poly-pos-${p.ticker}-${p.side}`,
                                  kind: 'position',
                                  label,
                                })
                              }
                            />
                            <PolyCloseControl
                              state={polyCloseStateFor(p.ticker, p.side, p.netCount)}
                              onClose={() => requestPolyClose(`poly-pos-${p.ticker}-${p.side}`)}
                            />
                            </div>
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-mono text-xs ${
                              splitCostUnknown ? 'text-muted-foreground' : ifWinCents >= 0 ? 'text-positive' : 'text-negative'
                            }`}
                          >
                            <div className="flex flex-col items-end leading-tight">
                              <span>
                                {splitCostUnknown ? '—' : formatDollars(ifWinCents)}{' '}
                                {/* Gross redemption payout (qty × $1) — same
                                    as Kalshi settlement; poly redemption has
                                    no fee either. toFixed→Number: poly counts
                                    are fractional floats (I-035 dust) — show
                                    3.3, never 3.3000000000000003. */}
                                <span
                                  className="text-muted-foreground"
                                  title="Gross settlement payout — qty × $1, no settlement fee"
                                >
                                  (${Number(p.netCount.toFixed(2))})
                                </span>
                              </span>
                              {p.netCount > 0 && (
                                <span className="text-foreground text-[10px]">
                                  {entryJuice}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    const pos = row.pos;
                    const { marketLabel, betTeam: fullBetTeam, titleTeam, badgeTeam, marketType } = describeMarket(pos.ticker, pos.side, {
                      gameDate: resolvedGameDate(pos.ticker),
                    });
                    // #964: the side cell keeps the #603 one-line badge; an NFL ladder
                    // prop's stat stacks under it so the column stays badge-wide.
                    const betTeam = badgeTeam ?? fullBetTeam;
                    const timeLabel = formatStartTimeLocal(row.sortStart);
                    const href = gameDetailHref(pos.ticker, pos.side);
                    // #248: chip only. The row's click, tooltip and hover
                    // affordance stay on `href`, the pre-change destination.
                    const chipHref = positionChipHref(
                      { venue: 'kalshi', ticker: pos.ticker, side: pos.side },
                      href,
                    );
                    // `label` is the Market cell's text; `ticket` names the row in
                    // pin snapshots and dialogs, the whole bet first (#964).
                    const label = `${marketLabel}${timeLabel ? ` ${timeLabel}` : ''}`;
                    const ticket = ticketLabel(label, fullBetTeam);
                    const divergence = divergenceByTicker.get(pos.ticker) ?? null;
                    const sgRow = sameGameRow({
                      venue: 'kalshi',
                      ticker: pos.ticker,
                      key: `${pos.ticker}-${pos.side}`,
                    });
                    return (
                    <tr
                      key={`${pos.ticker}-${pos.side}`}
                      data-view-anchor={posRowKey(row)}
                      onMouseEnter={sgRow.onMouseEnter}
                      onMouseLeave={sgRow.onMouseLeave}
                      className={`border-border/40 hover:bg-muted/30 cursor-pointer border-b ${
                        visibleDivergenceFlashTickers.has(pos.ticker) ? 'animate-divergence-flash' : ''
                      } ${sgRow.className}`}
                      // The click is no longer gated on `href` (#98) — an order
                      // panel can resolve for a market that has no game page.
                      // The explanatory tooltip now tracks what it actually
                      // claims: a market absent from the live feed is the state
                      // where neither surface can open.
                      title={
                        href || inLiveFeed(pos.ticker)
                          ? pos.ticker
                          : `${pos.ticker} — can't open the trade panel: market not in the live feed and the ticker doesn't map to a game page`
                      }
                      onClick={() => openOrderPanel(pos.ticker, pos.side)}
                    >
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        <span
                          className={href ? 'hover:text-blue-400 hover:underline' : undefined}
                        >
                          <BetTeamTitle title={label} team={titleTeam} />
                        </span>
                        {row.familyPending && <FamilyPendingMark />}
                        {chipHref && <GameDetailLink href={chipHref} className="-my-1 ml-1" />}
                        <DivergenceBadge info={divergence} className="ml-1.5 align-middle" />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <VenueChip venue="kalshi" />
                        {pos.origin && <RfqOriginChip origin={pos.origin} />}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex flex-col items-center gap-0.5">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                              pos.side === 'yes'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {betTeam ? `(${betTeam}) ` : ''}{pos.side.toUpperCase()}
                          </span>
                          <MarketTypeChip marketType={marketType} />
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {/* toFixed→Number: relay counts can be fractional
                            (fp fills) — show 12.5, never 3.3000000000000003 */}
                        {Number(pos.position.toFixed(2))}
                        {showRestingCloseCount && <RestingCloseNote qty={row.restingCloseQty} />}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        {formatCents(pos.averagePrice)}
                      </td>
                      <td
                        className="text-muted-foreground px-3 py-2 text-right font-mono text-xs"
                        title={pos.lastFillAt ?? undefined}
                      >
                        {fmtLastFillAt(pos.lastFillAt)}
                      </td>
                      <td
                        className="text-muted-foreground px-3 py-2 text-right font-mono text-xs"
                        title={
                          pos.feesPaidCents == null
                            ? 'No fee data on this position\u2019s fills yet'
                            : undefined
                        }
                      >
                        {pos.feesPaidCents != null
                          ? fmtUsd(pos.feesPaidCents / 100)
                          : '—'}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {fmtUsd(pos.totalCostCents / 100)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs ${nowColorClass(pos.currentPrice)}`}
                      >
                        {pos.currentPrice !== null ? formatCents(pos.currentPrice) : '—'}
                      </td>
                      {/* Poly mid, side-native (parity with the position
                          card's poly stat). Moneyline
                          markets only; other market types render '—'. */}
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        <UPOddsValue
                          odds={upOddsByKey.get(`${pos.ticker}-${pos.side}`) ?? null}
                        />
                      </td>
                      {/* P&L + Cash Out: bracketed pair. P&L is the round-trip
                          delta from cost basis; Cash Out is the post-fee
                          liquidation value. Pencil-edit lives in Cash Out so
                          the action sits at the row's far right edge. */}
                      <td
                        className="border-border/40 border-l px-3 py-2 text-right font-mono text-xs font-medium"
                        title="Cash Out − Cost (round-trip P&L if you sold now)"
                      >
                        <span
                          className={
                            !showPnl || pos.unrealizedPnl === null
                              ? 'text-muted-foreground'
                              : pos.unrealizedPnl >= 0
                                ? 'text-positive'
                                : 'text-negative'
                          }
                          title={showPnl ? undefined : 'P&L hidden — click the eye on the Unrealized P&L tile to reveal'}
                        >
                          {pos.unrealizedPnl === null
                            ? '—'
                            : showPnl
                              ? formatDollars(pos.unrealizedPnl)
                              : '•••'}
                        </span>
                      </td>
                      <td
                        className="text-foreground border-border/40 border-r px-3 py-2 text-right font-mono text-xs"
                        title={
                          pos.estClosingFeeCents !== null
                            ? `Market value ${fmtUsd((pos.totalValueCents ?? 0) / 100)} − est. close fee ${fmtUsd(pos.estClosingFeeCents / 100)}`
                            : 'Close fee not estimated (rate not loaded for this series) — value shown is pre-fee'
                        }
                      >
                        <div className="flex items-center justify-end gap-2">
                          <span>
                            {pos.cashOutCents !== null
                              ? fmtUsd(pos.cashOutCents / 100)
                              : '—'}
                          </span>
                          <PinToggle
                            pinned={pinnedKeySet.has(`${pos.ticker}-${pos.side}`)}
                            onToggle={() =>
                              togglePin({
                                key: `${pos.ticker}-${pos.side}`,
                                kind: 'position',
                                label: ticket,
                              })
                            }
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditPosition({
                                ticker: pos.ticker,
                                side: pos.side,
                                position: pos.position,
                                averagePrice: pos.averagePrice,
                                currentPriceCents: pos.currentPrice,
                              });
                            }}
                            className="text-muted-foreground rounded p-1 transition-colors hover:bg-blue-500/10 hover:text-blue-400"
                            title="Close / edit position"
                          >
                            <Pencil size={12} />
                          </button>
                        </div>
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs ${
                          pos.ifWinCents >= 0 ? 'text-positive' : 'text-negative'
                        }`}
                      >
                        <div className="flex flex-col items-end leading-tight">
                          <span>
                            {formatDollars(pos.ifWinCents)}{' '}
                            {/* Gross settlement payout (qty × $1) — what
                                actually hits the account on a WIN before
                                netting cost. Settlement has no Kalshi fee. */}
                            <span
                              className="text-muted-foreground"
                              title="Gross settlement payout — qty × $1, no settlement fee"
                            >
                              (${Number(pos.position.toFixed(2))})
                            </span>
                          </span>
                          {(() => {
                            // Implied cost per contract (cents) = totalCost / qty
                            // — fee-adjusted average entry. Feed straight into the
                            // existing americanOdds() helper, which guards 0/100.
                            if (pos.position <= 0) return null;
                            const impliedCents = pos.totalCostCents / pos.position;
                            const odds = americanOdds(impliedCents);
                            return (
                              <span className="text-foreground text-[10px]">{odds}</span>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
                </div>
              )
            ) : (
              <div className="bg-card border-border text-muted-foreground rounded-lg border px-4 py-8 text-center text-sm">
                {positionsEmptyText}
                {positionsUsUnavailable?.retry && <UsUnavailableRetry state={positionsUsUnavailable} />}
              </div>
            )}
          </section>
          </div>
          {/* ← end POSITIONS REGION (positions toolbar un-pins here) */}

          {/* PINNED — Prof's watch strip (Jul 9): duplicate/watch copies of
              pinned positions and orders. Its DOM slot is BETWEEN the two
              sections. Prof's Jul 24 follow-up makes normal document flow the
              default so tall Card mode cannot lock over the rest of the page.
              The header toggle can opt back into the original two-edge sticky
              behavior; in that mode z-40 outranks the section toolbars and the
              orders toolbar shifts by pinnedBlockH. Renders nothing while no
              pins exist. */}
          {pinnedEntries.length > 0 && (
            <section
              data-view-anchor="pinned"
              ref={pinsSticky ? pinnedStripRef : undefined}
              className={`border-border/70 bg-background/95 supports-[backdrop-filter]:bg-background/85 -mx-6 space-y-2 border-b border-t px-6 py-2 backdrop-blur ${
                pinnedPositionClass(pinsSticky)
              }`}
            >
              {/* The WHOLE header is the expand/collapse button (Brendan,
                  Jul 9) — pointer cursor + hover tint, chevron flips, an
                  aria-label carries the action for screen readers. The info
                  tooltip and view toggle stop propagation so using them
                  never collapses the strip. */}
              <div
                role="button"
                tabIndex={0}
                aria-expanded={!pinsCollapsed}
                aria-label={pinsCollapsed ? 'Expand pinned items' : 'Collapse pinned items'}
                onClick={() => setPinsCollapsed((c) => !c)}
                onKeyDown={(e) => {
                  if (e.currentTarget !== e.target) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPinsCollapsed((c) => !c);
                  }
                }}
                className="hover:bg-muted/30 -mx-2 flex cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-0.5 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400/70"
              >
                <div className="flex items-center gap-1.5">
                  <Pin size={13} className="text-amber-400" />
                  <h2 className="text-foreground text-sm font-semibold">Pinned</h2>
                  <span className="text-muted-foreground text-xs">
                    ({pinnedEntries.length})
                  </span>
                  {openOrders.length > 0 &&
                    pinnedEntries.some((entry) => entry.kind === 'order') && (
                      <span onClick={(e) => e.stopPropagation()}>
                        <QueueVintageBadge status={queueStatus} />
                      </span>
                    )}
                  <span onClick={(e) => e.stopPropagation()}>
                    <InfoTooltip title="Pinned">
                      Watch copies of positions and orders you pinned with the
                      thumbtack. The original stays in its own section — both
                      copies update live. Pins survive sorting and filtering,
                      and the strip normally stays in its home slot between
                      Open Positions and Open Orders. Use the scroll-pin toggle
                      beside the view selector if you want it to follow the
                      viewport instead. A pinned Kalshi order is also skipped
                      by Cancel All and Pull All on this browser. A pinned
                      Polymarket US item names its account where it can
                      differ. A pinned item that fills, closes or leaves
                      the open list stays here as a grayed note until you
                      unpin it. Click the
                      header to collapse or expand.
                    </InfoTooltip>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <PinnedHeaderControls
                    viewMode={pinsViewMode}
                    onViewModeChange={setPinsViewMode}
                    sticky={pinsSticky}
                    onStickyChange={setPinsSticky}
                  />
                  <ChevronDown
                    size={14}
                    aria-hidden="true"
                    className={`text-muted-foreground transition-transform duration-150 ${
                      pinsCollapsed ? '-rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
              {!pinsCollapsed && pinsViewMode === 'card' && (
                /* CARD (#76) — the SAME PositionCard / OrderCard the Open
                   Positions and Open Orders sections render, not a strip
                   lookalike: the Professor asked for "Card View for that
                   area", and a second imitation would drift from the real
                   cards the first time either changes. Every prop mirrors
                   the section call sites, so a card behaves identically
                   whether it's pinned or in its home section — same click
                   target, same edit/cancel, same divergence + up-odds
                   badges, same same-game hover.
                   Kept inside the horizontal EdgeFadeScroller rather than a
                   CardGrid: the strip can become sticky chrome at the viewport
                   edge, so it stays one row deep and absorbs width by scrolling.
                   A grid here would let the strip grow vertically without
                   bound and eat the page. Cards are min-width'd (not the
                   compact strip's fixed g1-pin-strip__card) because a real
                   card carries more stat lines and shouldn't be squeezed. */
                <EdgeFadeScroller>
                  <PinnedCardStrip
                    rows={pinnedRows}
                    onUnpin={togglePin}
                    renderPosition={(posVM) => {
                      const chipHref = positionChipHref(posVM, positionLegacyHref(posVM));
                      return (
                        <PositionCard
                          showRestingCloseCount={showRestingCloseCount}
                          vm={posVM}
                          close={closeFor(posVM)}
                          annotations={annotated ? cardAnnotationsFor({ kind: 'position', venue: posVM.venue, marketId: posVM.ticker, outcome: posVM.side }) : undefined}
                          {...sameGameProps(posVM)}
                          hidePnl={!showPnl}
                          upOdds={upOddsByKey.get(posVM.key) ?? null}
                          divergence={divergenceByTicker.get(posVM.ticker) ?? null}
                          divergenceFlash={visibleDivergenceFlashTickers.has(posVM.ticker)}
                          detailHref={chipHref}
                          onOpen={() => openPositionRow(posVM)}
                          onEdit={() =>
                            setEditPosition({
                              ticker: posVM.ticker,
                              side: posVM.side,
                              position: posVM.qty,
                              averagePrice: posVM.avgCents ?? 0,
                              currentPriceCents: posVM.nowCents,
                            })
                          }
                          pinned
                          onTogglePin={() =>
                            togglePin({
                              key: posVM.key,
                              kind: 'position',
                              label: ticketLabel(
                                  posVM.subtitle ? `${posVM.title} — ${posVM.subtitle}` : posVM.title,
                                  vmBet(posVM),
                                ),
                            })
                          }
                        />
                      );
                    }}
                    renderOrder={(orderVM) => {
                      const href = orderLegacyHref(orderVM);
                      // #248: chip only — `onOpen` keeps the legacy `href` as
                      // its Poly navigate fallback, unchanged.
                      const chipHref = orderChipHref(orderVM, href);
                      const label = ticketLabel(
                          orderVM.subtitle ? `${orderVM.title} — ${orderVM.subtitle}` : orderVM.title,
                          vmBet(orderVM),
                        );
                      return (
                        <OrderCard
                          vm={orderVM}
                          actions={orderVM.us ? usOrderActionProps(orderVM.us, usDispatch) : undefined}
                          annotations={annotated ? cardAnnotationsFor({ kind: 'order', venue: orderVM.venue, venueOrderId: orderVM.cancelId }) : undefined}
                          eventStateCertainty={eventStateCertaintyByTicker.get(orderVM.ticker)}
                          {...sameGameProps(orderVM)}
                          // Live signals + the mute control, from the SAME sources as
                          // the Open Orders call site. A pinned card that quietly
                          // dropped these would be the exact "second imitation" #76
                          // set out to avoid — worse, silently: the card would look
                          // right while never flashing a queue-critical crossing or a
                          // velocity spike, on the surface a trader pins *because*
                          // they want to watch it.
                          queueCriticalFlash={visibleQueueCriticalFlashIds.has(orderVM.key)}
                          queueWarningFlash={visibleQueueWarningFlashIds.has(orderVM.key)}
                          fellBehindFlash={visibleFellBehindFlashIds.has(orderVM.key)}
                          velocitySpikeSeverity={velocitySpikeFlash.get(orderVM.key) ?? null}
                          velocityMuted={velocityMutedIds.has(orderVM.key)}
                          onToggleVelocityMute={() => toggleVelocityMute(orderVM.key)}
                          divergence={divergenceByTicker.get(orderVM.ticker) ?? null}
                          divergenceFlash={visibleDivergenceFlashTickers.has(orderVM.ticker)}
                          upOdds={upOddsByKey.get(orderVM.key) ?? null}
                          detailHref={chipHref}
                          onOpen={openOrderRow(orderVM, href)}
                          {...rowCancelProps(orderVM.venue, orderVM.cancelId)}
                          onCancel={() =>
                            requestCancelOrder({
                              venue: orderVM.venue,
                              orderId: orderVM.cancelId,
                              label,
                            })
                          }
                          onEdit={() => {
                            if (orderVM.venue === 'polymarket_us') return;
                            setEditOrder({
                              venue: orderVM.venue,
                              orderId: orderVM.cancelId,
                              marketLabel: label,
                              side: orderVM.side ?? 'yes',
                              action: orderVM.isSell ? 'sell' : 'buy',
                              remainingCount: orderVM.remainingQty,
                              currentPriceCents: orderVM.bidCents ?? 50,
                              ticker: orderVM.ticker,
                              tickSize: orderVM.tickSize,
                              negRisk: orderVM.negRisk,
                            });
                          }}
                          pinned
                          onTogglePin={() =>
                            togglePin({ key: orderVM.key, kind: 'order', label })
                          }
                        />
                      );
                    }}
                  />
                </EdgeFadeScroller>
              )}
              {!pinsCollapsed && pinsViewMode === 'compact' && (
                /* COMPACT — the strip's original presentation, unchanged.
                   Renamed from 'card' in #76 when a real Card mode landed;
                   the pixels are identical, only the label moved.
                   g1-pin-strip: container-query card sizing — N full cards
                   + a ~third of the next fit the visible width exactly, so
                   full cards are never faded and the overflow card "creeps
                   in" through the edge fade (globals.css). */
                <EdgeFadeScroller className="g1-pin-strip">
                  <div className="flex w-max items-stretch gap-2">
                    {pinnedRows.map(({ entry, posVM, orderVM }) => {
                      if (posVM) {
                        const href = positionLegacyHref(posVM);
                        // #248: chip only — `onOpen`'s gate keeps the legacy
                        // `href`, so no card gains or loses a click.
                        const chipHref = positionChipHref(posVM, href);
                        return (
                          <div key={entry.key}
                            className="border-border g1-pin-strip__card shrink-0 overflow-hidden rounded-lg border"
                          >
                            <CompactPositionItem
                              showRestingCloseCount={showRestingCloseCount}
                              vm={posVM}
                              close={closeFor(posVM)}
                              annotations={annotated ? cardAnnotationsFor({ kind: 'position', venue: posVM.venue, marketId: posVM.ticker, outcome: posVM.side }) : undefined}
                              {...sameGameProps(posVM)}
                              hidePnl={!showPnl}
                              divergence={divergenceByTicker.get(posVM.ticker) ?? null}
                              divergenceFlash={visibleDivergenceFlashTickers.has(posVM.ticker)}
                              detailHref={chipHref}
                              onOpen={
                                posVM.us ? () => openPositionRow(posVM) : href ? () => openOrderPanel(posVM.ticker, posVM.side) : undefined
                              }
                              onEdit={() =>
                                setEditPosition({
                                  ticker: posVM.ticker,
                                  side: posVM.side,
                                  position: posVM.qty,
                                  averagePrice: posVM.avgCents ?? 0,
                                  currentPriceCents: posVM.nowCents,
                                })
                              }
                              pinned
                              onTogglePin={() => togglePin(entry)}
                            />
                          </div>
                        );
                      }
                      if (orderVM) {
                        const href = orderLegacyHref(orderVM);
                        const chipHref = orderChipHref(orderVM, href);
                        const label = ticketLabel(
                            orderVM.subtitle ? `${orderVM.title} — ${orderVM.subtitle}` : orderVM.title,
                            vmBet(orderVM),
                          );
                        return (
                          <div
                            key={entry.key}
                            className="border-border g1-pin-strip__card shrink-0 overflow-hidden rounded-lg border"
                          >
                            <CompactOrderItem
                              vm={orderVM}
                              actions={orderVM.us ? usOrderActionProps(orderVM.us, usDispatch) : undefined}
                              annotations={annotated ? cardAnnotationsFor({ kind: 'order', venue: orderVM.venue, venueOrderId: orderVM.cancelId }) : undefined}
                              eventStateCertainty={eventStateCertaintyByTicker.get(orderVM.ticker)}
                              {...sameGameProps(orderVM)}
                              detailHref={chipHref}
                              onOpen={openOrderRow(orderVM, href)}
                              {...rowCancelProps(orderVM.venue, orderVM.cancelId)}
                              onCancel={() =>
                                requestCancelOrder({
                                  venue: orderVM.venue,
                                  orderId: orderVM.cancelId,
                                  label,
                                })
                              }
                              onEdit={() => {
                                if (orderVM.venue === 'polymarket_us') return;
                                setEditOrder({
                                  venue: orderVM.venue,
                                  orderId: orderVM.cancelId,
                                  marketLabel: label,
                                  side: orderVM.side ?? 'yes',
                                  action: orderVM.isSell ? 'sell' : 'buy',
                                  remainingCount: orderVM.remainingQty,
                                  currentPriceCents: orderVM.bidCents ?? 50,
                                  ticker: orderVM.ticker,
                                  tickSize: orderVM.tickSize,
                                  negRisk: orderVM.negRisk,
                                });
                              }}
                              pinned
                              onTogglePin={() => togglePin(entry)}
                            />
                          </div>
                        );
                      }
                      // Tombstone — the pinned item is no longer in the open
                      // set. It stays (grayed) rather than silently vanishing:
                      // the whole point of a pin is that the trader never has
                      // to wonder where the item went.
                      return (
                        <div
                          key={entry.key}
                          className="border-border/60 bg-card g1-pin-strip__card flex shrink-0 items-center gap-2 overflow-hidden rounded-lg border border-dashed px-3 py-2"
                        >
                          <span className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
                            {stripTickerTail(entry.label)}
                            <span className="ml-2 italic">
                              — no longer open (
                              {entry.kind === 'position'
                                ? 'closed or settled'
                                : 'filled or canceled'}
                              )
                            </span>
                          </span>
                          <button
                            onClick={() => togglePin(entry)}
                            title="Unpin"
                            className="text-muted-foreground hover:text-foreground shrink-0 rounded p-1 transition-colors"
                          >
                            <PinOff size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </EdgeFadeScroller>
              )}
              {!pinsCollapsed && pinsViewMode === 'table' && (
                <EdgeFadeScroller>
                  {/* Grid + per-row subgrid: each field group is a shared
                      column, sized by its widest member across rows — chits,
                      titles, and sport·date all sit on the same meridians
                      without being forced equal-width (Brendan, Jul 9). */}
                  <div
                    className={`border-border bg-card divide-border/40 grid w-max min-w-full gap-x-3 divide-y overflow-hidden rounded-lg border ${PINNED_TABLE_GRID_CLASS}`}
                  >
                    {pinnedRows.map(({ entry, posVM, orderVM }) => {
                      if (posVM) {
                        const href = positionLegacyHref(posVM);
                        const chipHref = positionChipHref(posVM, href);
                        return (
                          <PinnedPositionRow key={entry.key}
                            showRestingCloseCount={showRestingCloseCount}
                            vm={posVM}
                            close={closeFor(posVM)}
                            {...sameGameProps(posVM)}
                            hidePnl={!showPnl}
                            detailHref={chipHref}
                            onOpen={
                              posVM.us ? () => openPositionRow(posVM) : href ? () => openOrderPanel(posVM.ticker, posVM.side) : undefined
                            }
                            onEdit={() =>
                              setEditPosition({
                                ticker: posVM.ticker,
                                side: posVM.side,
                                position: posVM.qty,
                                averagePrice: posVM.avgCents ?? 0,
                                currentPriceCents: posVM.nowCents,
                              })
                            }
                            onUnpin={() => togglePin(entry)}
                          />
                        );
                      }
                      if (orderVM) {
                        const href = orderLegacyHref(orderVM);
                        const chipHref = orderChipHref(orderVM, href);
                        const label = ticketLabel(
                            orderVM.subtitle ? `${orderVM.title} — ${orderVM.subtitle}` : orderVM.title,
                            vmBet(orderVM),
                          );
                        return (
                          <PinnedOrderRow
                            key={entry.key}
                            vm={orderVM}
                            actions={orderVM.us ? usOrderActionProps(orderVM.us, usDispatch) : undefined}
                            {...sameGameProps(orderVM)}
                            detailHref={chipHref}
                            onOpen={openOrderRow(orderVM, href)}
                            {...rowCancelProps(orderVM.venue, orderVM.cancelId)}
                            onCancel={() =>
                              requestCancelOrder({
                                venue: orderVM.venue,
                                orderId: orderVM.cancelId,
                                label,
                              })
                            }
                            onEdit={() => {
                              if (orderVM.venue === 'polymarket_us') return;
                              setEditOrder({
                                venue: orderVM.venue,
                                orderId: orderVM.cancelId,
                                marketLabel: label,
                                side: orderVM.side ?? 'yes',
                                action: orderVM.isSell ? 'sell' : 'buy',
                                remainingCount: orderVM.remainingQty,
                                currentPriceCents: orderVM.bidCents ?? 50,
                                ticker: orderVM.ticker,
                                tickSize: orderVM.tickSize,
                                negRisk: orderVM.negRisk,
                              });
                            }}
                            onUnpin={() => togglePin(entry)}
                          />
                        );
                      }
                      return (
                        <PinnedTombstoneRow
                          key={entry.key}
                          label={entry.label}
                          kind={entry.kind}
                          onUnpin={() => togglePin(entry)}
                        />
                      );
                    })}
                  </div>
                </EdgeFadeScroller>
              )}
            </section>
          )}

          {/* Open Orders — header + summary tiles are constant page
              chrome; only the item shape (rows vs cards) changes. The top
              border visually severs it from Open Positions above — the two
              sections have independent filters/toolbars and read as one
              undifferentiated wall without it (Dan, Jul 8; the border now
              rides the sticky toolbar below). */}
          {/* #828 — section identity for the reading-position primitive. The
              pinned strip renders DUPLICATE cards carrying the same vm.key as
              the lists below, so the row anchors alone are ambiguous; the
              primitive namespaces a row by its containing section, and these
              three wrappers are what make that disambiguation exist. */}
          <section data-view-anchor="open-orders" className="space-y-2">
            {/* STICKY orders toolbar (header row + section-scoped chips) —
                pins as the orders section reaches the top, exactly when the
                positions toolbar's containing block has pushed it away.
                Same glass treatment + the -top-6/pt-6 pairing as the
                positions toolbar so pinned content sits flush and covers
                the scroller's top padding zone. */}
            <div
              ref={ordersToolbarRef}
              className="border-border/70 bg-background/95 supports-[backdrop-filter]:bg-background/85 sticky -top-6 z-30 -mx-6 space-y-2 border-b border-t px-6 pb-3 pt-6 backdrop-blur"
              /* When sticky pins are enabled, the pinned block (z-40,
                 sticky -top-6)
                 parks ABOVE this toolbar — shift this toolbar's stick point
                 down by the block's live height so the two stack instead of
                 overlapping. -1.5rem = the base -top-6. */
              style={pinnedOrdersToolbarStyle(pinsSticky, pinnedBlockH)}
            >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <h2 className="text-foreground text-sm font-semibold">Open Orders</h2>
                <InfoTooltip title="Open Orders">
                  <OrdersHelp />
                </InfoTooltip>
                {/* Before the queue badge, which comes and goes. */}
                {ownAccountChip}
                {openOrders.length > 0 && <QueueVintageBadge status={queueStatus} />}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {orderViewMode === 'card' && sortedOrders.length > 0 && (
                  <div className="border-border inline-flex overflow-hidden rounded-md border">
                    <SortSegmentButton
                      active={orderSort.key === 'default'}
                      dir={orderSort.dir}
                      label="Default"
                      title="Sort orders by event time, then ticker"
                      onClick={() => handleOrderSort('default')}
                    />
                    <SortSegmentButton
                      active={orderSort.key === 'size'}
                      dir={orderSort.dir}
                      label="Size"
                      title="Sort orders by working size"
                      onClick={() => handleOrderSort('size')}
                    />
                    <SortSegmentButton
                      active={orderSort.key === 'queue'}
                      dir={orderSort.dir}
                      label="Queue"
                      title="Sort orders by queue position"
                      onClick={() => handleOrderSort('queue')}
                    />
                    <SortSegmentButton
                      active={orderSort.key === 'velocity'}
                      dir={orderSort.dir}
                      label="Velocity"
                      title="Sort orders by 1m queue/fill movement"
                      onClick={() => handleOrderSort('velocity')}
                    />
                  </div>
                )}
                {orderViewMode === 'card' && sortedOrders.length > 0 && (
                  <LockToggleButton
                    locked={ordersLockKeys != null}
                    onClick={toggleOrdersLock}
                  />
                )}
                <OrdersSyncBadge
                  sync={ordersSync}
                  active={accountStatus !== 'disconnected'}
                  onRetry={() => {
                    void refreshOrders();
                    void refreshPolyOrders(true);
                    bumpHeadroom();
                  }}
                />
                {orderFiltersNarrowing && (
                  <button
                    onClick={openCancelVisible}
                    disabled={cancelingAll || usBulk.sweeping}
                    className="border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20 mr-1 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-60"
                    title="Cancel only the orders currently visible under the active sport/market filters — hidden and pinned orders keep resting"
                  >
                    {cancelingAll ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <X size={12} />
                    )}
                    Cancel visible ({visibleCancelTargetCount + usVisibleCount})
                  </button>
                )}
                {/* A pending venue cannot hide another venue's readable orders.
                    Keep US cancellation available; an unknown K/P count is a
                    dash, and the confirmation names the reads left out. */}
                {(cancelAllTargetCount + pinnedOpenKalshiCount > 0 || usCancelHint) && (
                  <button
                    onClick={openCancelAll}
                    disabled={cancelingAll || usBulk.sweeping || (cancelAllTargetCount === 0 && !usCancelHint)}
                    className="border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20 mr-1 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-60"
                    title={`Cancel every open Kalshi and Polymarket order${
                      usCancel.kind === 'none' ? '' : usCancel.kind === 'unknown'
                        ? ' and your own Polymarket US orders (some could not be read yet; the confirmation checks)'
                        : ` and your ${usCancel.count} own Polymarket US order${usCancel.count === 1 ? '' : 's'} (${usCancelSource})`
                    } — includes orders hidden by the sport filter${pinnedOpenKalshiCount > 0 ? '. Pinned orders are skipped.' : ''}${
                      // The same list the confirmation discloses: pending or failed reads.
                      unreadOrderVenues().length > 0 ? `. Not counted: ${unreadOrderVenues().join('; ')}.` : ''}`}
                  >
                    {cancelingAll ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <X size={12} />
                    )}
                    {/* One span: the button's flex gap must not split the label. */}
                    <span>
                      {/* One count over every venue, as the lists below are one set.
                          An unknown US part (no read yet, or an account not read
                          whole) shows as "+" with the hover saying why; the
                          hint keeps a fixed width so the controls to its left
                          never move. */}
                      Cancel all ({cancelAllLabel.count}
                      {usCancelHint ? (
                        <span data-testid="cancel-all-us-hint" className="inline-grid text-left tabular-nums" title={cancelAllLabel.title}>
                          <span className="col-start-1 row-start-1 whitespace-nowrap">
                            {cancelAllLabel.more ? '+' : ''}{pinnedOpenKalshiCount > 0 ? ` · ${pinnedOpenKalshiCount} pinned` : ''})
                          </span>
                          <span aria-hidden="true" className="invisible col-start-1 row-start-1 whitespace-nowrap">+)</span>
                        </span>
                      ) : (
                        <>{pinnedOpenKalshiCount > 0 ? ` · ${pinnedOpenKalshiCount} pinned` : ''})</>
                      )}
                    </span>
                  </button>
                )}
                <ViewModeToggle
                  value={orderViewMode}
                  modes={SURFACE_VIEW_MODES}
                  onChange={setOrderViewMode}
                />
              </div>
            </div>
            {/* Section-scoped sport chips + market-family line — same
                tri-state cycle as the toolbar's positions chips, but this
                set narrows ONLY the orders list (+ its summary tiles).
                Cancel-all stays unfiltered on purpose. */}
            <SportMarketFilterRows
              scope="open orders"
              sportFilter={orderSportFilter}
              kindFilter={orderKindFilter}
              onAllSports={handleAllOrderSportsFilter}
              onSportToggle={handleOrderSportFilterToggle}
              onAllKinds={handleAllOrderKindsFilter}
              onKindToggle={handleOrderKindFilterToggle}
              unclassifiedCount={unclassifiedOrderCount}
              migrationNotice={kindMigrationNotice.orders}
              onDismissMigrationNotice={dismissOrderKindNotice}
            />
            </div>
            {/* ← end sticky orders toolbar */}
            <OrdersMismatchBanner
              intentContracts={headroom?.intentContracts ?? 0}
              listedCount={cancelAllTargetCount}
              syncFailures={ordersSync.consecutiveFailures}
              syncHealthy={isOrdersSyncFresh(ordersSync, Date.now())}
              active={accountStatus !== 'disconnected'}
              onRefresh={() => {
                void refreshOrders();
                void refreshPolyOrders(true);
                bumpHeadroom();
              }}
            />
            {/* #241/#1357 — stalled cancel synchronization, every order (a
                filter or collapsed section could hide the row). In the flow,
                never a fixed overlay: the old toast covered Recent Fills. The
                rail's tracker prong carries it at lg+ unless the buy window
                holds the rail or the prong is hidden; here it covers those. */}
            <KalshiCancelSyncNotice
              variant="inline"
              className={!buyPanelGame && trackerDocked ? 'lg:hidden' : ''}
            />
            <SummaryStrip>
              <SummaryTile label="open orders" value={orderFiguresUnknown ? '—' : orderSummary.total} />
              <SummaryTile label="top price" value={orderFiguresUnknown ? '—' : orderSummary.atFront} tone={orderFiguresUnknown ? 'default' : 'positive'} />
              <SummaryTile
                label="queue front"
                value={orderFiguresUnknown ? '—' : orderSummary.queueFront}
                tone={orderFiguresUnknown ? 'default' : 'positive'}
              />
              <SummaryTile label="in sale" value={orderFiguresUnknown ? '—' : orderSummary.inSale} tone={orderFiguresUnknown ? 'default' : 'warning'} />
              <SummaryTile label="behind" value={orderFiguresUnknown ? '—' : orderSummary.behind} tone={orderFiguresUnknown ? 'default' : 'danger'} />
              <SummaryTile label="way behind" value={orderFiguresUnknown ? '—' : orderSummary.wayBehind} tone={orderFiguresUnknown ? 'default' : 'danger'} />
            </SummaryStrip>
            <div className="h-3 text-[10px] text-muted-foreground">{orderSummary.retained ? 'Includes last confirmed book context' : 'Book context'}</div>
            {sortedOrders.length > 0 ? (
              orderViewMode === 'card' ? (
                <CardGrid>
                  {renderedOrderCardVMs.map((vm) => {
                    const href = orderLegacyHref(vm);
                    // #248: chip only — `onOpen` keeps the legacy `href` as its
                    // Poly navigate fallback.
                    const chipHref = orderChipHref(vm, href);
                    const label = ticketLabel(vm.subtitle ? `${vm.title} — ${vm.subtitle}` : vm.title, vmBet(vm));
                    return (
                      <OrderCardRow
                        key={vm.key}
                        usDispatch={usDispatch}
                        viewAnchor={vm.key}
                        vm={vm}
                        label={label}
                        href={href}
                        eventStateCertainty={eventStateCertaintyByTicker.get(vm.ticker)}
                        highlighted={isSameGameHighlighted(
                          gameKeyByVmKey.has(vm.key)
                            ? (gameKeyByVmKey.get(vm.key) ?? null)
                            : rowGameKey(vm),
                          activeGameKey,
                        )}
                        queueCriticalFlash={visibleQueueCriticalFlashIds.has(vm.key)}
                        queueWarningFlash={visibleQueueWarningFlashIds.has(vm.key)}
                        fellBehindFlash={visibleFellBehindFlashIds.has(vm.key)}
                        velocitySpikeSeverity={velocitySpikeFlash.get(vm.key) ?? null}
                        velocityMuted={velocityMutedIds.has(vm.key)}
                        divergence={divergenceByTicker.get(vm.ticker) ?? null}
                        divergenceFlash={visibleDivergenceFlashTickers.has(vm.ticker)}
                        upOdds={upOddsByKey.get(vm.key) ?? null}
                        detailHref={chipHref}
                        {...rowCancelProps(vm.venue, vm.cancelId)}
                        pinned={pinnedKeySet.has(vm.key)}
                        setHoveredRow={setHoveredRow}
                        orderCardOpen={orderCardOpen}
                        requestCancelOrder={requestCancelOrder}
                        setEditOrder={setEditOrder}
                        togglePin={togglePin}
                        toggleVelocityMute={toggleVelocityMute}
                      />
                    );
                  })}
                                </CardGrid>
              ) : (
                  <div
                    /* overflow-x-auto: wide tables scroll INSIDE the card
                       instead of bleeding past its right edge (Brendan,
                       Jul-9 — the orders row view ran out of its container
                       at rail-squeezed widths). */
                    className="bg-card border-border overflow-x-auto rounded-lg border"
                  >
                    {/* whitespace-nowrap (inherits into every cell): columns
                        take the width their content needs and the wrapper's
                        overflow-x-auto absorbs the excess — WITHOUT it the
                        table squeezes into the container and cell text
                        row-wraps, tripling row height (Brendan, Jul 9). */}
                    <table className="w-full whitespace-nowrap">
                  <thead>
                    <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                      <SortableTh
                        className="px-4 py-2 text-left"
                        sortKey="default"
                        activeSortKey={orderSort.key}
                        activeSortDir={orderSort.dir}
                        onSort={handleOrderSort}
                      >
                        Market
                      </SortableTh>
                      <th className="w-12 px-2 py-2 text-center">Source</th>
                      <th className="px-3 py-2 text-center">Side</th>
                      <th className="px-3 py-2 text-center">Type</th>
                      <SortableTh
                        className="px-3 py-2 text-right"
                        sortKey="size"
                        activeSortKey={orderSort.key}
                        activeSortDir={orderSort.dir}
                        onSort={handleOrderSort}
                        title="Sort by working order size"
                      >
                        Qty
                      </SortableTh>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-right">Last</th>
                    <SortableTh
                      className="px-3 py-2 text-right"
                      sortKey="velocity"
                      activeSortKey={orderSort.key}
                      activeSortDir={orderSort.dir}
                      onSort={handleOrderSort}
                      title="Sort by 1m order movement. Cell shows side-native price change over the last minute (Kalshi only)."
                    >
                      1m Δ
                    </SortableTh>
                    <th className="px-3 py-2 text-center">Book</th>
                      <SortableTh
                        className="px-3 py-2 text-right"
                        sortKey="queue"
                        activeSortKey={orderSort.key}
                        activeSortDir={orderSort.dir}
                        onSort={handleOrderSort}
                        title="Sort by queue position"
                      >
                        Queue
                      </SortableTh>
                      <th className="px-3 py-2 text-right">Level above</th>
                    <th className="px-3 py-2 text-right">In line behind</th>
                    {/* #1155 — each cell carries a second line for the
                        still-working remainder; the header labels it. */}
                    <th
                      className="px-3 py-2 text-right"
                      title="Top: the filled contracts once the order has filled at all, otherwise the whole working order — contracts × price, plus known maker fees on a buy, less them on a sell (parity with the order-card footer). Below: the same for the contracts still working (a sell's proceeds)."
                    >
                      Cost
                      <span className="block text-[10px] font-normal normal-case tracking-normal">remaining</span>
                    </th>
                    <th
                      className="px-3 py-2 text-right"
                      title="Top: the filled contracts once the order has filled at all, otherwise the whole working order — contracts × (100 − price), less known maker fees (parity with the order-card footer). Below: the same for the contracts still working; none for a sell."
                    >
                      To Win
                      <span className="block text-[10px] font-normal normal-case tracking-normal">remaining</span>
                    </th>
                    <th className="w-10 px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {stableTableRows.map((row, rowIndex) => (
                    <OrderTableRow
                      key={orderRowId(row)}
                      /* An id-less poly order yields '' (orderSort.ts:79), and
                         every such row would then share the anchor
                         ["open-orders",""]. An absent anchor is honest — the
                         section still answers for it — where a colliding one
                         would seek to whichever of them the DOM hands back
                         first. */
                      viewAnchor={orderRowId(row) || undefined}
                      row={row}
                      market={tableRowMarkets[rowIndex] ?? EMPTY_ORDER_TABLE_MARKET}
                      feeAdjustedOdds={feeAdjustedOdds}
                      quoteRebatePct={quoteRebatePct}
                      openPolyOrderPanel={openPolyOrderPanel}
                      orderCardOpen={orderCardOpen}
                      requestCancelOrder={requestCancelOrder}
                      sameGameRow={sameGameRow}
                      setEditOrder={setEditOrder}
                      togglePin={togglePin}
                      toggleVelocityMute={toggleVelocityMute}
                      usDispatch={usDispatch}
                    />
                  ))}
                </tbody>
              </table>
                </div>
              )
            ) : (
              <div className="bg-card border-border text-muted-foreground rounded-lg border px-4 py-8 text-center text-sm">
                {ordersEmptyText}
                {ordersUsUnavailable?.retry && <UsUnavailableRetry state={ordersUsUnavailable} />}
              </div>
            )}
          </section>
          {/* #1226 — the Polymarket US readers behind the rows above (and their
              confirm and Edit/Close dialogs); nothing of theirs renders here. */}
          <UsTrackerFeed
            sessionKey={usSessionKey}
            readiness={usVenue}
            refreshSignal={usBulk.refreshSignal}
            confirmCancel={confirmOrderCancel}
            marketServed={usServed}
            servingVersion={usMarkets.servingVersion}
            pinTransfer={usPinTransfer}
            onRead={onUsRead}
          />
          {footer}
        </div>
        </div>
      </div>

      {/* Buy window — the dashboard's GameDetailPanel docked as the right
          rail. Takes the tracker's slot while open (both at once crowds the
          positions table off the screen); closing restores the tracker.
          Keyed by game so switching games remounts cleanly. The rail column
          lives OUTSIDE the main scroller (VD layout) so it's always fully
          on screen at exactly viewport height; the panel's own root handles
          interior scrolling. (History: this slot was sticky inside the page
          scroller and hit two bugs in a row — a margin-top that rode the
          sticky pin ~600px down at every scroll depth (9cab18a, sticky
          insets position the margin box), then 100vh sizing that clipped
          the bottom below the real scrollport. The independent column
          retires the whole failure class — Prof, Jul 8: "fixed location
          no matter where i am on page".) */}
      {/* INVARIANT (Brendan, Jul 8): the rail's geometry — top edge, width,
          border — is a constant of the LAYOUT. Positions/orders/fills counts,
          panel origin, and feed state may only ever change what renders
          INSIDE the rail, never where the rail is. No content-derived
          offsets, ever (see 9cab18a's revert for the cautionary tale). */}
      {buyPanel && buyPanelGame && (
        /* Flush slot: the panel's own border-l IS the page's vertical
           separator — full height, no outer padding (Option B hard dock). */
        <div className="h-full shrink-0">
            <ExpandedGamePanel
              key={buyPanelGame.key}
              game={buyPanelGame}
              onClose={() => setBuyPanel(null)}
              tradeIntent={buyPanel.intent}
              marketSide={panelMarketSide}
              onTeamSideChange={(side) => {
                setBuyPanel((bp) => (bp ? { ...bp, intent: { ...bp.intent, side } } : null));
              }}
              betLineContext={buyPanelBetLineContext}
            />
          </div>
        )}

        {!buyPanelGame && (
          /* Rail column outside the main scroller (VD layout) — the frame is
             exactly the visible height, never grows, never moves (Brendan,
             Jul 8). Inside: a PINNED header (the Tracker/Fills toggles —
             always visible, never scrolls away) and beneath it ONE stack
             scroll area spanning the rest of the rail. The prong cards flow
             in it like a normal document — content-sized, never stretched
             (a tall monitor gets open rail below the cards, not padding);
             resting cards cap + scroll internally, expanded cards uncap and
             the STACK scrolls. The gutter lane on the right edge belongs to
             the stack's scroll context so the wheel always has a spot that
             drives the broad area even when hovering a card's own scroller.
             The rail ALWAYS renders (lg+) so the toggles stay anchored
             top-right — with both prongs hidden it shrinks to the button
             row. */
          <aside
            /* w-96 UNCONDITIONALLY — the authoritative rail width is the
               GameDetailPanel's (shared with the Value Dashboard), so the
               frame never changes size across occupancy states: cards,
               ticket, or even both prongs hidden (Brendan, Jul 8). */
            className="border-border hidden h-full w-96 shrink-0 flex-col border-l lg:flex"
            aria-label="Position tracker and recent fills rail"
          >
            {/* Pinned rail header — a distinguished band (bordered, tinted)
                holding the show/hide toggles; never scrolls away. min-h
                matches the admin org bar so the bands align across the
                separator as one continuous hairline. */}
            <div className="border-border bg-muted/30 flex min-h-[52px] shrink-0 items-center justify-between gap-2 border-b px-3">
              {/* Balances live HERE (not in the prong headers) so the prong
                  title rows stay one line no matter the rail width. */}
              {/* #1034: each venue's label and amount stay together and the
                  group wraps, so large balances plus the US slot take a second
                  line inside the band instead of being cut off. */}
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 overflow-hidden font-mono text-xs">
                <span className="flex items-baseline gap-1.5 whitespace-nowrap">
                <span className="text-muted-foreground">Bal</span>
                <span
                  className="text-muted-foreground text-[10px]"
                  title={kalshiReportedBalanceTitle(keyMode)}
                >
                  K
                </span>
                <span className="text-foreground" title={kalshiBalanceHidden ? ORG_BALANCE_HIDDEN_TITLE : undefined}>
                  {kalshiBalanceHidden ? ORG_BALANCE_HIDDEN_LABEL : kalshiBalance !== null
                    ? `$${(kalshiBalance / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'}
                </span>
                </span>
                <span className="flex items-baseline gap-1.5 whitespace-nowrap">
                <span
                  className="text-muted-foreground text-[10px]"
                  title={
                    polyBalance.source === 'org'
                      ? 'Polymarket org account (funder USDC)'
                      : polyBalance.source === 'personal'
                        ? 'Polymarket personal account (funder USDC)'
                        : 'Polymarket balance — no org poly keys and no personal connection'
                  }
                >
                  P
                </span>
                <span className="text-foreground" title={polyBalance.hidden ? ORG_BALANCE_HIDDEN_TITLE : undefined}>
                  {polyBalance.hidden ? ORG_BALANCE_HIDDEN_LABEL : polyBalance.usd !== null
                    ? `$${polyBalance.usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'}
                </span>
                </span>
                {/* #1034 — US beside K and P, from the page's one US rail read. */}
                <UsBalanceSlot rail={usRail} className="text-muted-foreground" valueClassName="text-foreground" />
              </div>
              <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={toggleTrackerHidden}
                aria-pressed={!trackerHidden}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  !trackerHidden
                    ? 'border-primary/50 bg-primary/15 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                title={
                  trackerHidden
                    ? 'Show the position tracker in the rail'
                    : 'Hide the position tracker'
                }
              >
                <Crosshair size={14} />
                <span>Tracker</span>
              </button>
              <button
                type="button"
                onClick={toggleFillsHidden}
                aria-pressed={!fillsHidden}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  !fillsHidden
                    ? 'border-primary/50 bg-primary/15 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                title={fillsHidden ? 'Show recent fills in the rail' : 'Hide recent fills'}
              >
                <Receipt size={14} />
                <span>Fills</span>
              </button>
              {/* #1124: icon only. A labelled third toggle shrank the balance band
                  beside it from about 193 px to 116 px and cut off a six-figure
                  K balance. self-stretch keeps Tracker and Fills' height. */}
              <button
                type="button"
                onClick={toggleDataShown}
                aria-pressed={dataShown}
                aria-label="Data pressure"
                className={`inline-flex items-center self-stretch rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  dataShown
                    ? 'border-primary/50 bg-primary/15 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                title={dataShown ? 'Hide data pressure' : 'Show where the market-data flood reaching this tab comes from'}
              >
                <Activity size={14} aria-hidden />
              </button>
              </div>
            </div>
            {/* Stack scroll area — everything below the pinned header.
                Cards run FLUSH against the rail's left border and the
                window's right edge (Option B hard dock): no horizontal
                padding, square card corners, card x-borders removed so the
                rail border is the only vertical line. */}
            <div className="flex min-h-0 flex-1 overflow-y-auto">
              <div className="min-w-0 flex-1 space-y-3">
                {trackerDocked && (
                  <PositionSidebar
                    variant="docked"
                    section="tracker"
                    usRail={usRail}
                    expanded={trackerExpanded}
                    onToggleExpand={() => setTrackerExpanded((v) => !v)}
                    onClose={() => setTrackerHidden(true)}
                  />
                )}
                {fillsDocked && (
                  <PositionSidebar
                    variant="docked"
                    section="fills"
                    expanded={fillsExpanded}
                    onToggleExpand={() => setFillsExpanded((v) => !v)}
                    onClose={() => setFillsHidden(true)}
                  />
                )}
                {dataShown && (
                  <DataPressureCard variant="prong" expanded={dataExpanded} onToggleExpanded={toggleDataExpanded} page="Positions" />
                )}
              </div>
              {/* Scroll gutter — owned by the stack scroller; sticky so the
                  lane rides the visible band at every scroll position. */}
              <div
                aria-hidden
                title="Scroll rail"
                className="border-border/20 hover:bg-muted/40 sticky top-0 h-full w-2 shrink-0 self-start border-l transition-colors"
              />
            </div>
          </aside>
        )}

      {cancelError && (
        <div
          className="fixed right-6 bottom-6 z-50 max-w-sm rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 shadow-lg"
          role="alert"
        >
          <div className="flex items-start justify-between gap-3">
            <span>{cancelError}</span>
            <button
              type="button"
              onClick={() => setCancelError(null)}
              className="text-red-300/70 hover:text-red-200"
              aria-label="Dismiss cancel error"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* #272 — the sweep result. Replaces the old "N orders failed to cancel"
          count: a bare count cannot be acted on, so a partial failure used to
          mean re-running the WHOLE sweep and re-DELETEing the orders that had
          already succeeded. Names each failed order and retries only those. */}
      {(bulkCancelReport || usBulk.report) && (
        <div
          // #1006 — capped as a whole: a long K/P failure list plus the US
          // results must stay on a 956 px screen.
          className="border-border bg-card fixed right-6 bottom-6 z-50 max-h-[80vh] max-w-sm overflow-y-auto rounded-lg border px-4 py-3 text-sm shadow-lg"
          role="status"
        >
          {bulkCancelReport && (
          <div className="flex items-start justify-between gap-3">
            <div>
              {/* "Submitted", not "cancelled" — removal is proven only when the
                  venue's own list stops carrying the ID. */}
              <div className="font-medium">
                Cancellation submitted for {bulkCancelReport.submitted.length} order
                {bulkCancelReport.submitted.length === 1 ? '' : 's'}
                {bulkCancelReport.failed.length > 0
                  ? ` · ${bulkCancelReport.failed.length} failed`
                  : ''}
                {bulkCancelReport.alreadyPending.length > 0
                  ? ` · ${bulkCancelReport.alreadyPending.length} already pending`
                  : ''}
                {bulkCancelReport.snapshot.skippedPinned.length > 0
                  ? ` · skipped ${bulkCancelReport.snapshot.skippedPinned.length} pinned`
                  : ''}
              </div>
              {bulkCancelReport.failed.map((row) => (
                <div key={row.target.key} className="mt-1 text-xs text-red-300">
                  <span className="font-semibold uppercase">{row.target.venue}</span>{' '}
                  <span className="break-all font-mono">{row.target.orderId}</span>
                  {row.error ? ` — ${row.error}` : ''}
                </div>
              ))}
              {bulkCancelReport.alreadyPending.map((row) => (
                <div key={row.target.key} className="text-muted-foreground mt-1 text-xs">
                  <span className="font-semibold uppercase">{row.target.venue}</span>{' '}
                  <span className="break-all font-mono">{row.target.orderId}</span> — {row.error}
                </div>
              ))}
              {bulkCancelReport.refreshErrors.map((message) => (
                <div key={message} className="mt-1 text-xs text-amber-300">
                  Requests were sent, but the order list could not be refreshed — {message}
                </div>
              ))}
              {bulkCancelReport.auditError && (
                <div className="mt-1 text-xs text-amber-300">
                  Cancellation continued, but its attempt log was unavailable —{' '}
                  {bulkCancelReport.auditError}
                </div>
              )}
              {bulkCancelReport.failed.length > 0 && (
                <button
                  type="button"
                  onClick={retryFailedBulkCancels}
                  disabled={cancelingAll}
                  className="mt-2 rounded border border-red-500/40 px-2 py-0.5 text-xs font-medium text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                >
                  Retry failed
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setBulkCancelReport(null)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Dismiss cancel report"
            >
              <X size={14} />
            </button>
          </div>
          )}
          {usBulk.report && (
            <div className="flex items-start justify-between gap-3">
              <UsBulkCancelReport results={usBulk.report} note={usBulk.reportNote} />
              <button
                type="button"
                onClick={usBulk.dismissReport}
                className="text-muted-foreground hover:text-foreground mt-2"
                aria-label="Dismiss Polymarket US cancel report"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {polyCloseMsg && (
        <div
          className={`fixed right-6 bottom-6 z-50 max-w-sm rounded-lg border px-4 py-3 text-sm shadow-lg ${
            polyCloseMsg.ok
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
              : 'border-red-500/30 bg-red-500/10 text-red-300'
          }`}
          role="alert"
        >
          <div className="flex items-start justify-between gap-3">
            <span>{polyCloseMsg.msg}</span>
            <button
              type="button"
              onClick={() => setPolyCloseMsg(null)}
              className="opacity-70 hover:opacity-100"
              aria-label="Dismiss close result"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={pendingCancelOrder !== null}
        onClose={() => setPendingCancelOrder(null)}
        onConfirm={() => {
          // Clear the captured target before firing so the modal can't re-submit
          // the same order; the store's duplicate guard remains the last resort.
          const target = pendingCancelOrder;
          setPendingCancelOrder(null);
          if (target) void executeCancelOrder(target);
        }}
        title="Cancel order?"
        message={
          pendingCancelOrder
            ? `Cancel ${pendingCancelOrder.label}?`
            : 'Cancel this order?'
        }
        confirmLabel="Cancel order"
        cancelLabel="Back"
        variant="danger"
      />

      {/* One frozen confirmation for both scopes. The count, venue list and IDs
          all read from `pendingBulkCancel` — never from `openOrders` /
          `sortedOrders` — so a poll or a filter change while the trader is
          reading cannot change what the confirm button sends. */}
      <Modal
        isOpen={pendingBulkCancel !== null}
        onClose={() => { setPendingBulkCancel(null); discardUsBulk(); }}
        title={
          pendingBulkCancel?.scope === 'visible'
            ? 'Cancel visible open orders?'
            : 'Cancel ALL open orders?'
        }
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setPendingBulkCancel(null); discardUsBulk(); }}
              className="bg-muted hover:bg-muted/80 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
            >
              Back
            </button>
            <button
              onClick={confirmBulkCancel}
              // #1034: with nothing else to send, a confirm while US is still
              // being read would send nothing — wait for the read (Brendan's
              // #1029 follow-up). With K/P orders it stays live (#1006 review).
              disabled={cancelingAll || usBulk.sweeping || (usBulkReading && bulkConfirmCount === 0)}
              title={usBulkReading && bulkConfirmCount === 0 ? 'Reading your Polymarket US orders…' : undefined}
              className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:opacity-50"
            >
              {usBulkReading && bulkConfirmCount === 0
                ? 'Checking US…'
                : `Cancel ${bulkConfirmCount} order${bulkConfirmCount === 1 ? '' : 's'}`}
            </button>
          </div>
        }
      >
        {pendingBulkCancel && (
          <div>
            <p className="text-muted-foreground text-sm">
              {pendingBulkCancel.targets.length === 0 && (usBulkFrozen?.targets.length ?? 0) > 0
                // #1006 — only US orders to send: "Cancel 0 … Venues: none" above
                // a "Cancel N orders" button would read as a contradiction.
                ? 'No Kalshi or Polymarket orders are included. Only the listed order IDs will be submitted; later updates cannot add orders.'
                : `Cancel ${pendingBulkCancel.targets.length} open order${
                pendingBulkCancel.targets.length === 1 ? '' : 's'
              } — ${pendingBulkCancel.label}. Venues: ${describeCancelVenues(
                pendingBulkCancel.venues,
              )} (${describeCancelVenueCounts(
                pendingBulkCancel.targets,
              )}). Only the listed order IDs will be submitted; later updates cannot add orders.`}
            </p>
            {pendingBulkUnread.length > 0 && (
              <p className="mt-1 text-xs text-amber-300 light:text-amber-700" data-testid="bulk-cancel-unread">
                Not included: {pendingBulkUnread.join('; ')}. Any of their open orders not listed here keep resting.
              </p>
            )}
            {pendingBulkCancel.scope === 'all' && (
              <p className="mt-1 text-xs text-amber-300 light:text-amber-700">
                Your sport and market filters do NOT narrow this — orders hidden by the filters are
                included.
              </p>
            )}
            {pendingBulkCancel.scope === 'visible' && (
              <p className="text-muted-foreground mt-1 text-xs">
                Orders hidden by the filters keep resting.
              </p>
            )}
            {pendingBulkCancel.scope === 'visible' && pendingBulkUnclassified > 0 && (
              <p className="mt-1 text-xs text-amber-300 light:text-amber-700">
                {`Includes ${pendingBulkUnclassified} unclassified order${
                  pendingBulkUnclassified === 1 ? '' : 's'
                } — market type not known yet, so the market filter can't tell what ${
                  pendingBulkUnclassified === 1 ? 'it is' : 'they are'
                }.`}
              </p>
            )}
            {pendingBulkCancel.skippedPinned.length > 0 && (
              <p className="mt-1 text-xs text-amber-300 light:text-amber-700">{describeSkippedPinned(pendingBulkCancel)}</p>
            )}
            <UsBulkCancelLines prepared={usBulk.prepared} waitsForUs={bulkConfirmCount === 0} />
            <ul className="mt-2 space-y-0.5">
              {pendingBulkCancel.targets.map((target) => (
                <li key={target.key} className="text-xs">
                  <span className="text-muted-foreground font-semibold uppercase">
                    {target.venue}
                  </span>{' '}
                  <span className="text-foreground break-all font-mono">{target.orderId}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!pendingPolyClose}
        onClose={() => setPendingPolyClose(null)}
        onConfirm={() => void handlePolyClose()}
        title="Close Polymarket position?"
        message={`Sell ${pendingPolyClose?.qty ?? 0} contract${(pendingPolyClose?.qty ?? 0) === 1 ? '' : 's'} of ${pendingPolyClose?.label ?? ''} ${pendingPolyClose?.side.toUpperCase() ?? ''} at market (fill-and-kill sweep — fills what the bid book has, cancels the rest; nothing rests). This cannot be undone.`}
        confirmLabel="Close position"
        cancelLabel="Back"
        variant="danger"
      />

      {editPosition && (
        <EditPositionModal
          data={editPosition}
          onClose={() => setEditPosition(null)}
          onPlaced={() => {
            // Nudge the next account refresh and P&L-card display tick so the
            // closed contracts disappear without a perceived lag.
            refreshPositions();
            refreshOrders();
            setDisplayTotalPnl(latestVisibleTotalPnlRef.current);
            setPnlCardAnchorMs(Date.now());
          }}
        />
      )}

      {editOrder && (
        <EditOrderPriceModal
          isOpen={editOrder !== null}
          onClose={() => setEditOrder(null)}
          onReplace={handleReplaceFromModal}
          marketLabel={editOrder.marketLabel}
          side={editOrder.side}
          action={editOrder.action}
          remainingCount={editOrder.remainingCount}
          currentPriceCents={editOrder.currentPriceCents}
          ticker={editOrder.ticker}
          venue={editOrder.venue}
        />
      )}
      <AccountRecordDialog
        record={recordPosition
          ? recordFromPosition(recordPosition, `${recordPosition.side}:${recordPosition.ticker}`, !showPnl,
            gameDetailHref(recordPosition.ticker, recordPosition.side))
          : null}
        onClose={positionRecord.close}
      />
    </div>
  );
}

// ── Manager view: own data + per-trader summary ─────────────────

function ManagerPositionsView({
  currentUserId,
  onSelectTrader,
}: {
  currentUserId: string | null;
  onSelectTrader?: (row: OrgTraderRow) => void;
}) {
  // No wrapper scroller: TraderView owns its height, scrolling, and rail
  // (VD layout). Wrapping it in an auto-height padded scroller silently
  // turned its h-full into `auto` — the frame then grew with content and
  // this wrapper scrolled everything as one document, rail included (the
  // Jul-8 "rail scrolls away / runs past the bottom" bug, admin+manager
  // views only — bare trader mounts were never wrapped).
  return (
    <TraderView
      footer={
        <OrgTradersTable
          highlightUserId={currentUserId ?? undefined}
          onRowClick={onSelectTrader}
          title="My Traders"
        />
      }
    />
  );
}

// ── Admin view: per-org summary ─────────────────────────────────

interface OrgSummaryRow {
  orgId: string;
  name: string;
  hasKeys: boolean;
  openCount: number;
  openNotionalCents: number;
  openOrderCount: number;
  realizedPnlCents: number;
}

// An admin spans every org, so (unlike the manager view) this adds an
// OrgSwitcher — the same shared useOrgScope control used on Org Account / Org
// Settings — to choose which org's traders to inspect. Clicking a trader drills
// in scoped to that org (PositionsRoute threads the selected orgId). Additive
// over the prior admin /positions: TraderView is preserved.
//
// The selector retargets only that roster. Open Positions, Open Orders,
// Polymarket US and Cancel all above it stay on the admin's own signed-in
// account, so the band says what it chooses ("Org traders & bots") and the
// three section headers carry "Your account · <own org>", with one quick
// reference explaining the split.
function AdminPositionsView({
  currentUserId,
  onSelectTrader,
}: {
  currentUserId: string | null;
  onSelectTrader?: (row: OrgTraderRow) => void;
}) {
  const selectedOrgId = useOrgScope((s) => s.selectedOrgId);
  const ownOrgId = useAuthStore((s) => s.user?.orgId ?? null);
  // The same list the switcher reads (shared store, one read per admin).
  const { orgs } = useAdminOrgs(true);
  const ownOrgName = ownOrgId ? orgs.find((o) => o.id === ownOrgId)?.name ?? null : null;
  const scopeHelp = "Chooses which organization's traders and bots are listed. "
    + `Your positions, orders and Cancel all act on your signed-in account${ownOrgName ? ` (${ownOrgName})` : ''}.`;
  const navigate = useNavigate();
  // The org bar rides TraderView's topBar slot — INSIDE the left column,
  // at the left column's width — so the rail's vertical separator runs
  // unbroken to the top of the layout (Brendan, Jul 8). No wrapper
  // scroller: an auto-height wrapper here once turned TraderView's h-full
  // into `auto` and broke the whole rail geometry (admin view only).
  return (
    <TraderView
      topBar={
        <Tooltip content={scopeHelp} position="bottom" className="w-80 whitespace-normal font-normal">
          <div className="flex items-center gap-2" data-testid="admin-org-scope">
            <span className="text-muted-foreground text-xs font-medium">Org traders &amp; bots:</span>
            <OrgSwitcher title={null} />
          </div>
        </Tooltip>
      }
      ownAccountChip={
        <Tooltip content={scopeHelp} position="bottom" className="w-80 whitespace-normal font-normal">
          <span
            data-testid="own-account-chip"
            className="border-border/70 bg-muted/40 text-muted-foreground max-w-[16rem] truncate rounded border px-1.5 py-0.5 text-[10px] font-medium"
          >
            {ownOrgName ? `Your account · ${ownOrgName}` : 'Your account'}
          </span>
        </Tooltip>
      }
      footer={
        <OrgTradersTable
          orgQueryParam={selectedOrgId ? `?orgId=${selectedOrgId}` : ''}
          highlightUserId={currentUserId ?? undefined}
          onRowClick={onSelectTrader}
          title="Org Traders"
          onViewOrgAccount={
            selectedOrgId ? () => navigate(`/positions/org/${selectedOrgId}`) : undefined
          }
        />
      }
    />
  );
}

export type { OrgSummaryRow };
